import { eq, and, isNull, isNotNull, sql, desc, asc, like, gte, lte, inArray, or, type SQL } from 'drizzle-orm';
import type { AnySQLiteColumn } from 'drizzle-orm/sqlite-core';
import { differenceInCalendarDays, format, parseISO, addDays, subDays } from 'date-fns';
import { db, execute, runInTransaction } from '@/db/client';
import { transactions, accounts, categories, subcategories, creditCards, tags, transactionTags, invoices, installments, recurringTransactions, financings, personalLoans, people, goalDeposits, pendingNotifications, reservas, reservaMovements } from '@/db/schema';
import { generateId } from '@/lib/id';
import { resolveTransactionTime } from '@/lib/transactionTime';
import { recalculateAccountBalance } from './accountRepository';
import {
    getInvoiceMonthForDate,
    getOrCreateInvoice,
    getInvoiceTransactions,
    recalculateInvoiceTotal,
} from './invoiceRepository';
import { AdjustmentType, FinancingEntryType, PersonalLoanEntryType, type FinancingEntryTypeValue, type TransactionTypeValue } from '@/lib/constants';
import {
    excludeHiddenAccountRecurringRules,
    excludeDeletedCardTransactions,
    excludeHiddenAccountTransactions,
    excludeIgnored,
    excludeIgnoredRecurringRules,
    excludeNonAnalyticInvoiceAdjustments,
    excludePersonalLoanPrincipal,
    excludeRawCreditCardTransactions,
    installmentFullValueAmount,
    isIgnoredTransaction,
    isPersonalLoanPrincipalEntry,
    onlyInstallmentAnchor,
    originCardIdExpr,
} from './transactionScopes';
import { collapseInstallmentRows, roundCurrency, type InstallmentGroupStats } from '@/lib/installments';
import { buildInstallmentRebasePlan, stripInstallmentSuffix, type InstallmentRebaseMode } from '@/lib/installmentRebase';
import {
    buildRecurringSeriesRebasePlan,
    type RecurringSeriesCadence,
    type RecurringSeriesRebaseMode,
} from '@/lib/recurringSeriesRebase';
import type { EditScope } from '@/lib/seriesRebase';
import { buildCashFlowCategorySpending } from '@/lib/analysisCashFlow';
import { confirmedFilterFor, includesProjected, type ReportStatusFilter } from '@/lib/reportStatus';
import { buildIncludedAccountBalanceMap } from '@/lib/accountTotals';
import { buildBalanceTimeline, type BalanceBoundary } from '@/lib/balanceTimeline';
import { getAccrualExpenseAmount } from '@/lib/accrualExpenses';
import { getMonthDateRange } from '@/lib/months';
import { monthKeyForDate, monthKeysAround, todayMonthKey } from '@/lib/monthCycle';
import { getMonthStartDay } from '@/lib/monthStart';
import { monthKeySql } from '@/db/monthKeySql';
import {
    isLowSignalDescription,
    marketplaceDisplayName,
    normalizeAutoCategorizationText,
    type AutoCategorizationTargetType,
} from '@/lib/autoCategorization';
import {
    CONTEXTUAL_CONFIDENT_PROBABILITY,
    rankCategoriesByContext,
    type ContextualQuery,
} from '@/lib/contextualCategory';
import {
    buildProjectedRecurringTransactions,
    parseProjectedRecurringTransactionId,
} from '@/lib/projectedRecurringTransactions';
import { buildAccrualInvoiceTransactionId, isVirtualTransactionId } from '@/lib/virtualTransactionId';
import {
    aggregateAccrualMonthlyTotals,
    mergeProjectedMonthlyTotals,
    type MonthlyTotal,
} from '@/lib/monthlyTotals';
import { firstOccurrence, nextOccurrence, previousOccurrence, occurrenceIndexOf, occurrenceDateAt, rewindFrom, parseSkippedDates, rebaseSkippedDates, advancePastSkipped, inferFrequencyFromDates, type RecurrenceRuleFields } from '@/lib/recurrence';
import {
    canSuggestFromSimilarDescriptions,
    loadMerchantCatalogResolver,
    loadSimilarDescriptionsIndex,
    matchDescription,
} from './autoCategorizationRepository';
import { suggestFromSimilarDescriptions } from '@/lib/similarDescriptions';
import { syncFinancingState } from './financingRepository';
import { getTagInfoForRecurringIds } from './tagRepository';
import {
    getNotificationMatchAmountRange,
    getNotificationMatchWindow,
    isSameSeriesLabel,
    pickBestNotificationMatch,
    NOTIFICATION_MATCH_MAX_WINDOW_DAYS,
    NOTIFICATION_MATCH_SERIES_HISTORY,
    type NotificationMatchCandidate,
    type NotificationMatchScope,
    type NotificationMatchSeries,
} from '@/lib/notificationMatch';
import { ExpectedError } from '@/lib/errorUtils';
import i18n from '@/lib/i18n';
import { compareIso } from '@/lib/compareIso';

export type Transaction = typeof transactions.$inferSelect;

export interface TransactionTagInfo {
    id: string;
    name: string;
    color: string;
}

export type TransactionWithRelations = Transaction & {
    /** Tags da transação — só preenchidas quando o filtro `includeTags` é passado. */
    tags?: TransactionTagInfo[];
    accountName?: string | null;
    accountColor?: string | null;
    accountIcon?: string | null;
    creditCardName?: string | null;
    categoryName?: string | null;
    categoryIcon?: string | null;
    categoryColor?: string | null;
    subcategoryName?: string | null;
    destinationAccountName?: string | null;
    destinationAccountColor?: string | null;
    invoiceReferenceMonth?: string | null;
    financingName?: string | null;
    financingStatus?: string | null;
    financingTotalInstallments?: number | null;
    isProjected?: boolean;
    /** Caixinha que recebeu este rendimento (ADR 0164): a lista desenha a linha como da caixinha. */
    reservaYieldName?: string | null;
    reservaYieldColor?: string | null;
    reservaYieldIcon?: string | null;
};

/**
 * Caixinha do rendimento ligado a esta receita (ADR 0164), para a lista desenhar a linha como da
 * caixinha. Só receita ignorada consulta o elo (índice `idx_reserva_movements_transaction`).
 * `"transactions"."id"` escrito à mão: o drizzle solta a coluna sem tabela nos campos do select,
 * e dentro da subconsulta ela casaria com o id do movimento (docs/kb/db.md).
 */
function reservaYieldField(column: 'name' | 'color' | 'icon') {
    return sql<string | null>`(CASE WHEN "transactions"."type" = 'income' AND "transactions"."is_ignored" = 1 THEN (
        SELECT r.${sql.raw(column)} FROM reserva_movements m JOIN reservas r ON r.id = m.reserva_id
        WHERE m.transaction_id = "transactions"."id" AND m.type = 'yield' LIMIT 1
    ) END)`;
}

/**
 * Condição de categoria que respeita o recorte por subcategoria (ADR 0110). A lista tem
 * que enxergar EXATAMENTE o mesmo dinheiro que a barra do orçamento — a lição do ADR 0103.
 */
function categoryScopeCondition(filters: TransactionFilters) {
    const categoryIds = filters.categoryIds ?? [];
    if (categoryIds.length === 0) return undefined;
    const scoped = (filters.subcategoryScope ?? []).filter(
        (s) => categoryIds.includes(s.categoryId) && s.subcategoryIds.length > 0,
    );
    if (scoped.length === 0) return inArray(transactions.categoryId, categoryIds);
    const scopedIds = new Set(scoped.map((s) => s.categoryId));
    const whole = categoryIds.filter((id) => !scopedIds.has(id));
    const clauses = scoped.map((s) => and(
        eq(transactions.categoryId, s.categoryId),
        inArray(transactions.subcategoryId, s.subcategoryIds),
    )!);
    if (whole.length > 0) clauses.push(inArray(transactions.categoryId, whole));
    return or(...clauses)!;
}

export interface TransactionFilters {
    startDate?: string;
    endDate?: string;
    type?: TransactionTypeValue;
    /**
     * Conta única — forma antiga, mantida pros chamadores que filtram uma conta só.
     * Contas e cartões combinam em OR (ADR 0170), ver `accountIds`/`creditCardIds`.
     */
    accountId?: string;
    /**
     * Várias contas de uma vez (relatório, filtro do extrato). Tem precedência sobre
     * `accountId`. Alcança a transferência que CHEGA na conta (destino), não só a que sai.
     */
    accountIds?: string[];
    /** Cartão único — forma antiga; `creditCardIds` tem precedência. */
    creditCardId?: string;
    /**
     * Vários cartões de uma vez (filtro do extrato, ADR 0170). Combina em OR com as
     * contas: o lançamento entra se a conta dele está nas escolhidas OU o cartão está nos
     * escolhidos. Só contas = só as contas (compra de cartão fica fora); só cartões = só
     * os cartões. Antes era AND, e conta + cartão quase sempre esvaziava a lista.
     */
    creditCardIds?: string[];
    /**
     * Recorte por Origem (ADR 0103): Contas/Cartões em que a leitura vale. Combina em OR
     * entre si (a Origem é excludente no lançamento) e em AND com os demais filtros.
     * Vazio/ausente = todas as origens.
     */
    origins?: { kind: 'account' | 'card'; id: string }[];
    financingId?: string;
    personalLoanId?: string;
    categoryId?: string;
    categoryIds?: string[];
    /**
     * Recorte por subcategoria de um orçamento (ADR 0110), por categoria: a categoria
     * listada aqui só vale nas subcategorias dadas; categoria de `categoryIds` que não
     * aparece aqui vale inteira. Só faz efeito junto com `categoryIds`.
     */
    subcategoryScope?: { categoryId: string; subcategoryIds: string[] }[];
    subcategoryId?: string;
    tagIds?: string[];
    search?: string;
    isConfirmed?: boolean;
    minAmount?: number;
    maxAmount?: number;
    includeProjectedRecurring?: boolean;
    includeInvoiceArtifacts?: boolean;
    includeRawCreditCardTransactions?: boolean;
    /** "Apenas efetivadas": só confirmadas e, no cartão, só se a fatura já foi paga. */
    settledOnly?: boolean;
    /** Anexa as tags de cada transação (chips no extrato). Opt-in pra não custar nas demais listas. */
    includeTags?: boolean;
    /**
     * Exclui os Lançamentos ignorados (ADR 0037) — para leituras ANALÍTICAS que somam
     * receita/despesa. A lista/extrato NÃO passa isto: o ignorado aparece (riscado). O
     * pipeline de competência liga em getAccrual*TransactionsForRange.
     */
    excludeIgnored?: boolean;
    /**
     * Exclui transações e recorrências de conta ARQUIVADA ou "FORA DO SALDO TOTAL"
     * (ADR 0061) — para leituras em COMPETÊNCIA (Resultado do mês, Receitas/Despesas
     * e drill-downs), espelhando a exclusão que os saldos já fazem. O extrato e as
     * listas operacionais NÃO passam isto. Compra de cartão (account_id NULL) segue dentro.
     */
    excludeHiddenAccounts?: boolean;
    sortBy?: 'date' | 'amount' | 'category';
    sortDir?: 'asc' | 'desc';
    limit?: number;
}

export type AnalyticsMode = 'accrual' | 'cash-flow';

/**
 * Opções transversais das leituras analíticas. `installmentsFullValue` =
 * Parcelamento pelo valor total (preferência, ADR 0043): compra parcelada conta
 * uma vez, pelo valor total, na parcela-âncora. Só tem efeito em leitura de
 * DESPESA fora do regime de caixa (no caixa, parcela não é dinheiro saindo).
 * `invoicesByClosingMonth` = Cartão no mês de fechamento (preferência, ADR 0050):
 * a fatura pesa no mês em que fecha, não no do vencimento. Só tem efeito nas
 * leituras em competência que ancoram por fatura.
 *
 * "Incluir previstos" (ADR 0063) NÃO trafega por aqui: ela vale só no Fluxo de caixa
 * (composta no hook) e no Relatório personalizado (`ReportFilters.includeProjected`).
 * Os demais relatórios ficam fora por construção — a opção não chega neles.
 */
export interface AnalyticReadOptions {
    installmentsFullValue?: boolean;
    invoicesByClosingMonth?: boolean;
    /** Início do mês (ADR 0190): a chave `yyyy-MM` nomeia o período desse dia. Padrão: o do app. */
    monthStartDay?: number;
}

function cashFlowExpenseScope() {
    return sql`(
        (${transactions.creditCardId} IS NULL AND ${transactions.adjustmentType} IS NULL)
        OR ${transactions.adjustmentType} = ${AdjustmentType.INVOICE_PAYMENT}
        OR ${transactions.adjustmentType} = ${AdjustmentType.INVOICE_EXPENSE}
    )`;
}

function cashFlowIncomeScope() {
    return sql`(
        ${transactions.creditCardId} IS NULL
        OR ${transactions.adjustmentType} = ${AdjustmentType.INVOICE_CREDIT}
    )`;
}

function updateDescriptionWithInstallmentSuffix(
    description: string | undefined,
    installmentNumber: number | null | undefined,
    installmentTotal: number | null | undefined,
): string | undefined {
    if (description === undefined) return undefined;
    if (!installmentNumber || !installmentTotal) return description;
    return `${stripInstallmentSuffix(description)} (${installmentNumber}/${installmentTotal})`;
}

/**
 * Contas e cartões escolhidos num `TransactionFilters` (ADR 0170). A lista tem
 * precedência sobre o id único legado; lista vazia = sem recorte daquele lado.
 */
function resolveAccountCardScope(filters: TransactionFilters): { accountIds: string[]; cardIds: string[] } {
    const accountIds = filters.accountIds?.length
        ? filters.accountIds
        : (filters.accountId ? [filters.accountId] : []);
    const cardIds = filters.creditCardIds?.length
        ? filters.creditCardIds
        : (filters.creditCardId ? [filters.creditCardId] : []);
    return { accountIds, cardIds };
}

/**
 * Recorte por conta/cartão sobre um par de colunas (lançamento ou Regra de recorrência).
 * Contas e cartões entram em OR entre si (ADR 0170) — a Origem é excludente no
 * lançamento, então AND entre os dois lados quase sempre dava lista vazia. Cada lado
 * mantém a semântica do filtro único de antes: a conta alcança o destino da
 * transferência; o cartão lê a coluna crua. `undefined` = sem recorte.
 */
function accountCardScopeCondition(
    filters: TransactionFilters,
    columns: {
        accountId: AnySQLiteColumn;
        transferDestinationAccountId: AnySQLiteColumn;
        creditCardId: AnySQLiteColumn;
    },
): SQL | undefined {
    const { accountIds, cardIds } = resolveAccountCardScope(filters);
    const clauses: SQL[] = [];
    if (accountIds.length > 0) {
        clauses.push(or(
            inArray(columns.accountId, accountIds),
            inArray(columns.transferDestinationAccountId, accountIds),
        )!);
    }
    if (cardIds.length > 0) clauses.push(inArray(columns.creditCardId, cardIds));
    if (clauses.length === 0) return undefined;
    return clauses.length === 1 ? clauses[0] : or(...clauses);
}

function normalizeOptionalId(value?: string | null): string | null | undefined {
    if (value === undefined) return undefined;
    if (value === null) return null;

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}

async function sanitizeTransactionReferenceIds(data: {
    accountId?: string;
    creditCardId?: string;
    categoryId?: string;
    subcategoryId?: string;
    transferDestinationAccountId?: string;
}) {
    const accountId = normalizeOptionalId(data.accountId) ?? null;
    const creditCardId = normalizeOptionalId(data.creditCardId) ?? null;
    const categoryId = normalizeOptionalId(data.categoryId) ?? null;
    let subcategoryId = normalizeOptionalId(data.subcategoryId) ?? null;
    const transferDestinationAccountId = normalizeOptionalId(data.transferDestinationAccountId) ?? null;

    const [accountRows, creditCardRows, categoryRows, subcategoryRows, destinationRows] = await Promise.all([
        accountId
            ? db.select({ id: accounts.id }).from(accounts).where(eq(accounts.id, accountId))
            : Promise.resolve([]),
        creditCardId
            ? db.select({ id: creditCards.id }).from(creditCards).where(eq(creditCards.id, creditCardId))
            : Promise.resolve([]),
        categoryId
            ? db.select({ id: categories.id }).from(categories).where(eq(categories.id, categoryId))
            : Promise.resolve([]),
        subcategoryId
            ? db.select({ id: subcategories.id, categoryId: subcategories.categoryId }).from(subcategories).where(eq(subcategories.id, subcategoryId))
            : Promise.resolve([]),
        transferDestinationAccountId
            ? db.select({ id: accounts.id }).from(accounts).where(eq(accounts.id, transferDestinationAccountId))
            : Promise.resolve([]),
    ]);

    if (accountId && accountRows.length === 0) {
        throw new ExpectedError(i18n.t('ui:errors.accountGone'));
    }

    if (creditCardId && creditCardRows.length === 0) {
        throw new ExpectedError(i18n.t('ui:errors.cardGone'));
    }

    if (categoryId && categoryRows.length === 0) {
        throw new ExpectedError(i18n.t('ui:errors.categoryGone'));
    }

    if (transferDestinationAccountId && destinationRows.length === 0) {
        throw new ExpectedError(i18n.t('ui:errors.destinationAccountGone'));
    }

    const resolvedSubcategory = subcategoryRows[0];
    if (!resolvedSubcategory || (categoryId && resolvedSubcategory.categoryId !== categoryId)) {
        subcategoryId = null;
    }

    return {
        accountId,
        creditCardId,
        categoryId,
        subcategoryId,
        transferDestinationAccountId,
    };
}

function hasFinancingStructuralChanges(data: Partial<{
    type: string;
    amount: number;
    description: string;
    date: string;
    time: string;
    notes: string;
    accountId: string;
    creditCardId: string;
    invoiceMonth: string;
    categoryId: string;
    subcategoryId: string;
    transferDestinationAccountId: string;
    isRecurring: boolean;
    recurringId: string | null;
    installmentId: string | null;
    installmentNumber: number | null;
    installmentTotal: number | null;
}>, options?: {
    /** Parcela PENDENTE aceita ajuste de valor/data/conta no fluxo de pagamento (antecipação com desconto, pagar por outra conta). */
    allowSettlementFields?: boolean;
}) {
    const allowSettlementFields = options?.allowSettlementFields ?? false;
    return (
        data.type !== undefined
        || (!allowSettlementFields && data.amount !== undefined)
        || data.description !== undefined
        || (!allowSettlementFields && data.date !== undefined)
        || data.time !== undefined
        || data.notes !== undefined
        || (!allowSettlementFields && data.accountId !== undefined)
        || data.creditCardId !== undefined
        || data.invoiceMonth !== undefined
        || data.categoryId !== undefined
        || data.subcategoryId !== undefined
        || data.transferDestinationAccountId !== undefined
        || data.isRecurring !== undefined
        || data.recurringId !== undefined
        || data.installmentId !== undefined
        || data.installmentNumber !== undefined
        || data.installmentTotal !== undefined
    );
}

type AccrualExpenseTransaction = TransactionWithRelations & {
    accrualMonth: string;
};

async function getAccrualInvoiceRows(startDate: string, endDate: string, invoicesByClosingMonth = false) {
    // A coluna que decide a qual mês a fatura pertence na competência: vencimento
    // por padrão; fechamento com a preferência Cartão no mês de fechamento (ADR 0050).
    const anchorColumn = invoicesByClosingMonth ? invoices.closingDate : invoices.dueDate;
    return db
        .select({
            id: invoices.id,
            creditCardId: invoices.creditCardId,
            referenceMonth: invoices.referenceMonth,
            openingDate: invoices.openingDate,
            closingDate: invoices.closingDate,
            dueDate: invoices.dueDate,
            totalAmount: invoices.totalAmount,
            paidAmount: invoices.paidAmount,
            status: invoices.status,
            createdAt: invoices.createdAt,
            updatedAt: invoices.updatedAt,
            creditCardName: creditCards.name,
            paymentAccountId: creditCards.paymentAccountId,
            paymentAccountName: accounts.name,
            paymentAccountColor: accounts.color,
        })
        .from(invoices)
        .innerJoin(creditCards, eq(invoices.creditCardId, creditCards.id))
        .leftJoin(accounts, eq(creditCards.paymentAccountId, accounts.id))
        .where(
            and(
                gte(anchorColumn, startDate),
                lte(anchorColumn, endDate),
                eq(creditCards.isArchived, 0),
                isNull(creditCards.deletedAt),
            ),
        );
}

/**
 * Data que ancora a fatura no mês das leituras de competência. O fechamento cai
 * sempre dentro do referenceMonth (calculateInvoiceDates), então ancorar pelo
 * closingDate ≡ ancorar pelo mês de referência da fatura.
 */
function invoiceAccrualAnchorDate(
    invoice: Pick<AccrualInvoiceRow, 'closingDate' | 'dueDate'>,
    invoicesByClosingMonth: boolean,
): string {
    return invoicesByClosingMonth ? invoice.closingDate : invoice.dueDate;
}

type AccrualInvoiceRow = Awaited<ReturnType<typeof getAccrualInvoiceRows>>[number];

function isAccrualInvoiceSettled(invoice: AccrualInvoiceRow): boolean {
    if (invoice.status === 'paid') return true;
    // Tolerância de meio centavo (resíduo de float em pagamentos parciais).
    return invoice.totalAmount > 0 && invoice.paidAmount >= invoice.totalAmount - 0.005;
}

/**
 * Filtros de linha que a leitura aplicou ANTES do colapso do Parcelamento pelo
 * valor total (ADR 0043). As estatísticas do grupo (âncora + soma) precisam
 * respeitar os MESMOS filtros: sem isso, uma âncora filtrada (ex.: parcela
 * desconfirmada no relatório confirmado-apenas, ou tag só numa parcela do meio)
 * elege um MIN que não está no conjunto e o grupo inteiro some da leitura.
 */
export interface InstallmentStatsFilters {
    confirmedOnly?: boolean;
    tagIds?: string[];
    categoryIds?: string[];
}

/**
 * Estatísticas dos grupos de parcelas (âncora = menor parcela que passa nos
 * filtros + soma dessas parcelas) para o Parcelamento pelo valor total
 * (ADR 0043). Batch único.
 */
async function getInstallmentGroupStats(
    installmentIds: string[],
    filters?: InstallmentStatsFilters,
): Promise<Map<string, InstallmentGroupStats>> {
    if (installmentIds.length === 0) return new Map();

    // Espelho JS do par SQL: repete o MESMO recorte analítico (ADR 0077) — conta oculta
    // (ADR 0061) e cartão excluído (ADR 0068) ficam fora da âncora e da soma do grupo,
    // senão o grupo evapora da leitura ou ressuscita dinheiro já removido.
    const conditions = [
        inArray(transactions.installmentId, installmentIds),
        isNull(transactions.deletedAt),
        excludeIgnored(),
        excludeHiddenAccountTransactions(),
        excludeDeletedCardTransactions(),
    ];
    if (filters?.confirmedOnly) {
        conditions.push(eq(transactions.isConfirmed, 1));
    }
    if (filters?.tagIds?.length) {
        conditions.push(
            sql`${transactions.id} IN (SELECT ${transactionTags.transactionId} FROM ${transactionTags} WHERE ${transactionTags.tagId} IN (${sql.join(filters.tagIds.map((id) => sql`${id}`), sql`, `)}))`,
        );
    }
    if (filters?.categoryIds?.length) {
        conditions.push(categoryScopeCondition(filters)!);
    }

    const rows = await db
        .select({
            installmentId: transactions.installmentId,
            firstNumber: sql<number>`MIN(${transactions.installmentNumber})`.as('firstNumber'),
            total: sql<number>`COALESCE(SUM(${transactions.amount}), 0)`.as('total'),
        })
        .from(transactions)
        .where(and(...conditions))
        .groupBy(transactions.installmentId);

    return new Map(
        rows
            .filter((row) => row.installmentId != null)
            .map((row) => [row.installmentId as string, { firstNumber: row.firstNumber, total: row.total }]),
    );
}

function collectInstallmentIds(rows: { installmentId?: string | null }[]): string[] {
    return [...new Set(
        rows.map((row) => row.installmentId).filter((id): id is string => id != null),
    )];
}

/**
 * Aplica o Parcelamento pelo valor total a uma lista já carregada: parcela-âncora
 * vira o total do grupo, demais parcelas saem. Linhas comuns passam intactas.
 * `filters` deve espelhar os filtros de linha que a leitura aplicou (ver
 * InstallmentStatsFilters). Exportado para leituras fora deste repositório
 * (resumo da IA, drill-down de relatório) — NÃO reimplementar o colapso.
 */
export async function applyInstallmentFullValue<T extends {
    installmentId?: string | null;
    installmentNumber?: number | null;
    amount: number;
}>(rows: T[], filters?: InstallmentStatsFilters): Promise<T[]> {
    const installmentIds = collectInstallmentIds(rows);
    if (installmentIds.length === 0) return rows;

    const stats = await getInstallmentGroupStats(installmentIds, filters);
    return collapseInstallmentRows(rows, stats);
}

function buildAccrualInvoiceSummaryTransaction(
    invoice: AccrualInvoiceRow,
    invoiceTransactions: TransactionWithRelations[],
    anchorDate: string,
    /** Chave do período da data (ADR 0190). */
    monthKeyOf: (date: string) => string,
): AccrualExpenseTransaction | null {
    const amount = invoiceTransactions.reduce((sum, transaction) => sum + getAccrualExpenseAmount(transaction), 0);
    if (amount === 0) return null;

    const confirmed = isAccrualInvoiceSettled(invoice);
    const latestUpdatedAt = invoiceTransactions.reduce(
        (latest, transaction) => (transaction.updatedAt > latest ? transaction.updatedAt : latest),
        invoice.updatedAt,
    );

    return {
        id: buildAccrualInvoiceTransactionId(invoice.id),
        type: 'expense',
        amount,
        description: `Fatura ${invoice.creditCardName}`,
        date: anchorDate,
        time: null,
        notes: null,
        accountId: invoice.paymentAccountId ?? null,
        creditCardId: invoice.creditCardId,
        invoiceId: invoice.id,
        transferDestinationAccountId: null,
        transferId: null,
        categoryId: null,
        subcategoryId: null,
        isRecurring: 0,
        recurringId: null,
        recurringOccurrenceDate: null,
        isHiddenFromSubs: 0,
        // Resumo de fatura é agregado; as compras ignoradas já foram tiradas antes de somar.
        isIgnored: 0,
        installmentId: null,
        installmentNumber: null,
        installmentTotal: null,
        financingId: null,
        financingEntryType: null,
        financingInstallmentNumber: null,
        personalLoanId: null,
        personalLoanEntryType: null,
        adjustmentType: AdjustmentType.INVOICE_EXPENSE,
        isConfirmed: confirmed ? 1 : 0,
        createdAt: invoice.createdAt,
        updatedAt: latestUpdatedAt,
        deletedAt: null,
        accountName: invoice.paymentAccountName ?? null,
        accountColor: invoice.paymentAccountColor ?? null,
        accountIcon: null,
        creditCardName: invoice.creditCardName,
        categoryName: null,
        categoryIcon: null,
        categoryColor: null,
        subcategoryName: null,
        destinationAccountName: null,
        destinationAccountColor: null,
        invoiceReferenceMonth: invoice.referenceMonth,
        financingName: null,
        financingStatus: null,
        financingTotalInstallments: null,
        accrualMonth: monthKeyOf(anchorDate),
    };
}

async function getAccrualExpenseTransactionsForRange(
    startDate: string,
    endDate: string,
    options?: {
        includeProjectedRecurring?: boolean;
        groupInvoices?: boolean;
        /** Item de fatura recebe a data-âncora da fatura (vencimento ou fechamento) no lugar da data da compra. */
        useInvoiceAnchorDateForEntries?: boolean;
        useInvoiceSettlementState?: boolean;
        /** Parcelamento pelo valor total (ADR 0043): parcela-âncora vale o grupo inteiro. */
        installmentsFullValue?: boolean;
        /** Cartão no mês de fechamento (ADR 0050): fatura ancora no mês em que fecha. */
        invoicesByClosingMonth?: boolean;
        monthStartDay?: number;
    },
): Promise<AccrualExpenseTransaction[]> {
    const includeProjectedRecurring = options?.includeProjectedRecurring ?? true;
    const groupInvoices = options?.groupInvoices ?? false;
    const useInvoiceAnchorDateForEntries = options?.useInvoiceAnchorDateForEntries ?? false;
    const useInvoiceSettlementState = options?.useInvoiceSettlementState ?? false;
    const installmentsFullValue = options?.installmentsFullValue ?? false;
    const invoicesByClosingMonth = options?.invoicesByClosingMonth ?? false;
    // Início do mês (ADR 0190): o mês de competência é a chave do PERÍODO da data-âncora.
    const monthStartDay = options?.monthStartDay ?? getMonthStartDay();
    const monthKeyOf = (date: string) => monthKeyForDate(date, monthStartDay);

    const [regularExpenseRows, invoiceRows] = await Promise.all([
        getTransactions({
            startDate,
            endDate,
            type: 'expense',
            includeProjectedRecurring,
            includeInvoiceArtifacts: false,
            includeRawCreditCardTransactions: false,
            excludeIgnored: true,
            excludeHiddenAccounts: true,
            sortBy: 'date',
            sortDir: 'asc',
        }),
        getAccrualInvoiceRows(startDate, endDate, invoicesByClosingMonth),
    ]);

    let resolvedInvoiceGroups = await Promise.all(
        invoiceRows.map(async (invoice) => {
            const invoiceTransactions = await getInvoiceTransactions(invoice.id, invoice);
            // Compra ignorada (ADR 0037) some da composição analítica da fatura, mas segue
            // no total da fatura (getInvoiceTransactions não filtra — só aqui, no acúmulo).
            const resolvedTransactions = invoiceTransactions.filter(
                (transaction) =>
                    (includeProjectedRecurring || !transaction.isProjected)
                    && !isIgnoredTransaction(transaction.isIgnored),
            );
            return { invoice, resolvedTransactions };
        }),
    );

    // Parcelamento pelo valor total: a parcela-âncora passa a valer o grupo
    // inteiro e as demais saem da leitura analítica. Cobre a composição de
    // fatura (parcela no cartão) E as linhas avulsas (parcelamento em conta
    // comum, que vive em regularExpenseRows). UM batch de stats para o range
    // todo — o mesmo grupo aparece em N faturas e re-consultar por fatura
    // multiplicaria queries idênticas. A fatura em si (invoices.totalAmount)
    // não muda — só esta composição.
    let resolvedRegularRows = regularExpenseRows;
    if (installmentsFullValue) {
        const stats = await getInstallmentGroupStats(collectInstallmentIds([
            ...regularExpenseRows,
            ...resolvedInvoiceGroups.flatMap((group) => group.resolvedTransactions),
        ]));
        resolvedRegularRows = collapseInstallmentRows(regularExpenseRows, stats);
        resolvedInvoiceGroups = resolvedInvoiceGroups.map(({ invoice, resolvedTransactions }) => ({
            invoice,
            resolvedTransactions: collapseInstallmentRows(resolvedTransactions, stats),
        }));
    }

    const invoiceTransactionGroups = resolvedInvoiceGroups.map(({ invoice, resolvedTransactions }) => {
        const anchorDate = invoiceAccrualAnchorDate(invoice, invoicesByClosingMonth);
        if (groupInvoices) {
            const summaryTransaction = buildAccrualInvoiceSummaryTransaction(invoice, resolvedTransactions, anchorDate, monthKeyOf);
            return summaryTransaction ? [summaryTransaction] : [];
        }

        const invoiceIsSettled = isAccrualInvoiceSettled(invoice);
        return resolvedTransactions.map((transaction) => ({
            ...transaction,
            date: useInvoiceAnchorDateForEntries ? anchorDate : transaction.date,
            isConfirmed: useInvoiceSettlementState ? (invoiceIsSettled ? 1 : 0) : transaction.isConfirmed,
            accrualMonth: monthKeyOf(anchorDate),
        }));
    });

    return [
        ...resolvedRegularRows
            // Saída de empréstimo a pessoa não é despesa — é troca de ativo.
            .filter((transaction) => !isPersonalLoanPrincipalEntry(transaction.personalLoanEntryType))
            .map((transaction) => ({
                ...transaction,
                accrualMonth: monthKeyOf(transaction.date),
            })),
        ...invoiceTransactionGroups.flat(),
    ].filter((transaction) => getAccrualExpenseAmount(transaction) !== 0);
}

async function getAccrualIncomeTransactionsForRange(
    startDate: string,
    endDate: string,
    options?: {
        includeProjectedRecurring?: boolean;
    },
): Promise<TransactionWithRelations[]> {
    const includeProjectedRecurring = options?.includeProjectedRecurring ?? true;

    const rows = await getTransactions({
        startDate,
        endDate,
        type: 'income',
        includeProjectedRecurring,
        includeInvoiceArtifacts: false,
        excludeIgnored: true,
        excludeHiddenAccounts: true,
        sortBy: 'date',
        sortDir: 'asc',
    });

    // Recebimento de empréstimo a pessoa não é receita (só o excedente é).
    return rows.filter((transaction) => !isPersonalLoanPrincipalEntry(transaction.personalLoanEntryType));
}

async function getAccrualMonthlyTotals(
    startMonth: string,
    endMonth: string,
    includeProjectedRecurring: boolean,
    options?: AnalyticReadOptions,
): Promise<MonthlyTotal[]> {
    const monthStartDay = options?.monthStartDay ?? getMonthStartDay();
    const { startDate } = getMonthDateRange(startMonth, monthStartDay);
    const { endDate } = getMonthDateRange(endMonth, monthStartDay);

    const [incomeTransactions, expenseTransactions] = await Promise.all([
        getAccrualIncomeTransactionsForRange(startDate, endDate, { includeProjectedRecurring }),
        getAccrualExpenseTransactionsForRange(startDate, endDate, {
            includeProjectedRecurring,
            installmentsFullValue: options?.installmentsFullValue,
            invoicesByClosingMonth: options?.invoicesByClosingMonth,
            monthStartDay,
        }),
    ]);

    return aggregateAccrualMonthlyTotals(
        incomeTransactions,
        expenseTransactions,
        (date) => monthKeyForDate(date, monthStartDay),
    );
}

/** Total de transações não-excluídas — usado por gatilhos de "momento de valor" (ex.: convite Pro). */
export async function countTransactions(): Promise<number> {
    const [row] = await db
        .select({ count: sql<number>`COUNT(*)` })
        .from(transactions)
        .where(isNull(transactions.deletedAt));
    return row?.count ?? 0;
}

/**
 * Lançamentos que a PESSOA fez desde um instante (ISO 8601) — o "você fez N lançamentos com o
 * Pro" do teste cancelado (ADR 0173). Conta receita e despesa criadas a partir de `sinceIso` e
 * deixa de fora o que o app gera sozinho: ocorrências de recorrência, parcelas 2+ de uma compra
 * parcelada (a compra é 1 lançamento) e lançamentos de financiamento.
 */
export async function countEntriesCreatedSince(sinceIso: string): Promise<number> {
    const since = new Date(sinceIso);
    if (Number.isNaN(since.getTime())) return 0;
    const [row] = await db
        .select({ count: sql<number>`COUNT(*)` })
        .from(transactions)
        .where(
            and(
                isNull(transactions.deletedAt),
                inArray(transactions.type, ['income', 'expense']),
                // createdAt é gravado por toISOString(): normalizar o limite pro mesmo formato
                // mantém a comparação de texto correta mesmo quando o RC manda sem milissegundos.
                sql`${transactions.createdAt} >= ${since.toISOString()}`,
                isNull(transactions.recurringId),
                isNull(transactions.financingId),
                sql`COALESCE(${transactions.installmentNumber}, 1) <= 1`,
            ),
        );
    return row?.count ?? 0;
}

/**
 * Quando a PESSOA fez o último lançamento (createdAt, ISO 8601), com o mesmo recorte de
 * `countEntriesCreatedSince`: recorrência, parcela 2+ e financiamento o app gera sozinho. É o
 * "há 3 dias sem lançar" do modo pausado do iPhone (ADR 0176). `null` sem nenhum.
 */
export async function getLastEntryCreatedAt(): Promise<string | null> {
    const [row] = await db
        .select({ last: sql<string | null>`MAX(${transactions.createdAt})` })
        .from(transactions)
        .where(
            and(
                isNull(transactions.deletedAt),
                inArray(transactions.type, ['income', 'expense']),
                isNull(transactions.recurringId),
                isNull(transactions.financingId),
                sql`COALESCE(${transactions.installmentNumber}, 1) <= 1`,
            ),
        );
    return row?.last ?? null;
}

/**
 * Resumo dos GASTOS do mês (total + contagem) — números reais exibidos na folha de
 * oferta contextual (pro-offer, design 5a: "Você registrou R$ X em N gastos").
 */
export async function getMonthExpenseSummary(
    month: string,
): Promise<{ total: number; count: number }> {
    const [row] = await db
        .select({
            total: sql<number>`COALESCE(SUM(${transactions.amount}), 0)`,
            count: sql<number>`COUNT(*)`,
        })
        .from(transactions)
        .where(
            and(
                eq(transactions.type, 'expense'),
                sql`substr(${transactions.date}, 1, 7) = ${month}`,
                isNull(transactions.deletedAt),
                excludeNonAnalyticInvoiceAdjustments(),
                excludeIgnored(),
                excludeDeletedCardTransactions(),
            ),
        );
    return { total: row?.total ?? 0, count: row?.count ?? 0 };
}

export async function getTransactions(filters?: TransactionFilters): Promise<TransactionWithRelations[]> {
    // Incondicional de propósito (ADR 0068): cartão excluído não existe pra NENHUMA
    // leitura desta função — quem quer histórico usa Arquivar.
    const conditions = [isNull(transactions.deletedAt), excludeDeletedCardTransactions()];

    if (filters?.startDate) conditions.push(gte(transactions.date, filters.startDate));
    if (filters?.endDate) conditions.push(lte(transactions.date, filters.endDate));
    if (filters?.type) conditions.push(eq(transactions.type, filters.type));
    // Contas OR cartões (ADR 0170). O destino da transferência entra junto: uma
    // transferência PRA conta filtrada é movimento dela, mesmo com a origem em outra conta.
    const accountCardScope = filters ? accountCardScopeCondition(filters, transactions) : undefined;
    if (accountCardScope) conditions.push(accountCardScope);
    if (filters?.financingId) conditions.push(eq(transactions.financingId, filters.financingId));
    if (filters?.personalLoanId) conditions.push(eq(transactions.personalLoanId, filters.personalLoanId));
    if (filters?.categoryIds?.length) {
        conditions.push(categoryScopeCondition(filters)!);
    } else if (filters?.categoryId) {
        conditions.push(eq(transactions.categoryId, filters.categoryId));
    }
    if (filters?.subcategoryId) conditions.push(eq(transactions.subcategoryId, filters.subcategoryId));
    // Recorte por origem (ADR 0103): OR entre contas e cartões, não AND — a lista precisa
    // bater EXATAMENTE com a barra do orçamento, senão o detalhe mostra R$ 760 em
    // lançamentos embaixo de uma barra que soma R$ 640. Lista vazia = sem recorte.
    if (filters?.origins && filters.origins.length > 0) {
        const accountIds = filters.origins.filter((o) => o.kind === 'account').map((o) => o.id);
        const cardIds = filters.origins.filter((o) => o.kind === 'card').map((o) => o.id);
        const clauses = [];
        if (accountIds.length > 0) clauses.push(inArray(transactions.accountId, accountIds));
        // Mesmo COALESCE do resto do app: o cartão nem sempre está na coluna crua.
        if (cardIds.length > 0) clauses.push(sql`${originCardIdExpr()} IN (${sql.join(cardIds.map((id) => sql`${id}`), sql`, `)})`);
        if (clauses.length > 0) conditions.push(or(...clauses)!);
    }
    if (filters?.search) {
        conditions.push(
            or(
                like(transactions.description, `%${filters.search}%`),
                like(transactions.notes, `%${filters.search}%`),
            )!,
        );
    }
    if (filters?.isConfirmed !== undefined) {
        conditions.push(eq(transactions.isConfirmed, filters.isConfirmed ? 1 : 0));
    }
    if (filters?.minAmount !== undefined) conditions.push(gte(transactions.amount, filters.minAmount));
    if (filters?.maxAmount !== undefined) conditions.push(lte(transactions.amount, filters.maxAmount));
    if (filters?.includeInvoiceArtifacts === false) {
        conditions.push(excludeNonAnalyticInvoiceAdjustments());
    }
    if (filters?.includeRawCreditCardTransactions === false) {
        conditions.push(excludeRawCreditCardTransactions());
    }
    if (filters?.excludeIgnored) {
        conditions.push(excludeIgnored());
    }
    if (filters?.excludeHiddenAccounts) {
        conditions.push(excludeHiddenAccountTransactions());
    }
    if (filters?.settledOnly) {
        // "Apenas efetivadas": só confirmadas e, no caso de compra no cartão, só se a
        // fatura dela já foi TOTALMENTE paga (o dinheiro saiu de fato). Demais transações
        // (conta, pagamento de fatura) entram normalmente quando confirmadas.
        conditions.push(eq(transactions.isConfirmed, 1));
        conditions.push(
            sql`(${transactions.creditCardId} IS NULL OR (${invoices.paidAmount} >= ${invoices.totalAmount} AND ${invoices.totalAmount} > 0))`,
        );
    }
    if (filters?.tagIds && filters.tagIds.length > 0) {
        conditions.push(
            sql`${transactions.id} IN (SELECT ${transactionTags.transactionId} FROM ${transactionTags} WHERE ${transactionTags.tagId} IN (${sql.join(filters.tagIds.map(id => sql`${id}`), sql`, `)}))`,
        );
    }

    const orderedQuery = db
        .select({
            id: transactions.id,
            type: transactions.type,
            amount: transactions.amount,
            description: transactions.description,
            date: transactions.date,
            time: transactions.time,
            notes: transactions.notes,
            accountId: transactions.accountId,
            creditCardId: sql<string | null>`COALESCE(${transactions.creditCardId}, ${invoices.creditCardId})`.as('creditCardId'),
            invoiceId: transactions.invoiceId,
            invoiceReferenceMonth: invoices.referenceMonth,
            transferDestinationAccountId: transactions.transferDestinationAccountId,
            transferId: transactions.transferId,
            categoryId: transactions.categoryId,
            subcategoryId: transactions.subcategoryId,
            isRecurring: transactions.isRecurring,
            recurringId: transactions.recurringId,
            recurringOccurrenceDate: transactions.recurringOccurrenceDate,
            isHiddenFromSubs: transactions.isHiddenFromSubs,
            isIgnored: transactions.isIgnored,
            installmentId: transactions.installmentId,
            installmentNumber: transactions.installmentNumber,
            installmentTotal: transactions.installmentTotal,
            financingId: transactions.financingId,
            financingEntryType: transactions.financingEntryType,
            financingInstallmentNumber: transactions.financingInstallmentNumber,
            personalLoanId: transactions.personalLoanId,
            personalLoanEntryType: transactions.personalLoanEntryType,
            adjustmentType: transactions.adjustmentType,
            isConfirmed: transactions.isConfirmed,
            createdAt: transactions.createdAt,
            updatedAt: transactions.updatedAt,
            deletedAt: transactions.deletedAt,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountIcon: accounts.icon,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
            subcategoryName: subcategories.name,
            creditCardName: creditCards.name,
            financingName: financings.name,
            financingStatus: financings.status,
            financingTotalInstallments: financings.totalInstallments,
            reservaYieldName: reservaYieldField('name').as('reservaYieldName'),
            reservaYieldColor: reservaYieldField('color').as('reservaYieldColor'),
            reservaYieldIcon: reservaYieldField('icon').as('reservaYieldIcon'),
        })
        .from(transactions)
        .leftJoin(accounts, eq(transactions.accountId, accounts.id))
        .leftJoin(categories, eq(transactions.categoryId, categories.id))
        .leftJoin(subcategories, eq(transactions.subcategoryId, subcategories.id))
        .leftJoin(invoices, eq(transactions.invoiceId, invoices.id))
        .leftJoin(financings, eq(transactions.financingId, financings.id))
        .leftJoin(creditCards, eq(sql`COALESCE(${transactions.creditCardId}, ${invoices.creditCardId})`, creditCards.id))
        .where(and(...conditions))
        .orderBy(
            ...(filters?.sortBy === 'amount'
                ? [filters.sortDir === 'asc' ? asc(transactions.amount) : desc(transactions.amount)]
                : filters?.sortBy === 'category'
                    ? [filters.sortDir === 'asc' ? asc(categories.name) : desc(categories.name), desc(transactions.date)]
                    // Desempate intra-dia pela hora da compra quando existe (notificação/OCR).
                    : [
                        filters?.sortDir === 'asc' ? asc(transactions.date) : desc(transactions.date),
                        filters?.sortDir === 'asc' ? asc(transactions.time) : desc(transactions.time),
                        desc(transactions.createdAt),
                    ]
            ),
        );

    const rows = filters?.limit ? await orderedQuery.limit(filters.limit) : await orderedQuery;

    // Fetch destination account names for transfers
    const transferIds = rows
        .filter((r) => r.type === 'transfer' && r.transferDestinationAccountId)
        .map((r) => r.transferDestinationAccountId!);

    let destMap = new Map<string, { name: string; color: string }>();
    if (transferIds.length > 0) {
        const uniqueIds = [...new Set(transferIds)];
        const destinationRows = await db
            .select({ id: accounts.id, name: accounts.name, color: accounts.color })
            .from(accounts)
            .where(inArray(accounts.id, uniqueIds));

        for (const destinationRow of destinationRows) {
            destMap.set(destinationRow.id, {
                name: destinationRow.name,
                color: destinationRow.color,
            });
        }
    }

    // Tags por transação (opt-in via `includeTags`) — chips no extrato. IN em lotes
    // pra não estourar o limite de variáveis do SQLite em listas grandes.
    const tagsByTransaction = new Map<string, TransactionTagInfo[]>();
    if (filters?.includeTags && rows.length > 0) {
        const ids = rows.map((row) => row.id);
        const CHUNK = 400;
        for (let i = 0; i < ids.length; i += CHUNK) {
            const tagRows = await db
                .select({
                    transactionId: transactionTags.transactionId,
                    id: tags.id,
                    name: tags.name,
                    color: tags.color,
                })
                .from(transactionTags)
                .innerJoin(tags, eq(transactionTags.tagId, tags.id))
                .where(inArray(transactionTags.transactionId, ids.slice(i, i + CHUNK)))
                .orderBy(asc(tags.name));
            for (const tagRow of tagRows) {
                const list = tagsByTransaction.get(tagRow.transactionId) ?? [];
                list.push({ id: tagRow.id, name: tagRow.name, color: tagRow.color });
                tagsByTransaction.set(tagRow.transactionId, list);
            }
        }
    }

    const resolvedRows = rows.map((row) => {
        const dest = row.transferDestinationAccountId
            ? destMap.get(row.transferDestinationAccountId)
            : undefined;
        return {
            ...row,
            destinationAccountName: dest?.name ?? null,
            destinationAccountColor: dest?.color ?? null,
            tags: tagsByTransaction.get(row.id),
        };
    });

    // Filtro por tag NÃO desliga mais a projeção (ADR 0101). A Regra é dona da tag
    // (recurring_transaction_tags, desde jun/2026) e a Ocorrência projetada herda — então dá
    // pra filtrar direito, em JS, logo abaixo. O guard antigo vinha da premissa de que "a
    // regra não tem tag", que já era falsa quando virou consequência do ADR 0063.
    const hasTagFilter = (filters?.tagIds?.length ?? 0) > 0;

    if (!filters?.includeProjectedRecurring || !filters.startDate || !filters.endDate || filters.isConfirmed === true || filters.financingId || filters.settledOnly) {
        return resolvedRows;
    }

    const recurringConditions = [
        eq(recurringTransactions.isActive, 1),
        isNull(recurringTransactions.deletedAt),
        lte(recurringTransactions.nextOccurrence, filters.endDate),
        sql`(${recurringTransactions.endDate} IS NULL OR ${recurringTransactions.endDate} >= ${filters.startDate})`,
    ];

    if (filters.type) recurringConditions.push(eq(recurringTransactions.type, filters.type));
    // Mesmo recorte conta OR cartão das materializadas (ADR 0170). Antes a projeção só
    // olhava o `accountId` único: com `accountIds` (relatório, extrato) vinham as
    // Ocorrências projetadas de TODAS as contas.
    const recurringAccountCardScope = accountCardScopeCondition(filters, recurringTransactions);
    if (recurringAccountCardScope) recurringConditions.push(recurringAccountCardScope);
    if (filters.categoryIds?.length) {
        recurringConditions.push(inArray(recurringTransactions.categoryId, filters.categoryIds));
    } else if (filters.categoryId) {
        recurringConditions.push(eq(recurringTransactions.categoryId, filters.categoryId));
    }
    if (filters.subcategoryId) recurringConditions.push(eq(recurringTransactions.subcategoryId, filters.subcategoryId));
    if (filters.search) recurringConditions.push(like(recurringTransactions.description, `%${filters.search}%`));
    if (filters.minAmount !== undefined) recurringConditions.push(gte(recurringTransactions.amount, filters.minAmount));
    if (filters.maxAmount !== undefined) recurringConditions.push(lte(recurringTransactions.amount, filters.maxAmount));
    if (filters.includeRawCreditCardTransactions === false) {
        recurringConditions.push(sql`${recurringTransactions.creditCardId} IS NULL`);
    }
    if (filters.excludeHiddenAccounts) {
        recurringConditions.push(excludeHiddenAccountRecurringRules());
    }
    // Mesmo recorte das linhas reais (ADR 0169): a projetada herda o Ignorado da regra, então
    // a leitura analítica a tira junto. A lista/extrato não passa o filtro — ela aparece riscada.
    if (filters.excludeIgnored) {
        recurringConditions.push(excludeIgnoredRecurringRules());
    }

    const recurringRows = await db
        .select({
            id: recurringTransactions.id,
            type: recurringTransactions.type,
            amount: recurringTransactions.amount,
            description: recurringTransactions.description,
            notes: recurringTransactions.notes,
            time: recurringTransactions.time,
            isIgnored: recurringTransactions.isIgnored,
            frequency: recurringTransactions.frequency,
            interval: recurringTransactions.interval,
            weekdays: recurringTransactions.weekdays,
            monthlyMode: recurringTransactions.monthlyMode,
            startDate: recurringTransactions.startDate,
            nextOccurrence: recurringTransactions.nextOccurrence,
            endDate: recurringTransactions.endDate,
            skippedDates: recurringTransactions.skippedDates,
            accountId: recurringTransactions.accountId,
            creditCardId: recurringTransactions.creditCardId,
            categoryId: recurringTransactions.categoryId,
            subcategoryId: recurringTransactions.subcategoryId,
            transferDestinationAccountId: recurringTransactions.transferDestinationAccountId,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountIcon: accounts.icon,
            creditCardName: creditCards.name,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
            subcategoryName: subcategories.name,
        })
        .from(recurringTransactions)
        .leftJoin(accounts, eq(recurringTransactions.accountId, accounts.id))
        .leftJoin(creditCards, eq(recurringTransactions.creditCardId, creditCards.id))
        .leftJoin(categories, eq(recurringTransactions.categoryId, categories.id))
        .leftJoin(subcategories, eq(recurringTransactions.subcategoryId, subcategories.id))
        .where(and(...recurringConditions));

    const recurringDestinationIds = recurringRows
        .map((row) => row.transferDestinationAccountId)
        .filter(Boolean) as string[];
    const recurringDestinationMap = new Map<string, { name: string; color: string }>();

    if (recurringDestinationIds.length > 0) {
        const destinationRows = await db
            .select({ id: accounts.id, name: accounts.name, color: accounts.color })
            .from(accounts)
            .where(inArray(accounts.id, [...new Set(recurringDestinationIds)]));

        for (const destinationRow of destinationRows) {
            recurringDestinationMap.set(destinationRow.id, {
                name: destinationRow.name,
                color: destinationRow.color,
            });
        }
    }

    // Deduplica a projeção pela data AGENDADA da ocorrência (recurringOccurrenceDate),
    // não pela data real da transação — senão uma ocorrência paga antecipada/adiada
    // (que moveu a data) fura o dedup e reaparece como fantasma projetada. A janela
    // também é pelo slot agendado. Fallback p/ `date` em linhas antigas (pré-migração).
    const occurrenceSlot = sql<string>`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date})`;
    const materializedOccurrences = await db
        .select({
            recurringId: transactions.recurringId,
            occurrenceDate: occurrenceSlot,
        })
        .from(transactions)
        .where(
            and(
                isNull(transactions.deletedAt),
                gte(occurrenceSlot, filters.startDate),
                lte(occurrenceSlot, filters.endDate),
                sql`${transactions.recurringId} IS NOT NULL`,
            ),
        );

    // Tags da Regra, opt-in (ADR 0101): só quando a leitura exibe os chips (`includeTags`) ou
    // precisa filtrar por tag. Quem só soma dinheiro (forecast, saldos) não paga o SELECT.
    const needsRuleTags = !!filters.includeTags || hasTagFilter;
    const tagsByRecurring = needsRuleTags
        ? await getTagInfoForRecurringIds(recurringRows.map((row) => row.id))
        : new Map<string, TransactionTagInfo[]>();

    let projectedRows = buildProjectedRecurringTransactions(
        recurringRows.map((row) => ({
            ...row,
            tags: tagsByRecurring.get(row.id),
            destinationAccountName: row.transferDestinationAccountId
                ? (recurringDestinationMap.get(row.transferDestinationAccountId)?.name ?? null)
                : null,
            destinationAccountColor: row.transferDestinationAccountId
                ? (recurringDestinationMap.get(row.transferDestinationAccountId)?.color ?? null)
                : null,
        })),
        filters.startDate,
        filters.endDate,
        new Set(
            materializedOccurrences
                .filter((row) => row.recurringId)
                .map((row) => `${row.recurringId}:${row.occurrenceDate}`),
        ),
    ) as TransactionWithRelations[];

    // O filtro por tag das materializadas é SQL (subselect em transaction_tags); a projetada
    // não está lá, então o mesmo recorte é aplicado aqui sobre a tag herdada da Regra.
    if (hasTagFilter) {
        const wanted = new Set(filters.tagIds);
        projectedRows = projectedRows.filter((row) => row.tags?.some((tag) => wanted.has(tag.id)));
    }

    const combinedRows = [...resolvedRows, ...projectedRows];

    if (filters.sortBy === 'amount') {
        return combinedRows.sort((left, right) =>
            filters.sortDir === 'asc'
                ? left.amount - right.amount
                : right.amount - left.amount,
        );
    }

    if (filters.sortBy === 'category') {
        return combinedRows.sort((left, right) => {
            const leftCategory = left.categoryName ?? '';
            const rightCategory = right.categoryName ?? '';
            const categoryCompare = filters.sortDir === 'asc'
                ? leftCategory.localeCompare(rightCategory)
                : rightCategory.localeCompare(leftCategory);

            if (categoryCompare !== 0) {
                return categoryCompare;
            }

            return compareIso(right.date, left.date);
        });
    }

    return combinedRows.sort((left, right) =>
        filters.sortDir === 'asc'
            ? compareIso(left.date, right.date) || compareIso(left.createdAt, right.createdAt)
            : compareIso(right.date, left.date) || compareIso(right.createdAt, left.createdAt),
    );
}

export async function getTransactionsByDate(
    filters?: TransactionFilters,
): Promise<{ date: string; transactions: TransactionWithRelations[] }[]> {
    const allTransactions = await getTransactions(filters);
    const grouped = new Map<string, TransactionWithRelations[]>();

    for (const t of allTransactions) {
        const list = grouped.get(t.date) ?? [];
        list.push(t);
        grouped.set(t.date, list);
    }

    return Array.from(grouped.entries())
        .sort(([a], [b]) =>
            filters?.sortDir === 'asc'
                ? compareIso(a, b)
                : compareIso(b, a),
        )
        .map(([date, txns]) => ({ date, transactions: txns }));
}

export async function getRecentExpenses(
    limit = 4,
    options?: { includePending?: boolean },
): Promise<TransactionWithRelations[]> {
    const includePending = options?.includePending ?? false;
    const today = format(new Date(), 'yyyy-MM-dd');
    // Parcelamento aqui tem dois mundos: no CARTÃO a compra aparece UMA vez (parcela 1),
    // ancorada na data da compra — o débito real é o pagamento da fatura. Na CONTA cada
    // parcela é um débito de verdade no dia dela, então toda parcela efetivada entra,
    // na própria data.
    const isCardInstallmentSeries = sql`
        ${transactions.installmentId} IS NOT NULL
        AND (${transactions.invoiceId} IS NOT NULL OR ${transactions.creditCardId} IS NOT NULL)
    `;
    const recentExpenseDate = sql<string>`
        CASE
            WHEN ${isCardInstallmentSeries} THEN
                MIN(
                    COALESCE(${installments.startDate}, ${transactions.date}),
                    substr(${transactions.createdAt}, 1, 10),
                    ${transactions.date}
                )
            ELSE ${transactions.date}
        END
    `;
    const conditions = [
        eq(transactions.type, 'expense'),
        isNull(transactions.deletedAt),
        isNull(transactions.adjustmentType),
        excludeDeletedCardTransactions(),
        or(
            isNull(transactions.installmentNumber),
            eq(transactions.installmentNumber, 1),
            sql`NOT (${isCardInstallmentSeries})`,
        ),
        lte(recentExpenseDate, today),
    ];

    if (!includePending) {
        conditions.push(eq(transactions.isConfirmed, 1));
    }

    const rows = await db
        .select({
            id: transactions.id,
            type: transactions.type,
            amount: transactions.amount,
            description: transactions.description,
            date: recentExpenseDate.as('date'),
            time: transactions.time,
            notes: transactions.notes,
            accountId: transactions.accountId,
            creditCardId: sql<string | null>`COALESCE(${transactions.creditCardId}, ${invoices.creditCardId})`.as('creditCardId'),
            invoiceId: transactions.invoiceId,
            invoiceReferenceMonth: invoices.referenceMonth,
            transferDestinationAccountId: transactions.transferDestinationAccountId,
            transferId: transactions.transferId,
            categoryId: transactions.categoryId,
            subcategoryId: transactions.subcategoryId,
            isRecurring: transactions.isRecurring,
            recurringId: transactions.recurringId,
            recurringOccurrenceDate: transactions.recurringOccurrenceDate,
            isHiddenFromSubs: transactions.isHiddenFromSubs,
            isIgnored: transactions.isIgnored,
            installmentId: transactions.installmentId,
            installmentNumber: transactions.installmentNumber,
            installmentTotal: transactions.installmentTotal,
            financingId: transactions.financingId,
            financingEntryType: transactions.financingEntryType,
            financingInstallmentNumber: transactions.financingInstallmentNumber,
            personalLoanId: transactions.personalLoanId,
            personalLoanEntryType: transactions.personalLoanEntryType,
            adjustmentType: transactions.adjustmentType,
            isConfirmed: transactions.isConfirmed,
            createdAt: transactions.createdAt,
            updatedAt: transactions.updatedAt,
            deletedAt: transactions.deletedAt,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountIcon: accounts.icon,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
            subcategoryName: subcategories.name,
            creditCardName: creditCards.name,
            financingName: financings.name,
            financingStatus: financings.status,
            financingTotalInstallments: financings.totalInstallments,
        })
        .from(transactions)
        .leftJoin(accounts, eq(transactions.accountId, accounts.id))
        .leftJoin(categories, eq(transactions.categoryId, categories.id))
        .leftJoin(subcategories, eq(transactions.subcategoryId, subcategories.id))
        .leftJoin(invoices, eq(transactions.invoiceId, invoices.id))
        .leftJoin(installments, eq(transactions.installmentId, installments.id))
        .leftJoin(financings, eq(transactions.financingId, financings.id))
        .leftJoin(creditCards, eq(sql`COALESCE(${transactions.creditCardId}, ${invoices.creditCardId})`, creditCards.id))
        .where(and(...conditions))
        .orderBy(
            desc(recentExpenseDate),
            desc(transactions.createdAt),
        )
        .limit(limit);

    return rows.map((row) => ({
        ...row,
        destinationAccountName: null,
        destinationAccountColor: null,
    }));
}

/**
 * "Novo gasto" (ver CONTEXT.md): despesa que entra pela 1ª vez na vida financeira
 * do usuário no período. Dois casos: (1) despesa avulsa que ENTROU no período — a
 * compra ou o cadastro, o que vier primeiro (parcelado: a compra da série);
 * (2) a PRIMEIRA ocorrência de uma Regra de recorrência — contada no mês em que ela
 * cai (pelo slot agendado, não pelo createdAt), inclusive ainda projetada. Ocorrências
 * seguintes de recorrência ficam de fora: o gasto é antigo, só se repete.
 */
async function getNewExpenseTransactionsForRange(
    startDate: string,
    endDate: string,
): Promise<TransactionWithRelations[]> {
    const transactionCreatedDate = sql<string>`substr(${transactions.createdAt}, 1, 10)`;
    // Dia em que a despesa avulsa entrou na vida financeira: o que vier PRIMEIRO entre a
    // compra e o cadastro. Só o cadastro jogava histórico importado, parcelamento em
    // andamento e o esquecido lançado depois no mês do cadastro; só a compra jogaria o
    // boleto agendado pra frente. Parcelado usa a compra da SÉRIE (startDate), pra todas
    // as parcelas caírem no mesmo mês e o agrupamento por installmentId não se dividir.
    const enteredDate = sql<string>`
        CASE
            WHEN ${transactions.installmentId} IS NOT NULL THEN
                MIN(COALESCE(${installments.startDate}, ${transactions.date}), ${transactionCreatedDate})
            ELSE MIN(${transactions.date}, ${transactionCreatedDate})
        END
    `;
    // Slot AGENDADO da ocorrência (a data real é editável e furaria o critério).
    const occurrenceSlot = sql<string>`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date})`;

    const transactionShape = {
        id: transactions.id,
        type: transactions.type,
        amount: transactions.amount,
        description: transactions.description,
        date: transactions.date,
        time: transactions.time,
        notes: transactions.notes,
        accountId: transactions.accountId,
        creditCardId: sql<string | null>`COALESCE(${transactions.creditCardId}, ${invoices.creditCardId})`.as('creditCardId'),
        invoiceId: transactions.invoiceId,
        invoiceReferenceMonth: invoices.referenceMonth,
        transferDestinationAccountId: transactions.transferDestinationAccountId,
        transferId: transactions.transferId,
        categoryId: transactions.categoryId,
        subcategoryId: transactions.subcategoryId,
        isRecurring: transactions.isRecurring,
        recurringId: transactions.recurringId,
        recurringOccurrenceDate: transactions.recurringOccurrenceDate,
        isHiddenFromSubs: transactions.isHiddenFromSubs,
        isIgnored: transactions.isIgnored,
        installmentId: transactions.installmentId,
        installmentNumber: transactions.installmentNumber,
        installmentTotal: transactions.installmentTotal,
        financingId: transactions.financingId,
        financingEntryType: transactions.financingEntryType,
        financingInstallmentNumber: transactions.financingInstallmentNumber,
        personalLoanId: transactions.personalLoanId,
        personalLoanEntryType: transactions.personalLoanEntryType,
        adjustmentType: transactions.adjustmentType,
        isConfirmed: transactions.isConfirmed,
        createdAt: transactions.createdAt,
        updatedAt: transactions.updatedAt,
        deletedAt: transactions.deletedAt,
        accountName: accounts.name,
        accountColor: accounts.color,
        accountIcon: accounts.icon,
        categoryName: categories.name,
        categoryIcon: categories.icon,
        categoryColor: categories.color,
        subcategoryName: subcategories.name,
        creditCardName: creditCards.name,
        financingName: financings.name,
        financingStatus: financings.status,
        financingTotalInstallments: financings.totalInstallments,
    };

    const rows = await db
        .select(transactionShape)
        .from(transactions)
        .leftJoin(accounts, eq(transactions.accountId, accounts.id))
        .leftJoin(categories, eq(transactions.categoryId, categories.id))
        .leftJoin(subcategories, eq(transactions.subcategoryId, subcategories.id))
        .leftJoin(invoices, eq(transactions.invoiceId, invoices.id))
        .leftJoin(installments, eq(transactions.installmentId, installments.id))
        .leftJoin(financings, eq(transactions.financingId, financings.id))
        .leftJoin(creditCards, eq(sql`COALESCE(${transactions.creditCardId}, ${invoices.creditCardId})`, creditCards.id))
        .where(
            and(
                eq(transactions.type, 'expense'),
                isNull(transactions.deletedAt),
                isNull(transactions.adjustmentType),
                excludeIgnored(),
                // Cadastrar um financiamento insere a série inteira de parcelas com o
                // mesmo createdAt — sem este filtro, o contrato todo (ex.: 60x) explodia
                // como "gasto novo" do mês. Abatimentos extras continuam contando: são
                // dinheiro novo de verdade.
                or(
                    isNull(transactions.financingEntryType),
                    sql`${transactions.financingEntryType} != ${FinancingEntryType.REGULAR_INSTALLMENT}`,
                ),
                excludePersonalLoanPrincipal(),
                or(
                    // Despesa avulsa: entrou no período (compra ou cadastro, o que vier
                    // primeiro). Recorrente fica de fora deste braço — ocorrência
                    // materializada nasce com createdAt = agora e vazava como "gasto
                    // novo" todo mês (o bug clássico).
                    and(
                        sql`(${transactions.isRecurring} = 0 AND ${transactions.recurringId} IS NULL)`,
                        gte(enteredDate, startDate),
                        lte(enteredDate, endDate),
                    ),
                    // Recorrente: candidata a 1ª ocorrência da série, pelo slot no
                    // período. O corte "é mesmo a 1ª?" é feito em JS contra a regra.
                    and(
                        sql`${transactions.recurringId} IS NOT NULL`,
                        gte(occurrenceSlot, startDate),
                        lte(occurrenceSlot, endDate),
                    ),
                ),
            ),
        );

    const normalizedRows = rows
        .map((row) => ({
            ...row,
            destinationAccountName: null,
            destinationAccountColor: null,
        }));

    // ── Recorrências: só a 1ª ocorrência da série conta como gasto novo ──
    const recurringRows = normalizedRows.filter((row) => row.recurringId != null);
    const firstSlotByRule = new Map<string, string>();
    if (recurringRows.length > 0) {
        const ruleIds = [...new Set(recurringRows.map((row) => row.recurringId as string))];
        const ruleRows = await db
            .select({
                id: recurringTransactions.id,
                frequency: recurringTransactions.frequency,
                interval: recurringTransactions.interval,
                weekdays: recurringTransactions.weekdays,
                monthlyMode: recurringTransactions.monthlyMode,
                startDate: recurringTransactions.startDate,
            })
            .from(recurringTransactions)
            .where(inArray(recurringTransactions.id, ruleIds));
        for (const rule of ruleRows) {
            firstSlotByRule.set(rule.id, firstOccurrence(rule));
        }
    }

    const materializedSlotKeys = new Set(
        recurringRows.map((row) => `${row.recurringId}:${row.recurringOccurrenceDate ?? row.date}`),
    );

    const scopedRows = normalizedRows.filter((row) => {
        if (row.recurringId == null) return true;
        const firstSlot = firstSlotByRule.get(row.recurringId);
        return firstSlot != null && (row.recurringOccurrenceDate ?? row.date) === firstSlot;
    });

    // 1ª ocorrência ainda PROJETADA: recorrência recém-criada cuja primeira cobrança
    // cai no período mas não foi lançada — já é gasto novo (igual "No mês" projeta).
    // nextOccurrence == firstOccurrence(regra) garante que nada foi materializado nem
    // pulado antes; regra pausada/encerrada fica de fora (isActive), como na projeção
    // do accrual.
    const candidateRules = await db
        .select({
            id: recurringTransactions.id,
            type: recurringTransactions.type,
            amount: recurringTransactions.amount,
            description: recurringTransactions.description,
            notes: recurringTransactions.notes,
            time: recurringTransactions.time,
            isIgnored: recurringTransactions.isIgnored,
            frequency: recurringTransactions.frequency,
            interval: recurringTransactions.interval,
            weekdays: recurringTransactions.weekdays,
            monthlyMode: recurringTransactions.monthlyMode,
            startDate: recurringTransactions.startDate,
            nextOccurrence: recurringTransactions.nextOccurrence,
            endDate: recurringTransactions.endDate,
            skippedDates: recurringTransactions.skippedDates,
            accountId: recurringTransactions.accountId,
            creditCardId: recurringTransactions.creditCardId,
            categoryId: recurringTransactions.categoryId,
            subcategoryId: recurringTransactions.subcategoryId,
            transferDestinationAccountId: recurringTransactions.transferDestinationAccountId,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountIcon: accounts.icon,
            creditCardName: creditCards.name,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
            subcategoryName: subcategories.name,
        })
        .from(recurringTransactions)
        .leftJoin(accounts, eq(recurringTransactions.accountId, accounts.id))
        .leftJoin(creditCards, eq(recurringTransactions.creditCardId, creditCards.id))
        .leftJoin(categories, eq(recurringTransactions.categoryId, categories.id))
        .leftJoin(subcategories, eq(recurringTransactions.subcategoryId, subcategories.id))
        .where(
            and(
                eq(recurringTransactions.type, 'expense'),
                eq(recurringTransactions.isActive, 1),
                isNull(recurringTransactions.deletedAt),
                gte(recurringTransactions.nextOccurrence, startDate),
                lte(recurringTransactions.nextOccurrence, endDate),
                // Gasto novo é leitura analítica: a série ignorada fica fora (ADR 0169).
                excludeIgnoredRecurringRules(),
            ),
        );

    const firstOccurrenceRules = candidateRules.filter(
        (rule) => firstOccurrence(rule) === rule.nextOccurrence,
    );
    const subcategoryNameByRule = new Map(
        firstOccurrenceRules.map((rule) => [rule.id, rule.subcategoryName ?? null]),
    );
    // Nestas regras firstOccurrence == nextOccurrence (é o filtro acima).
    const firstSlotOfProjectedRule = new Map(
        firstOccurrenceRules.map((rule) => [rule.id, rule.nextOccurrence]),
    );
    const projectedFirsts = buildProjectedRecurringTransactions(
        firstOccurrenceRules,
        startDate,
        endDate,
        materializedSlotKeys,
    )
        // A engine gera TODAS as ocorrências do período (semanal = 4+); gasto novo é só a 1ª.
        .filter((occurrence) => occurrence.date === firstSlotOfProjectedRule.get(occurrence.recurringId))
        .map((occurrence) => ({
            ...occurrence,
            recurringOccurrenceDate: occurrence.date,
            subcategoryName: subcategoryNameByRule.get(occurrence.recurringId) ?? null,
        }) as unknown as TransactionWithRelations);

    const groupedByExpense = new Map<string, TransactionWithRelations[]>();

    for (const row of [...scopedRows, ...projectedFirsts]) {
        const key = row.installmentId ?? row.id;
        const items = groupedByExpense.get(key) ?? [];
        items.push(row);
        groupedByExpense.set(key, items);
    }

    return Array.from(groupedByExpense.values())
        .map((items) => {
            if (items.length === 1) {
                return items[0];
            }

            const orderedItems = [...items].sort((left, right) => {
                const leftInstallment = left.installmentNumber ?? Number.MAX_SAFE_INTEGER;
                const rightInstallment = right.installmentNumber ?? Number.MAX_SAFE_INTEGER;

                return leftInstallment - rightInstallment
                    || compareIso(left.date, right.date)
                    || compareIso(left.createdAt, right.createdAt);
            });
            const representative = orderedItems[0];
            const groupedAmount = orderedItems.reduce((sum, item) => sum + item.amount, 0);

            return {
                ...representative,
                amount: groupedAmount,
            };
        })
        .sort((left, right) => compareIso(left.date, right.date) || compareIso(left.createdAt, right.createdAt));
}

export async function getAccrualExpenseTransactions(params: {
    month: string;
    categoryId?: string;
    categoryIds?: string[];
    installmentsFullValue?: boolean;
    invoicesByClosingMonth?: boolean;
    monthStartDay?: number;
}): Promise<TransactionWithRelations[]> {
    const { startDate, endDate } = getMonthDateRange(params.month, params.monthStartDay);
    const hasCategoryFilter = !!(params.categoryId || params.categoryIds?.length);
    let transactions = await getAccrualExpenseTransactionsForRange(startDate, endDate, {
        includeProjectedRecurring: true,
        groupInvoices: !hasCategoryFilter,
        useInvoiceAnchorDateForEntries: hasCategoryFilter,
        useInvoiceSettlementState: true,
        // ADR 0077: o colapso vale só no drill-down ANALÍTICO (pizza da Análise, que abre
        // esta tela com categoria) — lá ele precisa fechar com o gráfico que abriu. A lista
        // do mês inteiro, aberta pela Home, é leitura operacional e mostra a linha "Fatura X"
        // como fatura: colapsar ali fazia a linha divergir da própria fatura.
        installmentsFullValue: hasCategoryFilter && params.installmentsFullValue,
        invoicesByClosingMonth: params.invoicesByClosingMonth,
        monthStartDay: params.monthStartDay,
    });

    if (params.categoryIds?.length) {
        transactions = transactions.filter((transaction) =>
            matchesCategorySpendingFilter(transaction.categoryId, { categoryIds: params.categoryIds }),
        );
    } else if (params.categoryId) {
        transactions = transactions.filter((transaction) =>
            matchesCategorySpendingFilter(transaction.categoryId, { categoryId: params.categoryId }),
        );
    }

    return [...transactions].sort((left, right) =>
        compareIso(left.date, right.date) || compareIso(left.createdAt, right.createdAt),
    );
}

export async function getNewExpenseTransactions(params: {
    month: string;
    categoryId?: string;
    categoryIds?: string[];
    monthStartDay?: number;
}): Promise<TransactionWithRelations[]> {
    const { startDate, endDate } = getMonthDateRange(params.month, params.monthStartDay);
    const transactions = await getNewExpenseTransactionsForRange(startDate, endDate);

    return transactions.filter((transaction) =>
        matchesCategorySpendingFilter(transaction.categoryId, {
            categoryId: params.categoryId,
            categoryIds: params.categoryIds,
        }),
    );
}

export async function getTransactionById(id: string): Promise<TransactionWithRelations | undefined> {
    const projectedId = parseProjectedRecurringTransactionId(id);
    if (projectedId) {
        const projectedRows = await db
            .select({
                id: recurringTransactions.id,
                type: recurringTransactions.type,
                amount: recurringTransactions.amount,
                description: recurringTransactions.description,
                notes: recurringTransactions.notes,
                time: recurringTransactions.time,
                isIgnored: recurringTransactions.isIgnored,
                frequency: recurringTransactions.frequency,
                interval: recurringTransactions.interval,
                weekdays: recurringTransactions.weekdays,
                monthlyMode: recurringTransactions.monthlyMode,
                startDate: recurringTransactions.startDate,
                nextOccurrence: recurringTransactions.nextOccurrence,
                endDate: recurringTransactions.endDate,
                skippedDates: recurringTransactions.skippedDates,
                accountId: recurringTransactions.accountId,
                creditCardId: recurringTransactions.creditCardId,
                categoryId: recurringTransactions.categoryId,
                subcategoryId: recurringTransactions.subcategoryId,
                transferDestinationAccountId: recurringTransactions.transferDestinationAccountId,
                accountName: accounts.name,
                accountColor: accounts.color,
                accountIcon: accounts.icon,
                creditCardName: creditCards.name,
                categoryName: categories.name,
                categoryIcon: categories.icon,
                categoryColor: categories.color,
                subcategoryName: subcategories.name,
            })
            .from(recurringTransactions)
            .leftJoin(accounts, eq(recurringTransactions.accountId, accounts.id))
            .leftJoin(creditCards, eq(recurringTransactions.creditCardId, creditCards.id))
            .leftJoin(categories, eq(recurringTransactions.categoryId, categories.id))
            .leftJoin(subcategories, eq(recurringTransactions.subcategoryId, subcategories.id))
            .where(
                and(
                    eq(recurringTransactions.id, projectedId.recurringId),
                    eq(recurringTransactions.isActive, 1),
                    isNull(recurringTransactions.deletedAt),
                ),
            );

        if (!projectedRows[0]) {
            return undefined;
        }

        let destinationAccountName: string | null = null;
        let destinationAccountColor: string | null = null;

        if (projectedRows[0].transferDestinationAccountId) {
            const destinationRows = await db
                .select({ name: accounts.name, color: accounts.color })
                .from(accounts)
                .where(eq(accounts.id, projectedRows[0].transferDestinationAccountId));

            if (destinationRows[0]) {
                destinationAccountName = destinationRows[0].name;
                destinationAccountColor = destinationRows[0].color;
            }
        }

        return buildProjectedRecurringTransactions(
            [
                {
                    ...projectedRows[0],
                    destinationAccountName,
                    destinationAccountColor,
                },
            ],
            projectedId.date,
            projectedId.date,
            new Set(),
        )[0] as TransactionWithRelations | undefined;
    }

    const results = await db
        .select({
            id: transactions.id,
            type: transactions.type,
            amount: transactions.amount,
            description: transactions.description,
            date: transactions.date,
            time: transactions.time,
            notes: transactions.notes,
            accountId: transactions.accountId,
            creditCardId: sql<string | null>`COALESCE(${transactions.creditCardId}, ${invoices.creditCardId})`.as('creditCardId'),
            invoiceId: transactions.invoiceId,
            invoiceReferenceMonth: invoices.referenceMonth,
            transferDestinationAccountId: transactions.transferDestinationAccountId,
            transferId: transactions.transferId,
            categoryId: transactions.categoryId,
            subcategoryId: transactions.subcategoryId,
            isRecurring: transactions.isRecurring,
            recurringId: transactions.recurringId,
            recurringOccurrenceDate: transactions.recurringOccurrenceDate,
            isHiddenFromSubs: transactions.isHiddenFromSubs,
            isIgnored: transactions.isIgnored,
            installmentId: transactions.installmentId,
            installmentNumber: transactions.installmentNumber,
            installmentTotal: transactions.installmentTotal,
            financingId: transactions.financingId,
            financingEntryType: transactions.financingEntryType,
            financingInstallmentNumber: transactions.financingInstallmentNumber,
            personalLoanId: transactions.personalLoanId,
            personalLoanEntryType: transactions.personalLoanEntryType,
            adjustmentType: transactions.adjustmentType,
            isConfirmed: transactions.isConfirmed,
            createdAt: transactions.createdAt,
            updatedAt: transactions.updatedAt,
            deletedAt: transactions.deletedAt,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountIcon: accounts.icon,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
            subcategoryName: subcategories.name,
            creditCardName: creditCards.name,
            financingName: financings.name,
            financingStatus: financings.status,
            financingTotalInstallments: financings.totalInstallments,
        })
        .from(transactions)
        .leftJoin(accounts, eq(transactions.accountId, accounts.id))
        .leftJoin(categories, eq(transactions.categoryId, categories.id))
        .leftJoin(subcategories, eq(transactions.subcategoryId, subcategories.id))
        .leftJoin(invoices, eq(transactions.invoiceId, invoices.id))
        .leftJoin(financings, eq(transactions.financingId, financings.id))
        .leftJoin(creditCards, eq(sql`COALESCE(${transactions.creditCardId}, ${invoices.creditCardId})`, creditCards.id))
        .where(and(eq(transactions.id, id), isNull(transactions.deletedAt)));

    if (!results[0]) return undefined;

    const row = results[0];
    let destinationAccountName: string | null = null;
    let destinationAccountColor: string | null = null;

    if (row.transferDestinationAccountId) {
        const dest = await db
            .select({ name: accounts.name, color: accounts.color })
            .from(accounts)
            .where(eq(accounts.id, row.transferDestinationAccountId));
        if (dest[0]) {
            destinationAccountName = dest[0].name;
            destinationAccountColor = dest[0].color;
        }
    }

    return { ...row, destinationAccountName, destinationAccountColor };
}

export async function createTransaction(data: {
    type: string;
    amount: number;
    description: string;
    date: string;
    time?: string;
    notes?: string;
    accountId?: string;
    creditCardId?: string;
    invoiceMonth?: string;
    categoryId?: string;
    subcategoryId?: string;
    transferDestinationAccountId?: string;
    isConfirmed?: boolean;
    /** Lançamento ignorado (ADR 0037): fora da análise, mas conta no saldo e na fatura. */
    isIgnored?: boolean;
    isRecurring?: boolean;
    recurringId?: string;
    /** Data agendada da ocorrência. Default = `date` quando há recurringId (a ocorrência é criada no slot e só depois pode ser movida). */
    recurringOccurrenceDate?: string;
    installmentId?: string;
    installmentNumber?: number;
    installmentTotal?: number;
    financingId?: string;
    financingEntryType?: FinancingEntryTypeValue | null;
    financingInstallmentNumber?: number | null;
    /**
     * O lançamento veio de um ARQUIVO (fatura/extrato importado), não de uma ação de
     * agora. Sem `time` no arquivo, nasce SEM hora em vez de herdar o "agora" do
     * import — que numa compra de hoje diria a hora em que o PDF foi aberto.
     */
    isImported?: boolean;
}): Promise<Transaction> {
    const id = generateId();
    const now = new Date().toISOString();
    const refs = await sanitizeTransactionReferenceIds(data);

    // If credit card transaction, determine invoice
    let invoiceId: string | null = null;
    if (refs.creditCardId) {
        const card = await db.select().from(creditCards).where(eq(creditCards.id, refs.creditCardId));
        if (card[0]) {
            const invoiceMonth = data.invoiceMonth
                ?? getInvoiceMonthForDate(data.date, card[0].closingDay, card[0].closingDayToNextInvoice === 1);
            const invoice = await getOrCreateInvoice(refs.creditCardId, invoiceMonth);
            invoiceId = invoice.id;
        }
    }

    await db.insert(transactions).values({
        id,
        type: data.type,
        amount: data.amount,
        description: data.description,
        date: data.date,
        // Sem hora escolhida, lançamento de HOJE carimba a hora atual (ver
        // resolveTransactionTime). Fica AQUI, e não em cada tela, porque todo caminho
        // de criação avulsa passa por esta porta — formulário, primeiro gasto do
        // onboarding, conferir saldo da conta, transferência.
        // Só quando é dinheiro se movendo: PENDENTE nascendo (previsto de recorrência
        // materializado, conta a pagar) é registro, não pagamento — carimbar gravaria a
        // hora de abrir o app/digitar, e a hora falsa bloquearia o carimbo verdadeiro da
        // virada pendente→pago. Import idem (a hora seria a de abrir o arquivo).
        time: resolveTransactionTime(data.date, data.time, {
            stampNow: data.isConfirmed !== false && !data.isImported,
        }),
        notes: data.notes || null,
        accountId: refs.accountId,
        creditCardId: refs.creditCardId,
        invoiceId,
        categoryId: refs.categoryId,
        subcategoryId: refs.subcategoryId,
        transferDestinationAccountId: refs.transferDestinationAccountId,
        transferId: data.type === 'transfer' ? id : null,
        isRecurring: data.isRecurring ? 1 : 0,
        recurringId: normalizeOptionalId(data.recurringId) ?? null,
        recurringOccurrenceDate: normalizeOptionalId(data.recurringId)
            ? (data.recurringOccurrenceDate ?? data.date)
            : null,
        installmentId: normalizeOptionalId(data.installmentId) ?? null,
        installmentNumber: data.installmentNumber ?? null,
        installmentTotal: data.installmentTotal ?? null,
        financingId: normalizeOptionalId(data.financingId) ?? null,
        financingEntryType: data.financingEntryType ?? null,
        financingInstallmentNumber: data.financingInstallmentNumber ?? null,
        isHiddenFromSubs: 0,
        isIgnored: data.isIgnored ? 1 : 0,
        isConfirmed: data.isConfirmed !== false ? 1 : 0,
        createdAt: now,
        updatedAt: now,
    });

    // Recalculate balances
    if (refs.accountId) {
        await recalculateAccountBalance(refs.accountId);
    }
    if (data.type === 'transfer' && refs.transferDestinationAccountId) {
        await recalculateAccountBalance(refs.transferDestinationAccountId);
    }

    // Recalculate invoice total if credit card transaction
    if (invoiceId) {
        await recalculateInvoiceTotal(invoiceId);
    }

    const result = await db.select().from(transactions).where(eq(transactions.id, id));
    return result[0];
}

type TransactionUpdateData = Partial<{
    type: string;
    amount: number;
    description: string;
    date: string;
    time: string;
    notes: string;
    accountId: string;
    creditCardId: string;
    invoiceMonth: string;
    categoryId: string;
    subcategoryId: string;
    transferDestinationAccountId: string;
    isConfirmed: boolean;
    isIgnored: boolean;
    isRecurring: boolean;
    recurringId: string | null;
    installmentId: string | null;
    installmentNumber: number | null;
    installmentTotal: number | null;
}>;

/**
 * O usuário VIROU o Ignorar na ocorrência que abriu? É o único sinal que leva o flag pra
 * série (regra e ocorrências do escopo) — ver ADR 0169. `undefined` na âncora vale 0.
 */
function seriesIgnoredChanged(data: TransactionUpdateData, targetTx: Pick<Transaction, 'isIgnored'>): boolean {
    return data.isIgnored !== undefined && (data.isIgnored ? 1 : 0) !== (targetTx.isIgnored === 1 ? 1 : 0);
}

async function updateMaterializedRecurringSeries(
    targetTx: Transaction,
    data: TransactionUpdateData,
    mode: RecurringSeriesRebaseMode,
    // Cadência da regra (frequency/interval/…): com ela o plano de datas usa o motor de
    // recorrência em vez de assumir mensal. Sem ela (série órfã), inferimos das datas.
    cadence?: RecurringSeriesCadence | null,
): Promise<void> {
    const recurrenceKey = targetTx.installmentId || targetTx.recurringId;
    if (!recurrenceKey) return;

    const series = await db
        .select()
        .from(transactions)
        .where(
            and(
                targetTx.installmentId
                    ? eq(transactions.installmentId, recurrenceKey)
                    : eq(transactions.recurringId, recurrenceKey),
                isNull(transactions.deletedAt),
            )!,
        )
        .orderBy(asc(transactions.date), asc(transactions.createdAt));

    if (series.length === 0) return;

    const anchorIndex = Math.max(
        series.findIndex((transaction) => transaction.id === targetTx.id),
        0,
    );
    const effectiveCreditCardId =
        data.creditCardId !== undefined ? (data.creditCardId || null) : targetTx.creditCardId;
    // Só reatribui fatura quando algo que REALMENTE muda a fatura mudou: o cartão, a data, ou
    // um invoiceMonth explícito. Gatear só na PRESENÇA de creditCardId reatribuía a fatura de
    // toda ocorrência numa edição de nome/categoria (o form sempre reenvia creditCardId),
    // recomputando o mês pela data de cada linha e "puxando de volta" ocorrências movidas à mão
    // — inclusive em faturas já pagas. Ver troubleshooting (achado: editar nome em "Todas").
    const cardChanged =
        data.creditCardId !== undefined && (data.creditCardId || null) !== targetTx.creditCardId;
    const dateChanged = data.date !== undefined && data.date !== targetTx.date;
    const shouldReassignInvoices = cardChanged || dateChanged || data.invoiceMonth !== undefined;
    // Mesma armadilha do creditCardId: o formulário SEMPRE reenvia `isConfirmed` com o estado
    // da âncora. Copiar esse estado sem ele ter mudado marcava como PAGA toda ocorrência
    // seguinte ainda pendente numa edição só de valor em "esta e futuras"/"todas" — o aluguel
    // remarcado do mês+2 passava a descontar do saldo previsto (e2e, 14/set/2026). Só a
    // virada que o usuário fez na âncora vale pra série.
    const confirmationChanged =
        data.isConfirmed !== undefined && (data.isConfirmed ? 1 : 0) !== targetTx.isConfirmed;
    // Mesma armadilha com o Ignorado (ADR 0169): o form reenvia o estado da âncora. Copiá-lo
    // sem ele ter mudado reincluiria, numa edição só de valor, a ocorrência que o usuário
    // ignorou sozinha (Exceção) — ou ignoraria a série inteira a partir de uma âncora que é a
    // Exceção. Só a virada feita na âncora vale pro escopo.
    const ignoredChanged = seriesIgnoredChanged(data, targetTx);

    // Fechamento por cartão: a série pode ter uma ocorrência em OUTRA origem (exceção paga
    // no crédito, ADR 0137), e a fatura dela sai do cartão dela.
    const cardClosings = new Map<string, { closingDay: number; toNext: boolean } | null>();
    const getCardClosing = async (cardId: string) => {
        if (!cardClosings.has(cardId)) {
            const [card] = await db.select().from(creditCards).where(eq(creditCards.id, cardId));
            cardClosings.set(cardId, card
                ? { closingDay: card.closingDay, toNext: card.closingDayToNextInvoice === 1 }
                : null);
        }
        return cardClosings.get(cardId) ?? null;
    };

    let effectiveInvoiceMonth: string | undefined;
    if (effectiveCreditCardId && shouldReassignInvoices) {
        const closing = await getCardClosing(effectiveCreditCardId);
        if (closing) {
            effectiveInvoiceMonth = data.invoiceMonth
                ?? getInvoiceMonthForDate(data.date ?? targetTx.date, closing.closingDay, closing.toNext);
        }
    }

    // Fix #1 (auditoria jul/2026): o plano respeita a FREQUÊNCIA da série. Sem cadência
    // explícita (série órfã, sem regra viva), infere das próprias datas — uma série semanal
    // editada em "Todas" era reescrita com espaçamento mensal (histórico espalhado por meses).
    const effectiveCadence = cadence
        ?? inferFrequencyFromDates(series.map((tx) => tx.recurringOccurrenceDate ?? tx.date));

    const rebasePlan = data.date !== undefined || data.invoiceMonth !== undefined
        ? buildRecurringSeriesRebasePlan({
            occurrenceCount: series.length,
            anchorIndex,
            mode,
            baseDate: data.date ?? targetTx.date,
            baseInvoiceMonth: effectiveInvoiceMonth,
            cadence: effectiveCadence as RecurringSeriesCadence | null,
        })
        : [];
    const rebasePlanMap = new Map(rebasePlan.map((entry) => [entry.occurrenceIndex, entry]));

    const affectedAccounts = new Set<string>();
    const affectedInvoices = new Set<string>();

    for (const [occurrenceIndex, tx] of series.entries()) {
        const shouldUpdate = mode === 'all' || occurrenceIndex >= anchorIndex;
        if (!shouldUpdate) continue;

        if (tx.accountId) affectedAccounts.add(tx.accountId);
        if (tx.transferDestinationAccountId) affectedAccounts.add(tx.transferDestinationAccountId);
        if (tx.invoiceId) affectedInvoices.add(tx.invoiceId);

        const plannedEntry = rebasePlanMap.get(occurrenceIndex);
        const nextDate = data.date !== undefined ? (plannedEntry?.date ?? tx.date) : tx.date;
        const updateData: Record<string, unknown> = {
            updatedAt: new Date().toISOString(),
        };
        // "Esta e futuras" não reescreve dinheiro que já se moveu: numa ocorrência SEGUINTE já
        // paga, valor e conta ficam como foram pagos (decisão de produto, 14/set/2026). A
        // âncora (a que o usuário edita) e as pendentes seguem a mudança; "Todas" continua
        // reaplicando na série inteira, que é o que ela promete.
        const preserveSettledMoney = mode === 'future' && tx.id !== targetTx.id && tx.isConfirmed === 1;

        if (data.type !== undefined) updateData.type = data.type;
        if (data.amount !== undefined && !preserveSettledMoney) updateData.amount = data.amount;
        if (data.description !== undefined) updateData.description = data.description;
        if (data.date !== undefined) updateData.date = nextDate;
        // Fix #5: o SLOT acompanha o rebase. Sem isto a linha ficava com o
        // recurringOccurrenceDate antigo enquanto a régua da regra deslizava — o dedup
        // por slot furava (a ocorrência real E a projetada contavam juntas no mês) e o
        // generateOccurrences re-materializava o slot novo em DOBRO. Entrada SEM `slot`
        // (padrão avançado: a regra não re-ancora) mantém o slot atual de propósito.
        if (data.date !== undefined && plannedEntry?.slot && tx.recurringOccurrenceDate) {
            updateData.recurringOccurrenceDate = plannedEntry.slot;
        }
        if (data.time !== undefined) updateData.time = data.time || null;
        if (data.notes !== undefined) updateData.notes = data.notes || null;
        if (data.accountId !== undefined && !preserveSettledMoney) updateData.accountId = data.accountId || null;
        if (data.categoryId !== undefined) updateData.categoryId = data.categoryId || null;
        if (data.subcategoryId !== undefined) updateData.subcategoryId = data.subcategoryId || null;
        if (data.transferDestinationAccountId !== undefined && !preserveSettledMoney) {
            updateData.transferDestinationAccountId = data.transferDestinationAccountId || null;
        }
        if (confirmationChanged) updateData.isConfirmed = data.isConfirmed ? 1 : 0;
        // Ignorar não é dinheiro (não mexe em saldo nem fatura): vale também na ocorrência
        // seguinte já paga, em "esta e futuras" e em "todas".
        if (ignoredChanged) updateData.isIgnored = data.isIgnored ? 1 : 0;
        // EFETIVAR carimba a hora — mesma regra da porta 'single' (updateSingleTransaction),
        // que este caminho de série não tinha: confirmar escolhendo "esta e futuras"/"todas"
        // gravava o pagamento sem hora. Só na virada pendente→pago de cada linha, só sem
        // hora existente/escolhida; resolveTransactionTime devolve null fora de hoje, então
        // as linhas futuras da série seguem sem hora. Depois do `updateData.time` acima de
        // propósito: o '' do formulário vira null lá e o carimbo sobrescreve.
        if (confirmationChanged && data.isConfirmed === true && tx.isConfirmed === 0 && !tx.time && !data.time) {
            const settledTime = resolveTransactionTime((updateData.date as string | undefined) ?? tx.date);
            if (settledTime) updateData.time = settledTime;
        }
        if (data.isRecurring !== undefined) updateData.isRecurring = data.isRecurring ? 1 : 0;
        if (data.recurringId !== undefined) updateData.recurringId = data.recurringId;

        // Ocorrência SEGUINTE já concluída também não muda de ORIGEM em "esta e futuras": o
        // dinheiro dela já está na fatura (ou já saiu da conta). Sem isto, tirar uma série de
        // cartão pro modo conta soltava cartão e fatura da concluída e, como conta/valor ficam
        // como foram pagos, a linha ficava sem origem nenhuma — e o caminho inverso movia pra
        // fatura dinheiro que já tinha saído da conta.
        if (shouldReassignInvoices && !preserveSettledMoney) {
            // A ORIGEM só anda quando o usuário a mudou nesta edição. Mudança só de data
            // refaz a fatura de quem já está num cartão — no cartão DELA — e não puxa a
            // exceção paga no crédito de volta pra conta nem o resto da série pro cartão
            // da âncora (ADR 0137).
            const rowCardId = cardChanged ? effectiveCreditCardId : (tx.creditCardId ?? null);
            const closing = rowCardId ? await getCardClosing(rowCardId) : null;
            if (rowCardId && closing) {
                // O mês planejado/escolhido é do cartão da ÂNCORA; em outro cartão vale a data.
                const followsAnchorCard = rowCardId === effectiveCreditCardId;
                const nextInvoiceMonth = (followsAnchorCard ? (plannedEntry?.invoiceMonth ?? data.invoiceMonth) : undefined)
                    ?? getInvoiceMonthForDate(nextDate, closing.closingDay, closing.toNext);
                const invoice = await getOrCreateInvoice(rowCardId, nextInvoiceMonth);
                updateData.creditCardId = rowCardId;
                updateData.invoiceId = invoice.id;
                // Conta e Cartão são excludentes (mesma regra de updateSingleTransaction):
                // ocorrência entrando no cartão solta a conta, senão entra na fatura E
                // segue descontando do saldo.
                updateData.accountId = null;
                affectedInvoices.add(invoice.id);
            } else if (cardChanged) {
                updateData.creditCardId = null;
                updateData.invoiceId = null;
            }
        } else if (tx.creditCardId && !tx.adjustmentType) {
            // Ocorrência que JÁ está no cartão não ganha conta numa edição de outro campo
            // (e uma linha antiga gravada com os dois se cura aqui).
            updateData.accountId = null;
        }

        await db.update(transactions).set(updateData).where(eq(transactions.id, tx.id));

        if (data.accountId) affectedAccounts.add(data.accountId);
        if (data.transferDestinationAccountId) affectedAccounts.add(data.transferDestinationAccountId);
    }

    for (const accountId of affectedAccounts) await recalculateAccountBalance(accountId);
    for (const invoiceId of affectedInvoices) await recalculateInvoiceTotal(invoiceId);
}

/**
 * Encerra a regra ANTES do slot `anchorSlot` — usado quando "esta e futuras"/"esta e as
 * próximas" tira da regra tudo a partir da âncora. Se ainda há ocorrência PROJETADA antes
 * da âncora (série criada em setembro, âncora em janeiro: out–dez ainda não são linha real),
 * a regra sobrevive com endDate no slot anterior e continua projetando esse trecho com o
 * valor de sempre. Se tudo antes da âncora já é linha real (ou a âncora é a 1ª ocorrência),
 * vira tombstone — como sempre foi.
 *
 * Sem isto, o tombstone apagava out–dez da projeção (exclusão), e no split a regra nova —
 * clone de fase da antiga com o valor NOVO — passava a projetar out–dez com o valor novo
 * (edição). Caso do Denis, 15/set/2026; teste em recurringFutureEditProjectedTail.test.ts.
 */
async function endRecurringRuleBeforeSlot(
    rule: RecurrenceRuleFields & { id: string; nextOccurrence: string },
    anchorSlot: string,
    now: string,
): Promise<void> {
    const anchorIndex = occurrenceIndexOf(anchorSlot, rule);
    let previousSlot: string | null = null;
    if (anchorIndex !== null) {
        previousSlot = anchorIndex > 0 ? occurrenceDateAt(anchorIndex - 1, rule) : null;
    } else {
        // Âncora fora da régua (linha antiga sem slot, data editada): o slot anterior da
        // régua, se ainda estiver dentro da série.
        const previous = previousOccurrence(anchorSlot, rule);
        previousSlot = previous < anchorSlot && previous >= rule.startDate ? previous : null;
    }
    const hasProjectedBeforeAnchor = !!previousSlot && rule.nextOccurrence <= previousSlot;

    if (!hasProjectedBeforeAnchor) {
        await db
            .update(recurringTransactions)
            .set({ isActive: 0, deletedAt: now, updatedAt: now })
            .where(eq(recurringTransactions.id, rule.id));
        return;
    }

    await db
        .update(recurringTransactions)
        .set({
            endDate: previousSlot,
            // Invariante do count: truncar a cauda zera o count (degrada para endDate
            // explícito). Ver docs/features/RECURRENCE.md.
            count: null,
            isActive: 1,
            updatedAt: now,
        })
        .where(eq(recurringTransactions.id, rule.id));
}

async function updateRecurringRuleSeries(
    targetTx: Transaction,
    data: TransactionUpdateData,
    mode: RecurringSeriesRebaseMode,
): Promise<void> {
    if (!targetTx.recurringId) {
        await updateMaterializedRecurringSeries(targetTx, data, mode);
        return;
    }

    const [rule] = await db
        .select()
        .from(recurringTransactions)
        .where(and(eq(recurringTransactions.id, targetTx.recurringId), isNull(recurringTransactions.deletedAt)));

    if (!rule) {
        await updateMaterializedRecurringSeries(targetTx, data, mode);
        return;
    }

    // Fix #4: a âncora é o SLOT da ocorrência (recurringOccurrenceDate), não a data real —
    // a data real é editável (confirmação antecipada, edição "só esta") e, desalinhada da
    // régua, devolvia null; o antigo fallback `?? 0` re-ancorava a série inteira no
    // startDate e as cobranças entre a âncora verdadeira e o fim SUMIAM da projeção.
    // Sem âncora determinável, não rebaseia posições (cai nos fallbacks não-posicionais).
    const anchorSlot = targetTx.recurringOccurrenceDate ?? targetTx.date;
    const anchorIndex = occurrenceIndexOf(anchorSlot, rule);
    const nextOccurrenceIndex = occurrenceIndexOf(rule.nextOccurrence, rule);
    const endOccurrenceIndex = rule.endDate
        ? occurrenceIndexOf(rule.endDate, rule)
        : null;
    const nextDate = data.date ?? targetTx.date;
    // Só re-basear a série quando a data REALMENTE mudou nesta edição. O formulário
    // reenvia `date` em toda submissão; tratar "reenviou" como "mudou" deslocava a série
    // inteira num simples rename. Comparamos contra a data da ocorrência editada.
    const dateChanged = data.date !== undefined && data.date !== targetTx.date;
    // Regras avançadas (multi-dia/nth-weekday) não "deslocam" por mudança de data:
    // não dá pra re-basear um padrão ter+qui por um offset arbitrário.
    const isAdvanced = !!rule.weekdays || rule.monthlyMode === 'nth_weekday';
    // A origem só vai pra REGRA quando mudou NESTA edição. O form reenvia sempre o cartão
    // da ocorrência aberta, e ela pode ser uma exceção de origem — o corte de cabelo da
    // conta que, num mês, foi pago no crédito (ADR 0137). Renomear essa ocorrência em
    // "todas" levava a regra inteira pro cartão.
    const originCardChanged =
        data.creditCardId !== undefined && (data.creditCardId || null) !== targetTx.creditCardId;
    const ruleCreditCardId = originCardChanged ? (data.creditCardId || null) : rule.creditCardId;
    const ruleAccountId = !originCardChanged && targetTx.creditCardId
        // Âncora no cartão sem troca de origem: o form nem manda conta; a da regra fica.
        ? rule.accountId
        : (data.accountId !== undefined ? (data.accountId || null) : rule.accountId);
    const rulePatch = {
        type: data.type ?? rule.type,
        amount: data.amount ?? rule.amount,
        description: data.description ?? rule.description,
        notes: data.notes !== undefined ? (data.notes || null) : rule.notes,
        time: data.time !== undefined ? (data.time || null) : rule.time,
        // Conta e Cartão são excludentes também na regra (ver updateSingleTransaction): regra
        // com os dois materializava ocorrências que entravam na fatura E descontavam da conta.
        // O form manda `accountId: undefined` em modo cartão.
        accountId: ruleCreditCardId ? null : ruleAccountId,
        creditCardId: ruleCreditCardId,
        categoryId: data.categoryId !== undefined ? (data.categoryId || null) : rule.categoryId,
        subcategoryId: data.subcategoryId !== undefined ? (data.subcategoryId || null) : rule.subcategoryId,
        transferDestinationAccountId: data.transferDestinationAccountId !== undefined
            ? (data.transferDestinationAccountId || null)
            : rule.transferDestinationAccountId,
        // Ignorado da SÉRIE (ADR 0169): só muda quando o usuário o virou na âncora — a âncora
        // pode ser uma Exceção (ignorada sozinha), e reenviar o estado dela não é pedido pra
        // ignorar a série. Mesmo gate das ocorrências (seriesIgnoredChanged).
        isIgnored: seriesIgnoredChanged(data, targetTx) ? (data.isIgnored ? 1 : 0) : rule.isIgnored,
    };
    const now = new Date().toISOString();

    if (mode === 'all') {
        const canRebase = dateChanged && !isAdvanced && anchorIndex !== null;
        const newStartDate = canRebase && anchorIndex !== null
            ? rewindFrom(nextDate, anchorIndex, rule)
            : rule.startDate;
        const rebasedRule = { ...rule, startDate: newStartDate };
        const newNextOccurrence = canRebase && nextOccurrenceIndex !== null
            ? occurrenceDateAt(nextOccurrenceIndex, rebasedRule)
            : rule.nextOccurrence;
        const newEndDate = canRebase && endOccurrenceIndex !== null
            ? occurrenceDateAt(endOccurrenceIndex, rebasedRule)
            : rule.endDate;
        // As datas puladas são slots absolutos: um rebase de data desalinharia todas
        // (o pulo sumiria da projeção). O pulo segue a posição na série.
        const rebasedSkipped = canRebase
            ? rebaseSkippedDates(rule, parseSkippedDates(rule.skippedDates), rebasedRule)
            : null;
        const newSkippedDates = rebasedSkipped
            ? (rebasedSkipped.length > 0 ? JSON.stringify(rebasedSkipped) : null)
            : rule.skippedDates;

        // Numa edição que não mexeu na data, não propagar `date`/`invoiceMonth` às
        // ocorrências materializadas — senão o rebase de série recalcularia (e poderia
        // deslocar) as datas reais sem o usuário ter pedido.
        const seriesData: TransactionUpdateData = { ...data, recurringId: rule.id, isRecurring: true };
        if (!dateChanged) {
            delete seriesData.date;
            delete seriesData.invoiceMonth;
        }

        // Atômico: linhas materializadas + regra vão juntas — crash no meio deixava as
        // ocorrências reescritas com a regra antiga ainda valendo (série incoerente).
        await runInTransaction(async () => {
            await updateMaterializedRecurringSeries(targetTx, seriesData, 'all', rule);

            await db
                .update(recurringTransactions)
                .set({
                    ...rulePatch,
                    startDate: newStartDate,
                    nextOccurrence: newNextOccurrence,
                    endDate: newEndDate,
                    skippedDates: newSkippedDates,
                    updatedAt: now,
                })
                .where(eq(recurringTransactions.id, rule.id));
        });
        return;
    }

    const newRecurringId = generateId();

    // Fix #2: sem mudança de DATA, o split "esta e futuras" é IDENTITÁRIO — a regra nova é um
    // clone de fase da antiga (mesmo startDate/âncora, mesmos pulos absolutos, mesmo ponteiro
    // e fim). Ancorar a nova regra na data da ocorrência editada mudava a âncora da série numa
    // edição de VALOR: o form sempre reenvia `date`, então uma série do dia 31 editada na
    // ocorrência de 28/fev passava a cobrar dia 28 PARA SEMPRE. Só quando a data mudou de
    // verdade é que a série re-ancora na data nova (intenção explícita do usuário).
    if (!dateChanged) {
        const seriesData: TransactionUpdateData = { ...data, recurringId: newRecurringId, isRecurring: true };
        delete seriesData.date;
        delete seriesData.invoiceMonth;

        await runInTransaction(async () => {
            await updateMaterializedRecurringSeries(targetTx, seriesData, 'future', rule);
            // A antiga fica dona do que ainda projeta ANTES da âncora (ver o helper).
            await endRecurringRuleBeforeSlot(rule, anchorSlot, now);
            await db.insert(recurringTransactions).values({
                id: newRecurringId,
                type: rulePatch.type,
                amount: rulePatch.amount,
                description: rulePatch.description,
                notes: rulePatch.notes,
                time: rulePatch.time,
                isIgnored: rulePatch.isIgnored,
                advanceReminderEnabled: rule.advanceReminderEnabled,
                accountId: rulePatch.accountId,
                creditCardId: rulePatch.creditCardId,
                categoryId: rulePatch.categoryId,
                subcategoryId: rulePatch.subcategoryId,
                transferDestinationAccountId: rulePatch.transferDestinationAccountId,
                frequency: rule.frequency,
                interval: rule.interval,
                weekdays: rule.weekdays,
                monthlyMode: rule.monthlyMode,
                // Fase preservada → o count continua contando do MESMO início; herdar é correto.
                count: rule.count,
                startDate: rule.startDate,
                endDate: rule.endDate,
                skippedDates: rule.skippedDates,
                // Clone de fase, mas a projeção começa NA âncora: com o ponteiro herdado
                // (out) a regra nova projetava out–dez com o valor novo por cima do trecho
                // que ainda é da antiga.
                nextOccurrence: rule.nextOccurrence > anchorSlot ? rule.nextOccurrence : anchorSlot,
                isActive: rule.isActive,
                isHiddenFromSubs: rule.isHiddenFromSubs,
                createdAt: now,
                updatedAt: now,
            });
            await copyRecurringTags(rule.id, newRecurringId);
        });
        return;
    }

    const newRuleFields = { ...rule, startDate: nextDate };
    // O pulo (Exceção por exclusão) é propriedade do SLOT, não da data absoluta: remapeia
    // pela posição na série, como nextOccurrence/endDate abaixo. Sem isto a regra nova
    // nascia com skippedDates vazio e a ocorrência pulada reaparecia na cauda.
    const newSkippedDates = rebaseSkippedDates(
        rule,
        parseSkippedDates(rule.skippedDates),
        newRuleFields,
        anchorIndex ?? 0,
    );
    const rawNextOccurrence = nextOccurrenceIndex !== null && anchorIndex !== null && nextOccurrenceIndex >= anchorIndex
        ? occurrenceDateAt(nextOccurrenceIndex - anchorIndex, newRuleFields)
        : nextOccurrence(nextDate, newRuleFields);
    const newEndDate = endOccurrenceIndex !== null && anchorIndex !== null && endOccurrenceIndex >= anchorIndex
        ? occurrenceDateAt(endOccurrenceIndex - anchorIndex, newRuleFields)
        : null;
    // O fallback (ponteiro antigo atrás da âncora) dá um passo cego e pode cair num
    // slot pulado; no caminho posicional o remap preserva o ponteiro fora dos pulos.
    const newNextOccurrence = advancePastSkipped(
        { ...newRuleFields, endDate: newEndDate },
        rawNextOccurrence,
        new Set(newSkippedDates),
    );

    // Atômico: re-apontar a cauda + tombstonar a regra antiga + criar a nova vão juntos —
    // crash no meio deixava a cauda apontando pra uma regra fantasma (ou a série sem regra
    // viva nenhuma), sem caminho de recuperação pela UI.
    await runInTransaction(async () => {
        await updateMaterializedRecurringSeries(
            targetTx,
            { ...data, recurringId: newRecurringId, isRecurring: true },
            'future',
            rule,
        );

        // A antiga fica dona do que ainda projeta ANTES da âncora (slot original).
        await endRecurringRuleBeforeSlot(rule, anchorSlot, now);

        await db.insert(recurringTransactions).values({
            id: newRecurringId,
            type: rulePatch.type,
            amount: rulePatch.amount,
            description: rulePatch.description,
            notes: rulePatch.notes,
            time: rulePatch.time,
            isIgnored: rulePatch.isIgnored,
            // Sem isto, quebrar a série ("esta e futuras") silenciosamente DESLIGAVA o Aviso
            // antecipado da cauda — a regra nova nascia com o default (0) em vez de herdar.
            advanceReminderEnabled: rule.advanceReminderEnabled,
            accountId: rulePatch.accountId,
            creditCardId: rulePatch.creditCardId,
            categoryId: rulePatch.categoryId,
            subcategoryId: rulePatch.subcategoryId,
            transferDestinationAccountId: rulePatch.transferDestinationAccountId,
            frequency: rule.frequency,
            interval: rule.interval,
            weekdays: rule.weekdays,
            monthlyMode: rule.monthlyMode,
            // Invariante do count: o split re-ancorado quebra a fase da série → a nova regra
            // degrada para endDate explícito (count = null). Ver docs/features/RECURRENCE.md.
            count: null,
            startDate: nextDate,
            endDate: newEndDate,
            skippedDates: newSkippedDates.length > 0 ? JSON.stringify(newSkippedDates) : null,
            nextOccurrence: newNextOccurrence,
            isActive: newEndDate && newNextOccurrence > newEndDate ? 0 : 1,
            isHiddenFromSubs: rule.isHiddenFromSubs,
            createdAt: now,
            updatedAt: now,
        });
        await copyRecurringTags(rule.id, newRecurringId);
    });
}

/**
 * Herda as tags de SÉRIE da regra antiga pra nova no split "esta e futuras". Sem isto, um
 * rename simples de série taggeada fazia a cauda perder as tags (ocorrências futuras nasciam
 * sem tag) — o form só re-aplica tags quando o usuário as alterou. INSERT OR IGNORE respeita
 * o índice único (regra, tag).
 */
async function copyRecurringTags(fromRecurringId: string, toRecurringId: string): Promise<void> {
    await execute(
        `INSERT OR IGNORE INTO recurring_transaction_tags (recurring_id, tag_id)
         SELECT ?, tag_id FROM recurring_transaction_tags WHERE recurring_id = ?`,
        [toRecurringId, fromRecurringId],
    );
}

/**
 * Edita uma série recorrente a partir da REGRA (não de uma transação real), para uma
 * ocorrência ainda projetada. Evita materializar a ocorrência só para anexar uma edição
 * de "Todas" — materializar criaria uma cobrança adiantada e (antes do Fix #1) avançava a
 * agenda. Constrói um alvo sintético na data do slot e reusa o motor de rebase de série.
 * Só dá pra usar com `mode: 'all'`: 'future'/'single' precisam de uma linha-âncora real.
 */
export async function updateRecurringSeriesByRule(
    recurringId: string,
    occurrenceDate: string,
    data: TransactionUpdateData,
): Promise<void> {
    const [rule] = await db
        .select()
        .from(recurringTransactions)
        .where(and(eq(recurringTransactions.id, recurringId), isNull(recurringTransactions.deletedAt)));
    if (!rule) return;

    const syntheticTarget = {
        id: '',
        recurringId,
        date: occurrenceDate,
        recurringOccurrenceDate: occurrenceDate,
        isRecurring: 1,
        // A projetada herda o Ignorado da regra: é contra ele que se mede se o usuário o virou.
        isIgnored: rule.isIgnored,
    } as unknown as Transaction;

    await updateRecurringRuleSeries(syntheticTarget, data, 'all');
}

/**
 * Quantas ocorrências REAIS a série tem e quantas já estão concluídas — é o que a
 * confirmação do escopo "Todas" mostra antes de reescrever a série inteira (a única
 * operação que mexe em ocorrência já paga; "esta e futuras" preserva). Projetadas não
 * contam: são infinitas numa série sem fim e o valor delas ainda não é "histórico".
 */
export async function getSeriesOccurrenceStats(key: {
    recurringId?: string | null;
    installmentId?: string | null;
}): Promise<{ total: number; settled: number }> {
    const condition = key.installmentId
        ? eq(transactions.installmentId, key.installmentId)
        : key.recurringId
            ? eq(transactions.recurringId, key.recurringId)
            : null;
    if (!condition) return { total: 0, settled: 0 };
    const [row] = await db
        .select({
            total: sql<number>`count(*)`,
            settled: sql<number>`coalesce(sum(${transactions.isConfirmed}), 0)`,
        })
        .from(transactions)
        .where(and(condition, isNull(transactions.deletedAt)));
    return { total: Number(row?.total ?? 0), settled: Number(row?.settled ?? 0) };
}

export async function updateTransaction(
    id: string,
    data: TransactionUpdateData,
    mode: EditScope = 'single',
): Promise<void> {
    const old = await db.select().from(transactions).where(eq(transactions.id, id));
    if (!old[0]) return;

    const targetTx = old[0];
    if (targetTx.installmentId) {
        await updateInstallmentSeries(targetTx, data, mode);
        return;
    }

    const recurrenceKey = targetTx.installmentId || targetTx.recurringId;
    const isRecurringItem = (targetTx.isRecurring === 1 || !!targetTx.installmentId) && !!recurrenceKey;

    // For single mode or non-recurring, just update this one
    if (mode === 'single' || !isRecurringItem) {
        await updateSingleTransaction(id, data);
        return;
    }

    if (targetTx.recurringId) {
        await updateRecurringRuleSeries(targetTx, data, mode);
        return;
    }

    await updateMaterializedRecurringSeries(targetTx, data, mode);
}

/**
 * Receita de rendimento de uma caixinha ATIVA (ADR 0161). Ela e o movimento de rendimento se
 * desfazem juntos, pela caixinha: apagar ou mexer no dinheiro dela solto deixava o reservado
 * descasado do saldo (e-mail do Denis, 27/09/2026). Caixinha excluída ou arquivada libera a
 * receita — não há mais tela pra desfazer por lá.
 */
export async function hasActiveReservaYield(transactionIds: string[]): Promise<boolean> {
    if (transactionIds.length === 0) return false;
    const rows = await db
        .select({ id: reservaMovements.id })
        .from(reservaMovements)
        .innerJoin(reservas, eq(reservaMovements.reservaId, reservas.id))
        // Conta fora (ADR 0162): a caixinha vira só leitura, sem lixeira; a receita se solta.
        .innerJoin(accounts, eq(reservas.accountId, accounts.id))
        .where(and(
            inArray(reservaMovements.transactionId, transactionIds),
            eq(reservaMovements.type, 'yield'),
            isNull(reservas.deletedAt),
            eq(reservas.isArchived, 0),
            isNull(accounts.deletedAt),
            eq(accounts.isArchived, 0),
        ))
        .limit(1);
    return rows.length > 0;
}

/** A edição mexe no dinheiro da linha? (O formulário manda tudo; compara com o que está gravado.) */
function changesTransactionMoney(old: Transaction, data: TransactionUpdateData): boolean {
    const cents = (v: number) => Math.round(v * 100);
    return (data.type !== undefined && data.type !== old.type)
        || (data.amount !== undefined && cents(data.amount) !== cents(old.amount))
        || (data.date !== undefined && data.date !== old.date)
        || (data.accountId !== undefined && (data.accountId || null) !== old.accountId)
        || !!data.creditCardId
        || !!data.transferDestinationAccountId
        || (data.isConfirmed !== undefined && (data.isConfirmed ? 1 : 0) !== old.isConfirmed)
        // Rendimento de caixinha fica fora das receitas (ADR 0164): tirar o "Ignorar" solto o
        // contaria duas vezes, na conta e na caixinha.
        || (data.isIgnored !== undefined && (data.isIgnored ? 1 : 0) !== old.isIgnored)
        // Virar parcela de um parcelamento cria cópias do mesmo dinheiro. Com o valor "por parcela" o
        // valor da linha não muda, e a conversão passava pela trava (QA de 07/10/2026).
        || (data.installmentId != null && data.installmentId !== old.installmentId);
}

/** Transação criada por um depósito de meta (receita, ajuste ou transferência). */
async function isGoalDepositTransaction(transactionId: string): Promise<boolean> {
    const rows = await db
        .select({ id: goalDeposits.id })
        .from(goalDeposits)
        .where(eq(goalDeposits.transactionId, transactionId))
        .limit(1);
    return rows.length > 0;
}

async function updateSingleTransaction(
    id: string,
    data: Parameters<typeof updateTransaction>[1],
): Promise<void> {
    // Get old transaction for balance recalculation
    const old = await db.select().from(transactions).where(eq(transactions.id, id));
    if (!old[0]) return;

    if (old[0].financingId) {
        if (old[0].financingEntryType !== FinancingEntryType.REGULAR_INSTALLMENT) {
            throw new ExpectedError(i18n.t('ui:errors.paydownEntriesManagedInFinancing'));
        }

        // Valor/data/conta de parcela PENDENTE são ajustáveis no fluxo de pagamento
        // (antecipação com desconto, pagar por outra conta) — e no caminho de volta: desfazer o
        // pagamento devolve a parcela ao vencimento, valor e conta de antes (o pagamento carimba a
        // data de hoje; sem isto o Desfazer deixava a parcela pendente no mês errado). Confirmada
        // sem estar voltando a pendente, ou outros campos: bloqueado.
        const returningToPending = data.isConfirmed === false;
        if (hasFinancingStructuralChanges(data, { allowSettlementFields: old[0].isConfirmed === 0 || returningToPending })) {
            throw new ExpectedError(i18n.t('ui:errors.financingInstallmentsManaged'));
        }
    }

    // Saída, recebimento, excedente, baixa e entrada esperada vivem no ciclo do
    // empréstimo (saldo/eventos) — editar solto dessincronizaria o estado.
    if (old[0].personalLoanId) {
        throw new ExpectedError(i18n.t('ui:errors.loanEntriesManagedInLoans'));
    }

    // Liquidação de pagamento de fatura: é metade de um par com `invoice_payments`, e só a
    // tela da fatura mexe nos dois juntos. Voltar a pendente devolvia o dinheiro à conta com
    // a fatura ainda paga; efetivar de novo mudando valor/conta/data descasava o par e o
    // pagamento ficava impossível de desfazer (Sentry REACT-NATIVE-2M).
    if (old[0].adjustmentType === AdjustmentType.INVOICE_PAYMENT) {
        throw new ExpectedError(i18n.t('ui:errors.invoicePaymentManagedInInvoice'));
    }

    // Depósito de meta: editar solto dessincronizaria o progresso e o estorno.
    if (await isGoalDepositTransaction(id)) {
        throw new ExpectedError(i18n.t('ui:errors.goalDepositsManagedInGoal'));
    }

    // Rendimento de caixinha: nome, categoria, nota e tags mudam aqui; o dinheiro, só pela
    // caixinha (ADR 0161).
    if (changesTransactionMoney(old[0], data) && await hasActiveReservaYield([id])) {
        throw new ExpectedError(i18n.t('ui:errors.reservaYieldManagedInReserva'));
    }

    const updateData: Record<string, unknown> = { updatedAt: new Date().toISOString() };
    if (data.type !== undefined) updateData.type = data.type;
    if (data.amount !== undefined) updateData.amount = data.amount;
    if (data.description !== undefined) updateData.description = data.description;
    if (data.date !== undefined) updateData.date = data.date;
    if (data.time !== undefined) updateData.time = data.time || null;
    if (data.notes !== undefined) updateData.notes = data.notes || null;
    if (data.accountId !== undefined) updateData.accountId = data.accountId || null;
    if (data.creditCardId !== undefined) updateData.creditCardId = data.creditCardId || null;
    if (data.categoryId !== undefined) updateData.categoryId = data.categoryId || null;
    if (data.subcategoryId !== undefined) updateData.subcategoryId = data.subcategoryId || null;
    if (data.transferDestinationAccountId !== undefined) {
        updateData.transferDestinationAccountId = data.transferDestinationAccountId || null;
    }
    if (data.isConfirmed !== undefined) updateData.isConfirmed = data.isConfirmed ? 1 : 0;
    // EFETIVAR carimba a hora. Um previsto nasce sem hora (parcela de financiamento,
    // ocorrência de recorrência, entrada esperada) e, quando vira pago HOJE, ganha o
    // "agora" — a mesma regra que o formulário aplica na criação. Fica aqui porque é a
    // porta única da transição: marcar na lista, liquidar pelo sheet e salvar o
    // formulário passam todos por este ponto.
    // Só na virada pendente→pago e só quando não há hora: editar um lançamento JÁ pago
    // não é o momento da efetivação, e uma hora escolhida (ou vinda da captura de
    // notificação) é do usuário — remarcar não pode reescrevê-la. `!data.time` (e não
    // `=== undefined`): o formulário de edição sempre manda o campo, com '' quando o
    // usuário não escolheu hora — efetivar por ele (único caminho pra recorrência de
    // CARTÃO) deixava a ocorrência sem hora. String vazia é "sem hora", não escolha.
    if (
        data.isConfirmed === true
        && old[0].isConfirmed === 0
        && !old[0].time
        && !data.time
    ) {
        const settledTime = resolveTransactionTime(data.date ?? old[0].date);
        if (settledTime) updateData.time = settledTime;
    }
    // Ignorar (ADR 0037): flag por-lançamento; sem recálculo de saldo (o dinheiro é real).
    if (data.isIgnored !== undefined) updateData.isIgnored = data.isIgnored ? 1 : 0;
    if (data.isRecurring !== undefined) updateData.isRecurring = data.isRecurring ? 1 : 0;
    if (data.recurringId !== undefined) updateData.recurringId = data.recurringId;
    if (data.installmentId !== undefined) updateData.installmentId = data.installmentId;
    if (data.installmentNumber !== undefined) updateData.installmentNumber = data.installmentNumber;
    if (data.installmentTotal !== undefined) updateData.installmentTotal = data.installmentTotal;

    // Handle credit card / invoice assignment
    const effectiveCreditCardId =
        data.creditCardId !== undefined ? (data.creditCardId || null) : old[0].creditCardId;
    // Só re-resolve a fatura quando algo que a DEFINE mudou: o cartão, a data, um mês
    // explícito — ou a linha ainda não tem fatura. Antes QUALQUER edição de linha com cartão
    // (pendente/pago pelo arrasto, nota) recomputava a fatura pela data e puxava de volta uma
    // compra que o usuário tinha movido à mão pra outro mês no formulário. Mesmo gate das
    // séries (updateMaterializedRecurringSeries / updateInstallmentSeries).
    const cardChanged =
        data.creditCardId !== undefined && (data.creditCardId || null) !== old[0].creditCardId;
    const dateChanged = data.date !== undefined && data.date !== old[0].date;
    const shouldReassignInvoice =
        cardChanged || dateChanged || data.invoiceMonth !== undefined || !old[0].invoiceId;

    if (effectiveCreditCardId && shouldReassignInvoice) {
        const card = await db.select().from(creditCards).where(eq(creditCards.id, effectiveCreditCardId));
        const txDate = data.date ?? old[0].date;
        if (card[0]) {
            const invoiceMonth = data.invoiceMonth
                ?? getInvoiceMonthForDate(txDate, card[0].closingDay, card[0].closingDayToNextInvoice === 1);
            const invoice = await getOrCreateInvoice(effectiveCreditCardId, invoiceMonth);
            updateData.invoiceId = invoice.id;
        }
    } else if (data.creditCardId === '') {
        updateData.invoiceId = null;
    }
    // Conta e Cartão são EXCLUDENTES no lançamento (o formulário é um XOR — ver
    // `originCardIdExpr` em transactionScopes). O form manda `accountId: undefined` em
    // modo cartão, e "undefined = não mexer" deixava a conta antiga na linha: a despesa
    // cadastrada em débito e trocada pra cartão entrava na fatura E seguia descontando
    // do saldo da conta (Denis, e-mail de 17/set/2026). Quem tem cartão não tem conta.
    // Linhas sintéticas da fatura (`adjustment_type`) carregam os dois DE PROPÓSITO —
    // a despesa pendente da fatura e o pagamento vivem na conta de pagamento.
    if (effectiveCreditCardId && !old[0].adjustmentType) updateData.accountId = null;

    await db.update(transactions).set(updateData).where(eq(transactions.id, id));

    // Recalculate affected account balances
    const affectedAccounts = new Set<string>();
    if (old[0].accountId) affectedAccounts.add(old[0].accountId);
    if (old[0].transferDestinationAccountId) affectedAccounts.add(old[0].transferDestinationAccountId);
    if (data.accountId) affectedAccounts.add(data.accountId);
    if (data.transferDestinationAccountId) affectedAccounts.add(data.transferDestinationAccountId);

    for (const accountId of affectedAccounts) {
        await recalculateAccountBalance(accountId);
    }

    // Recalculate affected invoice totals
    const affectedInvoices = new Set<string>();
    if (old[0].invoiceId) affectedInvoices.add(old[0].invoiceId);
    const updated = await db.select().from(transactions).where(eq(transactions.id, id));
    if (updated[0]?.invoiceId) affectedInvoices.add(updated[0].invoiceId);
    for (const invoiceId of affectedInvoices) {
        await recalculateInvoiceTotal(invoiceId);
    }

    if (old[0].financingId) {
        await syncFinancingState(old[0].financingId);
    }
}

/** Resumo da série de parcelas, para o selo "3/12" do formulário. */
export interface InstallmentSeriesSummary {
    /** `installmentTotal` gravado nas parcelas (quantas a compra tem). */
    totalInstallments: number;
    /** Quantas parcelas da série existem de fato no banco. */
    recordedCount: number;
    /** Menor `installmentNumber` gravado. */
    firstNumber: number;
    /** Soma dos valores GRAVADOS — não `parcela × total`, que erra quando a última absorve centavos. */
    recordedTotal: number;
    /** Data da primeira e da última parcela gravadas (yyyy-MM-dd). */
    firstDate: string;
    lastDate: string;
    /**
     * A série está inteira no app: começa na parcela 1 e tem todas as `totalInstallments`.
     * Falso = não dá para afirmar o valor nem a data da COMPRA — quem cadastrou "a partir da
     * 3/12" nunca teve as duas primeiras aqui, e somar o que existe mentiria sobre dinheiro.
     */
    isComplete: boolean;
}

/**
 * Lê a série pelas TRANSAÇÕES, e não pela tabela `installments`.
 *
 * É o único caminho que serve aos dois casos: parcela de cartão cria linha em
 * `installments` (que exige `creditCardId`), mas parcelado em CONTA não cria nenhuma — ali
 * o vínculo existe só no `installmentId` das transações (laço manual do formulário).
 */
export async function getInstallmentSeriesSummary(
    installmentId: string,
): Promise<InstallmentSeriesSummary | null> {
    if (!installmentId) return null;

    const series = await db
        .select({
            amount: transactions.amount,
            date: transactions.date,
            installmentNumber: transactions.installmentNumber,
            installmentTotal: transactions.installmentTotal,
        })
        .from(transactions)
        .where(and(eq(transactions.installmentId, installmentId), isNull(transactions.deletedAt)))
        .orderBy(asc(transactions.installmentNumber), asc(transactions.date));

    if (series.length === 0) return null;

    const totalInstallments = series[0].installmentTotal ?? series.length;
    const firstNumber = Math.min(...series.map((item, index) => item.installmentNumber ?? index + 1));

    return {
        totalInstallments,
        recordedCount: series.length,
        firstNumber,
        recordedTotal: roundCurrency(series.reduce((sum, item) => sum + item.amount, 0)),
        firstDate: series[0].date,
        lastDate: series[series.length - 1].date,
        isComplete: firstNumber === 1 && series.length === totalInstallments,
    };
}

async function syncInstallmentRecord(installmentId: string): Promise<void> {
    const series = await db
        .select({
            id: transactions.id,
            description: transactions.description,
            amount: transactions.amount,
            date: transactions.date,
            creditCardId: transactions.creditCardId,
            categoryId: transactions.categoryId,
            subcategoryId: transactions.subcategoryId,
            installmentNumber: transactions.installmentNumber,
            installmentTotal: transactions.installmentTotal,
        })
        .from(transactions)
        .where(and(eq(transactions.installmentId, installmentId), isNull(transactions.deletedAt)))
        .orderBy(asc(transactions.installmentNumber), asc(transactions.date));

    if (series.length === 0) {
        await db
            .update(installments)
            .set({ isActive: 0, updatedAt: new Date().toISOString() })
            .where(eq(installments.id, installmentId));
        return;
    }

    // O cabeçalho só existe pra série DE CARTÃO (série nativa de conta nunca ganha linha
    // aqui — este UPDATE atinge 0 linhas). Parcela movida pra conta sai do espelho; sem
    // NENHUMA parcela no cartão, o cabeçalho desativa como no caso de série vazia — e
    // reativa sozinho se a série voltar pro cartão. `credit_card_id` é NOT NULL, então
    // gravar o card da série mista (ou nada) é obrigatório (Sentry REACT-NATIVE-14).
    const anchorCardId = series
        .map((tx) => tx.creditCardId)
        .find((cardId): cardId is string => cardId != null);
    if (!anchorCardId) {
        await db
            .update(installments)
            .set({ isActive: 0, updatedAt: new Date().toISOString() })
            .where(eq(installments.id, installmentId));
        return;
    }

    const totalAmount = series.reduce((sum, tx) => sum + tx.amount, 0);
    const first = series[0];
    const highestInstallmentTotal = Math.max(...series.map((tx) => tx.installmentTotal ?? series.length));
    const baseDescription = stripInstallmentSuffix(first.description);

    await db
        .update(installments)
        .set({
            description: baseDescription,
            totalAmount,
            installmentCount: highestInstallmentTotal,
            installmentAmount: first.amount,
            creditCardId: anchorCardId,
            categoryId: first.categoryId ?? null,
            subcategoryId: first.subcategoryId ?? null,
            startDate: first.date,
            isActive: 1,
            updatedAt: new Date().toISOString(),
        })
        .where(eq(installments.id, installmentId));
}

async function updateInstallmentSeries(
    targetTx: Transaction,
    data: Parameters<typeof updateTransaction>[1],
    mode: InstallmentRebaseMode,
): Promise<void> {
    if (!targetTx.installmentId) {
        await updateSingleTransaction(targetTx.id, data);
        return;
    }
    const installmentId = targetTx.installmentId;

    const series = await db
        .select()
        .from(transactions)
        .where(and(eq(transactions.installmentId, targetTx.installmentId), isNull(transactions.deletedAt)))
        .orderBy(asc(transactions.installmentNumber), asc(transactions.createdAt));

    if (series.length === 0) return;

    const effectiveCreditCardId =
        data.creditCardId !== undefined ? (data.creditCardId || null) : targetTx.creditCardId;
    const effectiveDate = data.date ?? targetTx.date;
    const anchorInstallmentNumber = targetTx.installmentNumber ?? 1;

    // Fix #3 (auditoria jul/2026): só rebaseia data/fatura das parcelas quando algo que
    // REALMENTE as muda mudou — o cartão, a data, ou um invoiceMonth explícito. O form
    // sempre reenvia `date` e `creditCardId`, então uma edição de CATEGORIA com escopo
    // "Todas" reescrevia a data de TODAS as parcelas pela régua (sobrescrevendo parcela
    // ajustada à mão) e reatribuía fatura — inclusive fatura já fechada/paga mudava de
    // total. Mesmo gate do fluxo de recorrência (ver updateMaterializedRecurringSeries).
    const cardChanged =
        data.creditCardId !== undefined && (data.creditCardId || null) !== targetTx.creditCardId;
    const dateChanged = data.date !== undefined && data.date !== targetTx.date;
    const shouldRebaseDates = cardChanged || dateChanged || data.invoiceMonth !== undefined;

    let effectiveInvoiceMonth: string | undefined;
    if (effectiveCreditCardId && shouldRebaseDates) {
        const [card] = await db.select().from(creditCards).where(eq(creditCards.id, effectiveCreditCardId));
        if (card) {
            effectiveInvoiceMonth = data.invoiceMonth
                ?? getInvoiceMonthForDate(effectiveDate, card.closingDay, card.closingDayToNextInvoice === 1);
        }
    }

    const plan = buildInstallmentRebasePlan({
        installmentNumbers: series.map((item) => item.installmentNumber ?? anchorInstallmentNumber),
        anchorInstallmentNumber,
        mode,
        baseDate: effectiveDate,
        baseInvoiceMonth: effectiveInvoiceMonth,
    });
    const planMap = new Map(plan.map((entry) => [entry.installmentNumber, entry]));

    const affectedSeries = series.flatMap((tx) => {
        const installmentNumber = tx.installmentNumber ?? anchorInstallmentNumber;
        const plannedEntry = planMap.get(installmentNumber);
        const shouldUpdate =
            mode === 'all'
                || installmentNumber >= anchorInstallmentNumber
                || installmentNumber === anchorInstallmentNumber;
        return plannedEntry && shouldUpdate ? [{ tx, plannedEntry }] : [];
    });

    // Tirar parcela do cartão só vale em fatura sem NENHUM pagamento: o pagamento da
    // fatura já debitou a conta, então re-lançar a parcela como despesa de conta
    // contaria o mesmo dinheiro duas vezes. Barreira ANTES de qualquer escrita.
    const removingCard = cardChanged && !effectiveCreditCardId;
    if (removingCard) {
        const scopedInvoiceIds = [...new Set(
            affectedSeries.flatMap(({ tx }) => (tx.invoiceId ? [tx.invoiceId] : [])),
        )];
        if (scopedInvoiceIds.length > 0) {
            const paidInvoices = await db
                .select({ id: invoices.id })
                .from(invoices)
                .where(and(inArray(invoices.id, scopedInvoiceIds), sql`${invoices.paidAmount} > 0`))
                .limit(1);
            if (paidInvoices.length > 0) {
                throw new ExpectedError(i18n.t('ui:errors.installmentLeavePaidInvoice'));
            }
        }
    }

    // Remanejo de série não entra nem sai de fatura que já recebeu pagamento (ADR 0086).
    // Mudar o mês da fatura de uma parcela do meio com escopo "Todas" desliza a competência
    // da série INTEIRA — `buildSeriesRebasePlan` aplica `posição − âncora` ao mês da fatura,
    // e as posições ANTERIORES à âncora entram nesse deslize. Numa fatura já paga isso
    // arrancava a compra sem tocar no pagamento: a fatura ficava com paid_amount > total
    // ("Pagamento completo R$ 200,00" ao lado de "Fatura paga R$ 100,00"). Em fatura ABERTA
    // o deslize é o comportamento pedido — regenerar a série — e segue valendo.
    //
    // O teste é por MOVIMENTO real (fatura de destino ≠ fatura de hoje), não pela intenção:
    // o formulário sempre reenvia `invoiceMonth`, então `shouldRebaseDates` é sempre verdade
    // vindo dele, e gatear pela flag barraria até renomear a série. A ÂNCORA fica de fora —
    // é a linha que o usuário escolheu mexer, e ali vale o aviso do ADR 0085, não a barreira.
    if (shouldRebaseDates && effectiveCreditCardId) {
        const movers = affectedSeries.filter(
            ({ tx }) => (tx.installmentNumber ?? anchorInstallmentNumber) !== anchorInstallmentNumber,
        );
        if (movers.length > 0) {
            const sourceIds = [...new Set(movers.flatMap(({ tx }) => (tx.invoiceId ? [tx.invoiceId] : [])))];
            const targetMonths = [...new Set(
                movers.flatMap(({ plannedEntry }) => (plannedEntry.invoiceMonth ? [plannedEntry.invoiceMonth] : [])),
            )];

            const sourceRows = sourceIds.length > 0
                ? await db
                    .select({
                        id: invoices.id,
                        creditCardId: invoices.creditCardId,
                        referenceMonth: invoices.referenceMonth,
                        paidAmount: invoices.paidAmount,
                    })
                    .from(invoices)
                    .where(inArray(invoices.id, sourceIds))
                : [];
            const sourceById = new Map(sourceRows.map((row) => [row.id, row]));

            const targetRows = targetMonths.length > 0
                ? await db
                    .select({ referenceMonth: invoices.referenceMonth, paidAmount: invoices.paidAmount })
                    .from(invoices)
                    .where(and(
                        eq(invoices.creditCardId, effectiveCreditCardId),
                        inArray(invoices.referenceMonth, targetMonths),
                    ))
                : [];
            const settledTargetMonths = new Set(
                targetRows.filter((row) => row.paidAmount > 0).map((row) => row.referenceMonth),
            );

            const dragsSettledInvoice = movers.some(({ tx, plannedEntry }) => {
                const source = tx.invoiceId ? sourceById.get(tx.invoiceId) : undefined;
                const staysPut = !!source
                    && source.creditCardId === effectiveCreditCardId
                    && source.referenceMonth === plannedEntry.invoiceMonth;
                if (staysPut) return false;
                return (source?.paidAmount ?? 0) > 0
                    || (!!plannedEntry.invoiceMonth && settledTargetMonths.has(plannedEntry.invoiceMonth));
            });

            if (dragsSettledInvoice) {
                throw new ExpectedError(i18n.t('ui:errors.installmentRebasePaidInvoice'));
            }
        }
    }

    const today = format(new Date(), 'yyyy-MM-dd');

    const affectedAccounts = new Set<string>();
    const affectedInvoices = new Set<string>();

    // Escritas casadas (parcelas + cabeçalho da série): o incidente REACT-NATIVE-14
    // estourou no cabeçalho DEPOIS das parcelas gravadas e deixou a série meio migrada.
    // Os recálculos ficam de fora de propósito — são agregações pesadas e segurariam o
    // mutex (mesma razão do dedup em recurringRepository).
    await runInTransaction(async () => {
        for (const { tx, plannedEntry } of affectedSeries) {
            if (tx.accountId) affectedAccounts.add(tx.accountId);
            if (tx.transferDestinationAccountId) affectedAccounts.add(tx.transferDestinationAccountId);
            if (tx.invoiceId) affectedInvoices.add(tx.invoiceId);

            const updateData: Record<string, unknown> = {
                updatedAt: new Date().toISOString(),
            };

            if (data.type !== undefined) updateData.type = data.type;
            if (data.amount !== undefined) updateData.amount = data.amount;
            if (data.description !== undefined) {
                updateData.description = updateDescriptionWithInstallmentSuffix(
                    data.description,
                    tx.installmentNumber,
                    tx.installmentTotal,
                );
            }
            if (data.notes !== undefined) updateData.notes = data.notes ?? null;
            if (data.time !== undefined) updateData.time = data.time || null;
            // Ignorar (ADR 0037) segue o escopo como notes/categoria — era descartado aqui,
            // então marcar "Ignorar" numa parcela (qualquer escopo) não gravava NADA.
            if (data.isIgnored !== undefined) updateData.isIgnored = data.isIgnored ? 1 : 0;
            if (data.accountId !== undefined) updateData.accountId = data.accountId || null;
            if (data.categoryId !== undefined) updateData.categoryId = data.categoryId || null;
            if (data.subcategoryId !== undefined) updateData.subcategoryId = data.subcategoryId || null;
            if (data.transferDestinationAccountId !== undefined) {
                updateData.transferDestinationAccountId = data.transferDestinationAccountId || null;
            }
            if (mode === 'single' && data.isConfirmed !== undefined) {
                updateData.isConfirmed = data.isConfirmed ? 1 : 0;
            }

            if (shouldRebaseDates) {
                if (effectiveCreditCardId) {
                    const invoice = await getOrCreateInvoice(effectiveCreditCardId, plannedEntry.invoiceMonth!);
                    updateData.creditCardId = effectiveCreditCardId;
                    updateData.invoiceId = invoice.id;
                    updateData.date = plannedEntry.date;
                    affectedInvoices.add(invoice.id);
                    if (cardChanged) {
                        // Entrar no cartão produz o formato canônico de série de cartão:
                        // parcela efetivada e SEM conta — o débito real é o pagamento da
                        // fatura; manter account_id contaria o dinheiro duas vezes.
                        updateData.accountId = null;
                        updateData.isConfirmed = 1;
                    }
                } else {
                    updateData.creditCardId = null;
                    updateData.invoiceId = null;
                    updateData.date = plannedEntry.date;
                    if (removingCard) {
                        // Parcela que sai do cartão segue a convenção da série nativa de
                        // conta: futura fica pendente, passada efetivada. No cartão toda
                        // parcela nasce isConfirmed=1 sem nunca tocar saldo (accountId
                        // nulo); manter isso na conta debitaria o saldo de uma vez com
                        // parcelas de anos à frente. Vence o isConfirmed do form.
                        updateData.isConfirmed = plannedEntry.date <= today ? 1 : 0;
                    }
                }
            }

            // EFETIVAR carimba a hora — mesma regra da porta 'single', que a série de
            // parcelas não tinha: pagar uma parcela de CONTA (installmentId) passa por
            // aqui e ficava sem hora. Só na virada explícita pendente→pago do form
            // (mode 'single'), nunca na normalização entrar/sair do cartão acima; fora
            // de hoje o resolveTransactionTime devolve null e nada muda.
            if (
                mode === 'single'
                && data.isConfirmed === true
                && tx.isConfirmed === 0
                && !tx.time
                && !data.time
            ) {
                const settledTime = resolveTransactionTime((updateData.date as string | undefined) ?? tx.date);
                if (settledTime) updateData.time = settledTime;
            }

            await db.update(transactions).set(updateData).where(eq(transactions.id, tx.id));

            if (data.accountId) affectedAccounts.add(data.accountId);
            if (data.transferDestinationAccountId) affectedAccounts.add(data.transferDestinationAccountId);
        }

        await syncInstallmentRecord(installmentId);
    });

    for (const accountId of affectedAccounts) {
        await recalculateAccountBalance(accountId);
    }
    for (const invoiceId of affectedInvoices) {
        await recalculateInvoiceTotal(invoiceId);
    }
}

export type DeleteMode = EditScope;

async function deleteProjectedRecurringTransaction(
    recurringId: string,
    occurrenceDate: string,
    mode: DeleteMode,
): Promise<void> {
    if (mode === 'single') {
        throw new ExpectedError(i18n.t('ui:errors.cannotDeleteSingleFutureOccurrence'));
    }

    const [rule] = await db
        .select()
        .from(recurringTransactions)
        .where(and(eq(recurringTransactions.id, recurringId), isNull(recurringTransactions.deletedAt)));

    if (!rule) return;

    const now = new Date().toISOString();
    const materializedCondition = mode === 'all'
        ? and(eq(transactions.recurringId, recurringId), isNull(transactions.deletedAt))
        : and(
            eq(transactions.recurringId, recurringId),
            gte(transactions.date, occurrenceDate),
            isNull(transactions.deletedAt),
        );

    const toDelete = await db.select().from(transactions).where(materializedCondition);

    if (toDelete.length > 0) {
        await db
            .update(transactions)
            .set({ deletedAt: now, updatedAt: now })
            .where(materializedCondition);
    }

    if (mode === 'all') {
        await db
            .update(recurringTransactions)
            .set({
                isActive: 0,
                deletedAt: now,
                updatedAt: now,
            })
            .where(eq(recurringTransactions.id, recurringId));
    } else {
        const anchorIndex = occurrenceIndexOf(occurrenceDate, rule) ?? 0;
        const hasPreviousOccurrence = anchorIndex > 0;
        const previousOccurrenceDate = hasPreviousOccurrence
            ? occurrenceDateAt(anchorIndex - 1, rule)
            : null;
        const shouldRemainActive = !!previousOccurrenceDate && rule.nextOccurrence <= previousOccurrenceDate;

        await db
            .update(recurringTransactions)
            .set({
                endDate: previousOccurrenceDate,
                // Invariante do count: truncar a cauda zera o count (degrada para
                // endDate explícito). Ver docs/features/RECURRENCE.md.
                count: null,
                isActive: shouldRemainActive ? 1 : 0,
                updatedAt: now,
            })
            .where(eq(recurringTransactions.id, recurringId));
    }

    const affectedAccounts = new Set<string>();
    const affectedInvoices = new Set<string>();

    for (const tx of toDelete) {
        if (tx.accountId) affectedAccounts.add(tx.accountId);
        if (tx.transferDestinationAccountId) affectedAccounts.add(tx.transferDestinationAccountId);
        if (tx.invoiceId) affectedInvoices.add(tx.invoiceId);
    }

    for (const accountId of affectedAccounts) {
        await recalculateAccountBalance(accountId);
    }
    for (const invoiceId of affectedInvoices) {
        await recalculateInvoiceTotal(invoiceId);
    }
}

export async function deleteTransaction(id: string, mode: DeleteMode = 'single'): Promise<void> {
    const projectedId = parseProjectedRecurringTransactionId(id);
    if (projectedId) {
        await deleteProjectedRecurringTransaction(projectedId.recurringId, projectedId.date, mode);
        return;
    }

    const old = await db.select().from(transactions).where(eq(transactions.id, id));
    if (!old[0]) return;

    const targetTx = old[0];
    if (targetTx.financingId) {
        throw new ExpectedError(i18n.t('ui:errors.financingEntriesManagedInFinancing'));
    }
    if (targetTx.personalLoanId) {
        throw new ExpectedError(i18n.t('ui:errors.loanEntriesManagedInLoans'));
    }
    // Excluir só a liquidação deixava o registro em `invoice_payments` sem par: fatura paga,
    // dinheiro de volta na conta. Desfazer é na tela da fatura (deleteInvoicePayment).
    if (targetTx.adjustmentType === AdjustmentType.INVOICE_PAYMENT) {
        throw new ExpectedError(i18n.t('ui:errors.invoicePaymentManagedInInvoice'));
    }
    if (await isGoalDepositTransaction(id)) {
        throw new ExpectedError(i18n.t('ui:errors.goalDepositsManagedInGoal'));
    }
    if (await hasActiveReservaYield([id])) {
        throw new ExpectedError(i18n.t('ui:errors.reservaYieldManagedInReserva'));
    }
    const isRecurringItem = targetTx.isRecurring === 1 || !!targetTx.installmentId;
    const recurrenceKey = targetTx.installmentId || targetTx.recurringId;

    let condition = eq(transactions.id, id);

    if (isRecurringItem && recurrenceKey && mode !== 'single') {
        if (mode === 'all') {
            condition = targetTx.installmentId
                ? eq(transactions.installmentId, recurrenceKey)
                : eq(transactions.recurringId, recurrenceKey);
        } else if (mode === 'future') {
            // Corta por SLOT (recurringOccurrenceDate), não pela data REAL. Uma ocorrência
            // remarcada pra outro dia tem data ≠ slot: cortar por `date >= targetTx.date`
            // apagava o conjunto ERRADO — uma ocorrência de slot posterior remarcada pra antes
            // escapava (sobrevivia), uma de slot anterior remarcada pra depois era apagada
            // junto. Parcelamentos não têm slot, então o COALESCE cai na data e o comportamento
            // deles não muda. Mesma raiz do R4/ruleShapeChanged. Auditoria jul/2026 (M2).
            const anchorSlot = targetTx.recurringOccurrenceDate ?? targetTx.date;
            condition = and(
                targetTx.installmentId
                    ? eq(transactions.installmentId, recurrenceKey)
                    : eq(transactions.recurringId, recurrenceKey),
                sql`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date}) >= ${anchorSlot}`,
            ) as any;
        }
    }

    const toDelete = await db.select().from(transactions).where(condition);
    const now = new Date().toISOString();

    // Atômico (M2, auditoria jul/2026): o soft-delete das ocorrências + a desativação da regra
    // vão juntos — um kill entre os dois deixava a série sem linhas mas com a regra viva (ou
    // vice-versa), re-materializando ocorrências fantasma no próximo ciclo.
    await runInTransaction(async () => {
        await db
            .update(transactions)
            .set({ deletedAt: now, updatedAt: now })
            .where(condition);

        if (targetTx.recurringId && mode !== 'single') {
            const [rule] = await db
                .select()
                .from(recurringTransactions)
                .where(eq(recurringTransactions.id, targetTx.recurringId));
            if (mode === 'future' && rule && !rule.deletedAt) {
                // "Esta e as próximas" numa linha REAL: a regra só perde a cauda a partir da
                // âncora; o que ela ainda projeta ANTES fica. Tombstonar aqui apagava out–dez
                // de uma série criada em setembro ao excluir janeiro (Denis, 15/set/2026).
                await endRecurringRuleBeforeSlot(rule, targetTx.recurringOccurrenceDate ?? targetTx.date, now);
            } else {
                await db
                    .update(recurringTransactions)
                    .set({ isActive: 0, deletedAt: now, updatedAt: now })
                    .where(eq(recurringTransactions.id, targetTx.recurringId));
            }
        }
    });

    // Recalculate balances for all affected accounts and invoices
    const affectedAccounts = new Set<string>();
    const affectedInvoices = new Set<string>();

    for (const tx of toDelete) {
        if (tx.accountId) affectedAccounts.add(tx.accountId);
        if (tx.transferDestinationAccountId) affectedAccounts.add(tx.transferDestinationAccountId);
        if (tx.invoiceId) affectedInvoices.add(tx.invoiceId);
    }

    for (const accountId of affectedAccounts) {
        await recalculateAccountBalance(accountId);
    }
    for (const invoiceId of affectedInvoices) {
        await recalculateInvoiceTotal(invoiceId);
    }
    if (targetTx.installmentId) {
        await syncInstallmentRecord(targetTx.installmentId);
    }
}

/**
 * Inverso do soft delete de UMA transação — o "Desfazer" do toast de exclusão.
 *
 * Por que existe: excluir uma transação era um diálogo de confirmação ("Tem certeza?")
 * numa ação que sempre foi REVERSÍVEL (a linha só ganha `deletedAt` e fica na lixeira).
 * Cobrar confirmação no que dá pra desfazer treina a pessoa a tocar em "Excluir" no
 * automático — e aí a confirmação não protege mais nada. O caminho certo é o contrário:
 * executa na hora, oferece o desfazer.
 *
 * Escopo DELIBERADAMENTE estreito: uma linha, pelo id. É o inverso exato de
 * `deleteTransaction(id, 'single')` numa transação que não é recorrente nem parcelada —
 * ali o delete toca só essa linha e não mexe em nenhuma regra de recorrência. Série
 * (recorrente/parcelada) continua no fluxo de escolha explícita, que não tem inverso
 * de uma linha só.
 */
export async function undeleteTransaction(id: string): Promise<void> {
    const [target] = await db.select().from(transactions).where(eq(transactions.id, id));
    // Sumiu de vez (expurgo da lixeira) ou já está viva: nada a fazer.
    if (!target || !target.deletedAt) return;

    await db
        .update(transactions)
        .set({ deletedAt: null, updatedAt: new Date().toISOString() })
        .where(eq(transactions.id, id));

    // Mesmos recálculos do delete, na ordem dele — os saldos foram recalculados SEM
    // esta linha e precisam voltar a contá-la.
    if (target.accountId) await recalculateAccountBalance(target.accountId);
    if (target.transferDestinationAccountId) await recalculateAccountBalance(target.transferDestinationAccountId);
    if (target.invoiceId) await recalculateInvoiceTotal(target.invoiceId);
    if (target.installmentId) await syncInstallmentRecord(target.installmentId);
}

// ─── Aggregation queries ─────────────────────────────────────

export interface CategorySpending {
    categoryId: string;
    categoryName: string;
    categoryColor: string;
    categoryIcon: string;
    total: number;
    /**
     * Receita ou despesa, herdado da primeira transação da categoria. Sem isso a
     * UI não tem como saber o sinal do dinheiro: as pizzas por tipo são
     * homogêneas, mas a do relatório com "Todas" mistura os dois e pintava tudo
     * de vermelho — inclusive Salário. `undefined` nas agregações que nascem de
     * `groupBy` (todas elas já recortadas em despesa).
     */
    categoryType?: 'income' | 'expense';
}

export const UNCATEGORIZED_CATEGORY = {
    id: '__uncategorized__',
    name: 'Sem categoria',
    color: '#6B7280',
    icon: 'dots-three',
} as const;

export interface SubcategorySpending {
    /** Sentinela UNCATEGORIZED_SUBCATEGORY.id para a fatia "Sem subcategoria". */
    subcategoryId: string;
    subcategoryName: string;
    total: number;
}

export const UNCATEGORIZED_SUBCATEGORY = {
    id: '__no_subcategory__',
    name: 'Sem subcategoria',
} as const;

function addSubcategorySpending(
    totals: Map<string, SubcategorySpending>,
    row: { subcategoryId?: string | null; subcategoryName?: string | null },
    amount: number,
) {
    if (amount === 0) return;

    const subcategoryId = row.subcategoryId ?? UNCATEGORIZED_SUBCATEGORY.id;
    const entry = totals.get(subcategoryId) ?? {
        subcategoryId,
        subcategoryName: row.subcategoryName ?? UNCATEGORIZED_SUBCATEGORY.name,
        total: 0,
    };
    entry.total += amount;
    totals.set(subcategoryId, entry);
}

function matchesCategorySpendingFilter(
    transactionCategoryId: string | null | undefined,
    filters?: { categoryId?: string; categoryIds?: string[] },
) {
    const normalizedCategoryId = transactionCategoryId ?? UNCATEGORIZED_CATEGORY.id;

    if (filters?.categoryIds?.length) {
        return filters.categoryIds.includes(normalizedCategoryId);
    }

    if (filters?.categoryId) {
        return normalizedCategoryId === filters.categoryId;
    }

    return true;
}

function addCategorySpending(
    totalsByCategory: Map<string, CategorySpending>,
    category: {
        categoryId?: string | null;
        categoryName?: string | null;
        categoryColor?: string | null;
        categoryIcon?: string | null;
        /** Da transação de origem; a categoria em si já é tipada no cadastro. */
        type?: string | null;
    },
    amount: number,
) {
    if (amount === 0) return;

    const categoryId = category.categoryId ?? UNCATEGORIZED_CATEGORY.id;
    const entry = totalsByCategory.get(categoryId) ?? {
        categoryId,
        categoryName: category.categoryName ?? UNCATEGORIZED_CATEGORY.name,
        categoryColor: category.categoryColor ?? UNCATEGORIZED_CATEGORY.color,
        categoryIcon: category.categoryIcon ?? UNCATEGORIZED_CATEGORY.icon,
        total: 0,
        categoryType: category.type === 'income' || category.type === 'expense'
            ? category.type
            : undefined,
    };

    entry.total += amount;
    totalsByCategory.set(categoryId, entry);
}

export async function getSpendingByCategory(
    startDate: string,
    endDate: string,
    type: 'expense' | 'income' = 'expense',
    mode: AnalyticsMode = 'accrual',
    options?: AnalyticReadOptions,
): Promise<CategorySpending[]> {
    if (type === 'expense' && mode === 'cash-flow') {
        const [regularRows, invoiceRows] = await Promise.all([
            db
                .select({
                    categoryId: transactions.categoryId,
                    categoryName: categories.name,
                    categoryColor: categories.color,
                    categoryIcon: categories.icon,
                    total: sql<number>`COALESCE(SUM(${transactions.amount}), 0)`.as('total'),
                })
                .from(transactions)
                .leftJoin(categories, eq(transactions.categoryId, categories.id))
                .where(
                    and(
                        eq(transactions.type, 'expense'),
                        gte(transactions.date, startDate),
                        lte(transactions.date, endDate),
                        isNull(transactions.deletedAt),
                        sql`${transactions.creditCardId} IS NULL AND ${transactions.adjustmentType} IS NULL`,
                        excludePersonalLoanPrincipal(),
                        excludeIgnored(),
                    ),
                )
                .groupBy(transactions.categoryId)
                .orderBy(sql`total DESC`),
            db
                .select({
                    total: sql<number>`COALESCE(SUM(${transactions.amount}), 0)`.as('total'),
                })
                .from(transactions)
                .where(
                    and(
                        eq(transactions.type, 'expense'),
                        gte(transactions.date, startDate),
                        lte(transactions.date, endDate),
                        isNull(transactions.deletedAt),
                        sql`${transactions.adjustmentType} IN (${AdjustmentType.INVOICE_PAYMENT}, ${AdjustmentType.INVOICE_EXPENSE})`,
                        excludeIgnored(),
                        excludeDeletedCardTransactions(),
                    ),
                ),
        ]);

        return buildCashFlowCategorySpending(regularRows.map((row) => ({
            categoryId: row.categoryId ?? UNCATEGORIZED_CATEGORY.id,
            categoryName: row.categoryName ?? UNCATEGORIZED_CATEGORY.name,
            categoryColor: row.categoryColor ?? UNCATEGORIZED_CATEGORY.color,
            categoryIcon: row.categoryIcon ?? UNCATEGORIZED_CATEGORY.icon,
            total: row.total,
        })), invoiceRows[0]?.total ?? 0);
    }

    // Parcelamento pelo valor total: só em leitura analítica de despesa fora do
    // caixa — a âncora soma o grupo e as demais parcelas saem do SUM.
    const installmentsFullValue = (options?.installmentsFullValue ?? false)
        && type === 'expense'
        && mode !== 'cash-flow';
    const amountExpr = installmentsFullValue ? installmentFullValueAmount() : transactions.amount;

    const rows = await db
        .select({
            categoryId: transactions.categoryId,
            categoryName: categories.name,
            categoryColor: categories.color,
            categoryIcon: categories.icon,
            total: sql<number>`COALESCE(SUM(${amountExpr}), 0)`.as('total'),
        })
        .from(transactions)
        .leftJoin(categories, eq(transactions.categoryId, categories.id))
        .where(
            and(
                eq(transactions.type, type),
                gte(transactions.date, startDate),
                lte(transactions.date, endDate),
                isNull(transactions.deletedAt),
                mode === 'cash-flow'
                    ? type === 'income'
                        ? cashFlowIncomeScope()
                        : cashFlowExpenseScope()
                    : excludeNonAnalyticInvoiceAdjustments(),
                excludePersonalLoanPrincipal(),
                excludeIgnored(),
                excludeDeletedCardTransactions(),
                ...(mode !== 'cash-flow' ? [excludeHiddenAccountTransactions()] : []),
                ...(installmentsFullValue ? [onlyInstallmentAnchor()] : []),
            ),
        )
        .groupBy(transactions.categoryId)
        .orderBy(sql`total DESC`);

    return rows.map((r) => ({
        categoryId: r.categoryId ?? UNCATEGORIZED_CATEGORY.id,
        categoryName: r.categoryName ?? UNCATEGORIZED_CATEGORY.name,
        categoryColor: r.categoryColor ?? UNCATEGORIZED_CATEGORY.color,
        categoryIcon: r.categoryIcon ?? UNCATEGORIZED_CATEGORY.icon,
        total: r.total,
    }));
}

export interface CategoryNetRow {
    categoryId: string;
    categoryName: string;
    categoryColor: string;
    categoryIcon: string;
    /** Entradas: receitas + estornos (despesas negativas) da categoria. */
    inflow: number;
    /** Saídas: despesas brutas (positivas) da categoria. */
    outflow: number;
    /** Resultado líquido = inflow − outflow. */
    net: number;
}

/**
 * Resultado líquido por categoria (mini-DRE): por categoria, soma entradas
 * (receitas + estornos) e saídas (despesas brutas) no período e o saldo líquido.
 * Usa o mesmo escopo analítico do relatório por categoria (estornos abatem; ver
 * lib/refunds e a Fase 1 da devolução).
 */
export async function getCategoryNetReport(
    startDate: string,
    endDate: string,
    options?: AnalyticReadOptions,
): Promise<CategoryNetRow[]> {
    const installmentsFullValue = options?.installmentsFullValue ?? false;
    // Parcelamento pelo valor total: a âncora carrega a soma do grupo (estornos
    // de parcela inclusos — o sinal do GRUPO decide entrada × saída).
    const amountExpr = installmentsFullValue ? installmentFullValueAmount() : transactions.amount;

    const rows = await db
        .select({
            categoryId: transactions.categoryId,
            categoryName: categories.name,
            categoryColor: categories.color,
            categoryIcon: categories.icon,
            outflow: sql<number>`COALESCE(SUM(CASE WHEN ${transactions.type} = 'expense' AND ${amountExpr} > 0 THEN ${amountExpr} ELSE 0 END), 0)`.as('outflow'),
            inflow: sql<number>`COALESCE(SUM(CASE WHEN ${transactions.type} = 'income' THEN ${amountExpr} WHEN ${transactions.type} = 'expense' AND ${amountExpr} < 0 THEN -${amountExpr} ELSE 0 END), 0)`.as('inflow'),
        })
        .from(transactions)
        .leftJoin(categories, eq(transactions.categoryId, categories.id))
        .where(
            and(
                or(eq(transactions.type, 'income'), eq(transactions.type, 'expense')),
                gte(transactions.date, startDate),
                lte(transactions.date, endDate),
                isNull(transactions.deletedAt),
                excludeNonAnalyticInvoiceAdjustments(),
                excludePersonalLoanPrincipal(),
                excludeIgnored(),
                excludeDeletedCardTransactions(),
                ...(installmentsFullValue ? [onlyInstallmentAnchor()] : []),
            ),
        )
        .groupBy(transactions.categoryId);

    return rows
        .map((row) => {
            const inflow = row.inflow ?? 0;
            const outflow = row.outflow ?? 0;
            return {
                categoryId: row.categoryId ?? UNCATEGORIZED_CATEGORY.id,
                categoryName: row.categoryName ?? UNCATEGORIZED_CATEGORY.name,
                categoryColor: row.categoryColor ?? UNCATEGORIZED_CATEGORY.color,
                categoryIcon: row.categoryIcon ?? UNCATEGORIZED_CATEGORY.icon,
                inflow,
                outflow,
                net: inflow - outflow,
            };
        })
        .filter((row) => row.inflow !== 0 || row.outflow !== 0)
        .sort((left, right) => Math.abs(right.net) - Math.abs(left.net));
}

export interface FixedVariableBreakdown {
    fixed: number;
    variable: number;
}

/**
 * Despesas fixas (recorrentes/assinaturas) vs variáveis (avulsas) no período.
 * "Fixa" = transação recorrente (isRecurring) ou ligada a uma recorrência.
 */
export async function getFixedVsVariableExpenses(
    startDate: string,
    endDate: string,
    options?: AnalyticReadOptions,
): Promise<FixedVariableBreakdown> {
    const installmentsFullValue = options?.installmentsFullValue ?? false;
    const amountExpr = installmentsFullValue ? installmentFullValueAmount() : transactions.amount;
    // Parcela regular de financiamento é despesa fixa por natureza (compromisso
    // mensal contratado), mesmo sem vínculo com recorrência. Abatimento extra é
    // pontual e segue como variável.
    // Pagamento parcial entra como fixo junto com a parcela: é a MESMA obrigação
    // contratual, só quebrada em dois lançamentos — deixá-lo de fora jogaria parte da
    // parcela no balde de variável e subestimaria o compromisso fixo (ADR 0055).
    const isFixedExpense = sql`(${transactions.isRecurring} = 1 OR ${transactions.recurringId} IS NOT NULL OR IFNULL(${transactions.financingEntryType}, '') IN (${FinancingEntryType.REGULAR_INSTALLMENT}, ${FinancingEntryType.PARTIAL_PAYMENT}))`;
    const rows = await db
        .select({
            fixed: sql<number>`COALESCE(SUM(CASE WHEN ${isFixedExpense} THEN ${amountExpr} ELSE 0 END), 0)`.as('fixed'),
            variable: sql<number>`COALESCE(SUM(CASE WHEN NOT ${isFixedExpense} THEN ${amountExpr} ELSE 0 END), 0)`.as('variable'),
        })
        .from(transactions)
        .where(
            and(
                eq(transactions.type, 'expense'),
                gte(transactions.date, startDate),
                lte(transactions.date, endDate),
                isNull(transactions.deletedAt),
                excludeNonAnalyticInvoiceAdjustments(),
                excludePersonalLoanPrincipal(),
                excludeIgnored(),
                excludeDeletedCardTransactions(),
                ...(installmentsFullValue ? [onlyInstallmentAnchor()] : []),
            ),
        );

    return { fixed: rows[0]?.fixed ?? 0, variable: rows[0]?.variable ?? 0 };
}

export async function getNewExpenseByCategory(
    startDate: string,
    endDate: string,
): Promise<CategorySpending[]> {
    const newTransactions = await getNewExpenseTransactionsForRange(startDate, endDate);

    const totalsByCategory = new Map<string, CategorySpending>();

    for (const transaction of newTransactions) {
        addCategorySpending(totalsByCategory, transaction, transaction.amount);
    }

    return Array.from(totalsByCategory.values()).sort((left, right) => right.total - left.total);
}

/**
 * As queries de regra dos breakdowns abaixo não juntam subcategories: a ocorrência
 * projetada chega com subcategoryId e sem o nome. Resolve os nomes das entradas que
 * ficaram com a sentinela apesar de terem subcategoria real. (A lista, `getTransactions`,
 * e `getTransactionById` já juntam desde a 1.0.68 — Denis, 22/09/2026.)
 */
async function resolveMissingSubcategoryNames(totals: Map<string, SubcategorySpending>) {
    const unresolvedIds = Array.from(totals.values())
        .filter((entry) => entry.subcategoryId !== UNCATEGORIZED_SUBCATEGORY.id
            && entry.subcategoryName === UNCATEGORIZED_SUBCATEGORY.name)
        .map((entry) => entry.subcategoryId);
    if (unresolvedIds.length === 0) return;

    const nameRows = await db
        .select({ id: subcategories.id, name: subcategories.name })
        .from(subcategories)
        .where(inArray(subcategories.id, unresolvedIds));
    const nameById = new Map(nameRows.map((row) => [row.id, row.name]));
    for (const entry of totals.values()) {
        const resolved = nameById.get(entry.subcategoryId);
        if (resolved) entry.subcategoryName = resolved;
    }
}

/**
 * Quebra por subcategoria DENTRO de uma categoria, no mesmo regime da aba
 * Análise: 'accrual' = compromissos do mês (despesa ou receita), 'new' = novos
 * gastos (só despesa). O resto sem subcategoria entra como
 * UNCATEGORIZED_SUBCATEGORY. Espelha a MESMA fonte do total da categoria
 * (getSpendingByCategoryWithRecurring no accrual, getNewExpenseByCategory no
 * new), só que agrupado por subcategoria e preso a uma categoria — então as
 * subcategorias fecham com o total da categoria, inclusive recorrência
 * projetada e composição de fatura. Ver docs/IDEAS.md #11.
 */
export async function getSpendingBySubcategory(
    categoryId: string,
    startDate: string,
    endDate: string,
    mode: 'accrual' | 'new' = 'accrual',
    type: 'expense' | 'income' = 'expense',
    options?: AnalyticReadOptions,
): Promise<SubcategorySpending[]> {
    if (type === 'income') {
        // Espelha a fonte do total de receita da categoria (getSpendingByCategoryWithRecurring
        // income accrual): lançamentos por data + recorrências de receita projetadas.
        const rows = await db
            .select({
                categoryId: transactions.categoryId,
                subcategoryId: transactions.subcategoryId,
                subcategoryName: subcategories.name,
                amount: transactions.amount,
            })
            .from(transactions)
            .leftJoin(subcategories, eq(transactions.subcategoryId, subcategories.id))
            .where(
                and(
                    eq(transactions.type, 'income'),
                    gte(transactions.date, startDate),
                    lte(transactions.date, endDate),
                    isNull(transactions.deletedAt),
                    excludeNonAnalyticInvoiceAdjustments(),
                    excludePersonalLoanPrincipal(),
                    excludeIgnored(),
                    excludeDeletedCardTransactions(),
                    excludeHiddenAccountTransactions(),
                ),
            );

        const totals = new Map<string, SubcategorySpending>();
        for (const row of rows) {
            if ((row.categoryId ?? UNCATEGORIZED_CATEGORY.id) !== categoryId) continue;
            addSubcategorySpending(totals, row, row.amount);
        }

        const incomeRecurringRules = await db
            .select({
                id: recurringTransactions.id,
                type: recurringTransactions.type,
                amount: recurringTransactions.amount,
                isIgnored: recurringTransactions.isIgnored,
                frequency: recurringTransactions.frequency,
                interval: recurringTransactions.interval,
                weekdays: recurringTransactions.weekdays,
                monthlyMode: recurringTransactions.monthlyMode,
                startDate: recurringTransactions.startDate,
                nextOccurrence: recurringTransactions.nextOccurrence,
                endDate: recurringTransactions.endDate,
                skippedDates: recurringTransactions.skippedDates,
                categoryId: recurringTransactions.categoryId,
                subcategoryId: recurringTransactions.subcategoryId,
                creditCardId: recurringTransactions.creditCardId,
            })
            .from(recurringTransactions)
            .where(
                and(
                    eq(recurringTransactions.type, 'income'),
                    eq(recurringTransactions.isActive, 1),
                    isNull(recurringTransactions.deletedAt),
                    lte(recurringTransactions.nextOccurrence, endDate),
                    sql`(${recurringTransactions.endDate} IS NULL OR ${recurringTransactions.endDate} >= ${startDate})`,
                    sql`${recurringTransactions.creditCardId} IS NULL`,
                    excludeHiddenAccountRecurringRules(),
                    excludeIgnoredRecurringRules(),
                ),
            );

        // Dedup pelo slot agendado, mesma chave da projeção por categoria.
        const occurrenceSlot = sql<string>`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date})`;
        const materializedOccurrences = await db
            .select({ recurringId: transactions.recurringId, slot: occurrenceSlot })
            .from(transactions)
            .where(
                and(
                    isNull(transactions.deletedAt),
                    gte(occurrenceSlot, startDate),
                    lte(occurrenceSlot, endDate),
                    sql`${transactions.recurringId} IS NOT NULL`,
                ),
            );
        const materializedKeys = new Set(
            materializedOccurrences.map((row) => `${row.recurringId}:${row.slot}`),
        );

        const projectedOccurrences = buildProjectedRecurringTransactions(
            incomeRecurringRules,
            startDate,
            endDate,
            materializedKeys,
        );
        for (const occurrence of projectedOccurrences) {
            if ((occurrence.categoryId ?? UNCATEGORIZED_CATEGORY.id) !== categoryId) continue;
            addSubcategorySpending(totals, occurrence, occurrence.amount);
        }

        await resolveMissingSubcategoryNames(totals);

        return Array.from(totals.values()).sort((left, right) => right.total - left.total);
    }

    if (mode === 'new') {
        const newTransactions = await getNewExpenseTransactionsForRange(startDate, endDate);
        const totals = new Map<string, SubcategorySpending>();
        for (const transaction of newTransactions) {
            const txCategoryId = transaction.categoryId ?? UNCATEGORIZED_CATEGORY.id;
            if (txCategoryId !== categoryId) continue;
            addSubcategorySpending(totals, transaction, transaction.amount);
        }
        return Array.from(totals.values()).sort((left, right) => right.total - left.total);
    }

    // Accrual: espelha EXATAMENTE a fonte do total da categoria
    // (getSpendingByCategoryWithRecurring → getAccrualExpenseTransactionsForRange +
    // getAccrualExpenseAmount), só que filtrado a uma categoria e agrupado por
    // subcategoria. Assim a soma das subcategorias fecha com o total da categoria,
    // incluindo recorrência projetada e composição de fatura (compras com data fora
    // do período cuja fatura vence no período).
    const accrualTransactions = await getAccrualExpenseTransactionsForRange(startDate, endDate, {
        includeProjectedRecurring: true,
        installmentsFullValue: options?.installmentsFullValue,
        invoicesByClosingMonth: options?.invoicesByClosingMonth,
    });
    const totals = new Map<string, SubcategorySpending>();
    for (const transaction of accrualTransactions) {
        const txCategoryId = transaction.categoryId ?? UNCATEGORIZED_CATEGORY.id;
        if (txCategoryId !== categoryId) continue;
        addSubcategorySpending(totals, transaction, getAccrualExpenseAmount(transaction));
    }

    await resolveMissingSubcategoryNames(totals);

    return Array.from(totals.values()).sort((left, right) => right.total - left.total);
}

/**
 * Spending by category augmented with projected non-materialized recurring
 * transactions and invoice composition for purchases whose dates are outside
 * the requested range (accrual mode only).
 */
export async function getSpendingByCategoryWithRecurring(
    startDate: string,
    endDate: string,
    type: 'expense' | 'income' = 'expense',
    mode: AnalyticsMode = 'accrual',
    options?: AnalyticReadOptions,
): Promise<CategorySpending[]> {
    if (type === 'expense' && mode === 'accrual') {
        const transactions = await getAccrualExpenseTransactionsForRange(startDate, endDate, {
            includeProjectedRecurring: true,
            installmentsFullValue: options?.installmentsFullValue,
            invoicesByClosingMonth: options?.invoicesByClosingMonth,
        });
        const totalsByCategory = new Map<string, CategorySpending>();

        for (const transaction of transactions) {
            const amount = getAccrualExpenseAmount(transaction);
            addCategorySpending(totalsByCategory, transaction, amount);
        }

        return Array.from(totalsByCategory.values()).sort((left, right) => right.total - left.total);
    }

    const baseTotals = await getSpendingByCategory(startDate, endDate, type, mode, options);

    // Receita em caixa não projeta nada; receita em COMPETÊNCIA segue adiante e ganha
    // as recorrências projetadas (ex.: salário ainda não lançado), pra pizza de
    // receitas fechar com o card "Receitas" do Balanço (getMonthlyTotalsWithRecurring).
    if (type !== 'expense' && mode !== 'accrual') return baseTotals;

    // --- Projected recurring transactions by category ---
    const catRecurringConditions = [
        eq(recurringTransactions.isActive, 1),
        isNull(recurringTransactions.deletedAt),
        eq(recurringTransactions.type, type),
        lte(recurringTransactions.nextOccurrence, endDate),
        sql`(${recurringTransactions.endDate} IS NULL OR ${recurringTransactions.endDate} >= ${startDate})`,
        // Pizza por categoria é análise: série ignorada fora, como a linha real (ADR 0169).
        excludeIgnoredRecurringRules(),
    ];
    // In accrual mode, CC-linked recurring expenses are captured by the invoice
    // composition query instead. Including them here would double-count.
    if (mode === 'accrual') {
        catRecurringConditions.push(sql`${recurringTransactions.creditCardId} IS NULL`);
        catRecurringConditions.push(excludeHiddenAccountRecurringRules());
    }

    const recurringRows = await db
        .select({
            id: recurringTransactions.id,
            type: recurringTransactions.type,
            amount: recurringTransactions.amount,
            isIgnored: recurringTransactions.isIgnored,
            frequency: recurringTransactions.frequency,
            interval: recurringTransactions.interval,
            weekdays: recurringTransactions.weekdays,
            monthlyMode: recurringTransactions.monthlyMode,
            startDate: recurringTransactions.startDate,
            nextOccurrence: recurringTransactions.nextOccurrence,
            endDate: recurringTransactions.endDate,
            skippedDates: recurringTransactions.skippedDates,
            categoryId: recurringTransactions.categoryId,
            creditCardId: recurringTransactions.creditCardId,
            categoryName: categories.name,
            categoryColor: categories.color,
            categoryIcon: categories.icon,
        })
        .from(recurringTransactions)
        .leftJoin(categories, eq(recurringTransactions.categoryId, categories.id))
        .where(and(...catRecurringConditions));

    // Dedup pelo slot agendado (COALESCE recurringOccurrenceDate, date), não pela data real:
    // a data é editável e furaria a chave da projeção, inflando a categoria em 1 ocorrência.
    const occurrenceSlot = sql<string>`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date})`;
    const materializedOccurrences = await db
        .select({
            recurringId: transactions.recurringId,
            slot: occurrenceSlot,
        })
        .from(transactions)
        .where(
            and(
                isNull(transactions.deletedAt),
                gte(occurrenceSlot, startDate),
                lte(occurrenceSlot, endDate),
                sql`${transactions.recurringId} IS NOT NULL`,
            ),
        );

    const materializedKeys = new Set(
        materializedOccurrences.map((row) => `${row.recurringId}:${row.slot}`),
    );

    // Accumulate projected recurring by category
    const projectedByCategory = new Map<string, {
        categoryId: string;
        categoryName: string;
        categoryColor: string;
        categoryIcon: string;
        total: number;
    }>();

    const projectedOccurrences = buildProjectedRecurringTransactions(
        recurringRows,
        startDate,
        endDate,
        materializedKeys,
    );
    for (const occurrence of projectedOccurrences) {
        addCategorySpending(projectedByCategory, occurrence, occurrence.amount);
    }

    // --- Invoice composition for purchases outside the date range (despesa apenas) ---
    if (type === 'expense' && mode === 'accrual') {
        // Get invoices with dueDate in the period
        const invoiceRows = await db
            .select({
                id: invoices.id,
                totalAmount: invoices.totalAmount,
                paidAmount: invoices.paidAmount,
            })
            .from(invoices)
            .innerJoin(creditCards, eq(invoices.creditCardId, creditCards.id))
            .where(
                and(
                    gte(invoices.dueDate, startDate),
                    lte(invoices.dueDate, endDate),
                    sql`${invoices.totalAmount} > ${invoices.paidAmount}`,
                    eq(creditCards.isArchived, 0),
                    isNull(creditCards.deletedAt),
                ),
            );

        if (invoiceRows.length > 0) {
            const invoiceIds = invoiceRows.map((inv) => inv.id);

            // Get individual CC purchases belonging to these invoices that are
            // OUTSIDE the current date range (not already counted by base query)
            const missingPurchases = await db
                .select({
                    categoryId: transactions.categoryId,
                    categoryName: categories.name,
                    categoryColor: categories.color,
                    categoryIcon: categories.icon,
                    total: sql<number>`COALESCE(SUM(${transactions.amount}), 0)`.as('total'),
                })
                .from(transactions)
                .leftJoin(categories, eq(transactions.categoryId, categories.id))
                .where(
                    and(
                        inArray(transactions.invoiceId, invoiceIds),
                        isNull(transactions.deletedAt),
                        eq(transactions.type, 'expense'),
                        sql`${transactions.adjustmentType} IS NULL`,
                        sql`(${transactions.date} < ${startDate} OR ${transactions.date} > ${endDate})`,
                        excludeIgnored(),
                        excludeDeletedCardTransactions(),
                    ),
                )
                .groupBy(transactions.categoryId);

            for (const row of missingPurchases) {
                addCategorySpending(projectedByCategory, row, row.total);
            }

            // Also add invoice adjustments (manual entries, remainders) that have categories
            // and belong to these invoices — they contribute to the total but are excluded
            // by the base accrual query since they have non-null adjustmentType
            const adjustmentPurchases = await db
                .select({
                    categoryId: transactions.categoryId,
                    categoryName: categories.name,
                    categoryColor: categories.color,
                    categoryIcon: categories.icon,
                    total: sql<number>`COALESCE(SUM(${transactions.amount}), 0)`.as('total'),
                })
                .from(transactions)
                .leftJoin(categories, eq(transactions.categoryId, categories.id))
                .where(
                    and(
                        inArray(transactions.invoiceId, invoiceIds),
                        isNull(transactions.deletedAt),
                        eq(transactions.type, 'expense'),
                        sql`${transactions.adjustmentType} IS NOT NULL`,
                        excludeNonAnalyticInvoiceAdjustments(),
                    ),
                )
                .groupBy(transactions.categoryId);

            for (const row of adjustmentPurchases) {
                addCategorySpending(projectedByCategory, row, row.total);
            }
        }
    }

    // Merge projected categories into base totals
    if (projectedByCategory.size === 0) return baseTotals;

    const baseMap = new Map(baseTotals.map((item) => [item.categoryId, item]));
    for (const [categoryId, projected] of projectedByCategory) {
        const base = baseMap.get(categoryId);
        if (base) {
            base.total += projected.total;
        } else {
            baseMap.set(categoryId, projected);
        }
    }

    return Array.from(baseMap.values()).sort((a, b) => b.total - a.total);
}

export type { MonthlyTotal };

export async function getMonthlyTotals(
    startMonth: string,
    endMonth: string,
    mode: AnalyticsMode = 'accrual',
    options?: AnalyticReadOptions,
): Promise<MonthlyTotal[]> {
    if (mode === 'accrual') {
        return getAccrualMonthlyTotals(startMonth, endMonth, false, options);
    }

    // Início do mês (ADR 0190): a chave do período em SQL — com dia 1 é o substr de sempre.
    const monthKey = monthKeySql(transactions.date, options?.monthStartDay ?? getMonthStartDay());
    const rows = await db
        .select({
            month: monthKey.as('month'),
            income: sql<number>`COALESCE(SUM(CASE
                WHEN ${transactions.type} = 'income'
                 AND ${mode === 'cash-flow' ? cashFlowIncomeScope() : sql`1 = 1`}
                THEN ${transactions.amount}
                ELSE 0
            END), 0)`.as('income'),
            expense: sql<number>`COALESCE(SUM(CASE
                WHEN ${transactions.type} = 'expense'
                 AND ${mode === 'cash-flow' ? cashFlowExpenseScope() : sql`1 = 1`}
                THEN ${transactions.amount}
                ELSE 0
            END), 0)`.as('expense'),
        })
        .from(transactions)
        .where(
            and(
                sql`${monthKey} >= ${startMonth}`,
                sql`${monthKey} <= ${endMonth}`,
                isNull(transactions.deletedAt),
                mode === 'cash-flow'
                    ? sql`1 = 1`
                    : excludeNonAnalyticInvoiceAdjustments(),
                excludeIgnored(),
                excludeDeletedCardTransactions(),
            ),
        )
        .groupBy(monthKey)
        .orderBy(sql`month ASC`);

    return rows;
}

export interface CashFlowMonthlyTotal {
    month: string;
    /** Confirmadas — dinheiro que de fato entrou/saiu. */
    income: number;
    expense: number;
    /** Pendentes (lançadas e sem baixa, incl. recorrências materializadas e vencidas). */
    pendingIncome: number;
    pendingExpense: number;
}

/**
 * Totais mensais em regime de CAIXA com corte por DIA (não por mês cheio) — usado pelo
 * relatório Fluxo de caixa com período personalizado (nem todo mundo fecha o mês no dia 1º).
 * Mesmo escopo do modo 'cash-flow' de getMonthlyTotals, mas separa o REALIZADO (confirmadas)
 * do EM ABERTO (pendentes): pendente ainda não saiu do caixa e não pode somar no realizado.
 */
export async function getCashFlowMonthlyTotalsInRange(
    startDate: string,
    endDate: string,
    options?: { monthStartDay?: number },
): Promise<CashFlowMonthlyTotal[]> {
    // Início do mês (ADR 0190): agrupa pela chave do período, não pelo mês-calendário.
    const monthKey = monthKeySql(transactions.date, options?.monthStartDay ?? getMonthStartDay());
    return db
        .select({
            month: monthKey.as('month'),
            income: sql<number>`COALESCE(SUM(CASE
                WHEN ${transactions.type} = 'income'
                 AND ${transactions.isConfirmed} = 1
                 AND ${cashFlowIncomeScope()}
                THEN ${transactions.amount}
                ELSE 0
            END), 0)`.as('income'),
            expense: sql<number>`COALESCE(SUM(CASE
                WHEN ${transactions.type} = 'expense'
                 AND ${transactions.isConfirmed} = 1
                 AND ${cashFlowExpenseScope()}
                THEN ${transactions.amount}
                ELSE 0
            END), 0)`.as('expense'),
            pendingIncome: sql<number>`COALESCE(SUM(CASE
                WHEN ${transactions.type} = 'income'
                 AND ${transactions.isConfirmed} = 0
                 AND ${cashFlowIncomeScope()}
                THEN ${transactions.amount}
                ELSE 0
            END), 0)`.as('pendingIncome'),
            pendingExpense: sql<number>`COALESCE(SUM(CASE
                WHEN ${transactions.type} = 'expense'
                 AND ${transactions.isConfirmed} = 0
                 AND ${cashFlowExpenseScope()}
                THEN ${transactions.amount}
                ELSE 0
            END), 0)`.as('pendingExpense'),
        })
        .from(transactions)
        .where(
            and(
                gte(transactions.date, startDate),
                lte(transactions.date, endDate),
                isNull(transactions.deletedAt),
                excludeIgnored(),
                excludeDeletedCardTransactions(),
            ),
        )
        .groupBy(monthKey)
        .orderBy(sql`month ASC`);
}

/**
 * Monthly totals augmented with projected non-materialized recurring transactions
 * and unpaid invoice deltas (for accrual mode).
 * Ensures future months show realistic forecasts in analysis charts.
 */
export async function getMonthlyTotalsWithRecurring(
    startMonth: string,
    endMonth: string,
    mode: AnalyticsMode = 'accrual',
    options?: AnalyticReadOptions,
): Promise<MonthlyTotal[]> {
    if (mode === 'accrual') {
        return getAccrualMonthlyTotals(startMonth, endMonth, true, options);
    }

    const baseTotals = await getMonthlyTotals(startMonth, endMonth, mode, options);

    const monthStartDay = options?.monthStartDay ?? getMonthStartDay();
    const { startDate } = getMonthDateRange(startMonth, monthStartDay);
    const { endDate } = getMonthDateRange(endMonth, monthStartDay);

    const recurringConditions = [
        eq(recurringTransactions.isActive, 1),
        isNull(recurringTransactions.deletedAt),
        lte(recurringTransactions.nextOccurrence, endDate),
        sql`(${recurringTransactions.endDate} IS NULL OR ${recurringTransactions.endDate} >= ${startDate})`,
        sql`${recurringTransactions.creditCardId} IS NULL`, // Credit card recurring items are handled by invoice deltas
        // Balanço do mês é análise: série ignorada fora, como a linha real (ADR 0169).
        excludeIgnoredRecurringRules(),
    ];

    const recurringRows = await db
        .select({
            id: recurringTransactions.id,
            type: recurringTransactions.type,
            amount: recurringTransactions.amount,
            isIgnored: recurringTransactions.isIgnored,
            frequency: recurringTransactions.frequency,
            interval: recurringTransactions.interval,
            weekdays: recurringTransactions.weekdays,
            monthlyMode: recurringTransactions.monthlyMode,
            startDate: recurringTransactions.startDate,
            nextOccurrence: recurringTransactions.nextOccurrence,
            endDate: recurringTransactions.endDate,
            skippedDates: recurringTransactions.skippedDates,
        })
        .from(recurringTransactions)
        .where(and(...recurringConditions));

    // Dedup pelo slot agendado (COALESCE recurringOccurrenceDate, date), não pela data real:
    // a data é editável e furaria a chave da projeção, contando a ocorrência 2x no mês.
    const occurrenceSlot = sql<string>`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date})`;
    const materializedOccurrences = await db
        .select({
            recurringId: transactions.recurringId,
            slot: occurrenceSlot,
        })
        .from(transactions)
        .where(
            and(
                isNull(transactions.deletedAt),
                gte(occurrenceSlot, startDate),
                lte(occurrenceSlot, endDate),
                sql`${transactions.recurringId} IS NOT NULL`,
            ),
        );

    const materializedKeys = new Set(
        materializedOccurrences.map((row) => `${row.recurringId}:${row.slot}`),
    );

    const projectedOccurrences = buildProjectedRecurringTransactions(
        recurringRows,
        startDate,
        endDate,
        materializedKeys,
    );

    return mergeProjectedMonthlyTotals(
        baseTotals,
        projectedOccurrences,
        startMonth,
        endMonth,
        (date) => monthKeyForDate(date, monthStartDay),
    );
}

export interface FrequentTransfer {
    sourceAccountId: string;
    destinationAccountId: string;
    count: number;
}

export async function getFrequentTransfers(accountId?: string): Promise<FrequentTransfer[]> {
    const conditions = [
        eq(transactions.type, 'transfer'),
        isNull(transactions.deletedAt),
        sql`${transactions.accountId} IS NOT NULL`,
        sql`${transactions.transferDestinationAccountId} IS NOT NULL`,
    ];

    if (accountId) {
        conditions.push(
            sql`(${transactions.accountId} = ${accountId} OR ${transactions.transferDestinationAccountId} = ${accountId})`,
        );
    }

    const rows = await db
        .select({
            sourceAccountId: transactions.accountId,
            destinationAccountId: transactions.transferDestinationAccountId,
            count: sql<number>`COUNT(*)`.as('count'),
        })
        .from(transactions)
        .where(and(...conditions))
        .groupBy(transactions.accountId, transactions.transferDestinationAccountId)
        .orderBy(sql`count DESC`)
        .limit(5);

    return rows.map((r) => ({
        sourceAccountId: r.sourceAccountId!,
        destinationAccountId: r.destinationAccountId!,
        count: r.count,
    }));
}

export async function createTransfer(data: {
    amount: number;
    description: string;
    date: string;
    time?: string;
    sourceAccountId: string;
    destinationAccountId: string;
    notes?: string;
    isConfirmed?: boolean;
}): Promise<Transaction> {
    return createTransaction({
        type: 'transfer',
        amount: data.amount,
        description: data.description || 'Transferencia',
        date: data.date,
        time: data.time,
        notes: data.notes,
        accountId: data.sourceAccountId,
        transferDestinationAccountId: data.destinationAccountId,
        isConfirmed: data.isConfirmed ?? true,
    });
}

/**
 * Uma despesa ou receita avulsa pode virar transferência (ADR 0158). O que barra: cartão,
 * série (parcela ou recorrência), financiamento, empréstimo a pessoa, linha sintética de
 * fatura, depósito de meta — todos têm dono próprio e outro caminho de edição.
 */
function isConvertibleToTransfer(tx: typeof transactions.$inferSelect): boolean {
    return (tx.type === 'income' || tx.type === 'expense')
        && !!tx.accountId
        && !tx.creditCardId
        && !tx.installmentId
        && !tx.recurringId
        && tx.isRecurring !== 1
        && !tx.financingId
        && !tx.personalLoanId
        && !tx.adjustmentType
        && !tx.deletedAt;
}

export type TransferCounterpartCandidate = {
    id: string;
    date: string;
    description: string;
    amount: number;
    type: string;
    accountName: string | null;
};

/**
 * A OUTRA PERNA de uma transferência lançada como despesa + receita: o lançamento do tipo
 * oposto, do mesmo valor, na conta de lá, até 3 dias de distância (extrato de banco
 * diferente pode datar a chegada no dia seguinte). O mais próximo na data ganha.
 */
export async function findTransferCounterpart(
    id: string,
    counterpartAccountId: string,
): Promise<TransferCounterpartCandidate | null> {
    const [tx] = await db.select().from(transactions).where(eq(transactions.id, id));
    if (!tx || !isConvertibleToTransfer(tx)) return null;

    const oppositeType = tx.type === 'expense' ? 'income' : 'expense';
    const anchor = parseISO(tx.date);
    const from = format(subDays(anchor, 3), 'yyyy-MM-dd');
    const to = format(addDays(anchor, 3), 'yyyy-MM-dd');

    const rows = await db
        .select({
            id: transactions.id,
            date: transactions.date,
            description: transactions.description,
            amount: transactions.amount,
            type: transactions.type,
            accountName: accounts.name,
        })
        .from(transactions)
        .leftJoin(accounts, eq(transactions.accountId, accounts.id))
        .where(
            and(
                eq(transactions.accountId, counterpartAccountId),
                eq(transactions.type, oppositeType),
                isNull(transactions.deletedAt),
                isNull(transactions.creditCardId),
                isNull(transactions.installmentId),
                isNull(transactions.recurringId),
                isNull(transactions.financingId),
                isNull(transactions.personalLoanId),
                isNull(transactions.adjustmentType),
                gte(transactions.date, from),
                lte(transactions.date, to),
                sql`ABS(${transactions.amount} - ${tx.amount}) < 0.005`,
                // Rendimento ligado a caixinha ativa não se apaga fora dela (ADR 0161): absorvê-lo
                // faria a conversão inteira falhar. Mesmo critério de `hasActiveReservaYield`.
                sql`NOT EXISTS (
                    SELECT 1 FROM reserva_movements m
                    JOIN reservas r ON r.id = m.reserva_id
                    JOIN accounts a ON a.id = r.account_id
                    WHERE m.transaction_id = ${transactions.id} AND m.type = 'yield'
                      AND r.deleted_at IS NULL AND r.is_archived = 0
                      AND a.deleted_at IS NULL AND a.is_archived = 0
                )`,
            ),
        )
        .orderBy(sql`ABS(julianday(${transactions.date}) - julianday(${tx.date}))`, transactions.date)
        .limit(1);

    return rows[0] ?? null;
}

/**
 * Despesa ou receita já lançada vira TRANSFERÊNCIA entre contas (ADR 0158).
 *
 * Caso que motivou (Newton Bastos, 25/09/2026): importou os extratos de setembro de bancos
 * diferentes, e o que era transferência entre as contas dele entrou como saída num banco e
 * entrada no outro — inflando despesa e receita. Não havia como converter o lançamento
 * salvo: o tipo é fixo na edição, e só a pendente de notificação virava transferência (ADR
 * 0045).
 *
 * A linha muda de tipo NO LUGAR (mesmo id, tags, data e valor) e passa a sair de
 * `sourceAccountId` pra `destinationAccountId` — a tela sugere a direção natural (despesa:
 * da conta dela pra escolhida; receita: da escolhida pra conta dela), mas a pessoa pode
 * inverter ou trocar as pontas (26/09/2026, tarde). Categoria e subcategoria saem
 * (transferência não tem) e "Ignorar" desliga (transferência já é neutra na análise). Com
 * `absorbTransactionId`, a outra perna — a entrada/saída correspondente na outra conta — é
 * excluída antes, senão a conta de lá contaria o dinheiro duas vezes: uma pela perna antiga,
 * outra pela transferência nova. Absorver só vale na direção natural: é nela que a perna de
 * lá tem o tipo oposto.
 */
export async function convertTransactionToTransfer(
    id: string,
    params: { sourceAccountId: string; destinationAccountId: string; absorbTransactionId?: string | null },
): Promise<void> {
    if (parseProjectedRecurringTransactionId(id)) {
        throw new ExpectedError(i18n.t('ui:errors.convertToTransferUnsupported'));
    }
    const [tx] = await db.select().from(transactions).where(eq(transactions.id, id));
    if (!tx || !isConvertibleToTransfer(tx)) {
        throw new ExpectedError(i18n.t('ui:errors.convertToTransferUnsupported'));
    }
    if (await isGoalDepositTransaction(id) || await hasActiveReservaYield([id])) {
        throw new ExpectedError(i18n.t('ui:errors.convertToTransferUnsupported'));
    }
    const { sourceAccountId, destinationAccountId } = params;
    if (sourceAccountId === destinationAccountId) {
        throw new ExpectedError(i18n.t('ui:errors.convertToTransferSameAccount'));
    }
    const liveAccounts = await db
        .select({ id: accounts.id })
        .from(accounts)
        .where(and(inArray(accounts.id, [sourceAccountId, destinationAccountId]), isNull(accounts.deletedAt)));
    if (liveAccounts.length !== 2) {
        throw new ExpectedError(i18n.t('ui:errors.accountGone'));
    }

    // Direção natural: a conta do lançamento fica no papel que o tipo dele já dizia. Só
    // nela existe "outra perna" com o tipo oposto na conta de lá.
    const natural = tx.type === 'expense' ? sourceAccountId === tx.accountId : destinationAccountId === tx.accountId;
    const otherAccountId = tx.type === 'expense' ? destinationAccountId : sourceAccountId;

    if (params.absorbTransactionId) {
        const [other] = await db.select().from(transactions).where(eq(transactions.id, params.absorbTransactionId));
        const oppositeType = tx.type === 'expense' ? 'income' : 'expense';
        const matches = natural
            && !!other
            && isConvertibleToTransfer(other)
            && other.type === oppositeType
            && other.accountId === otherAccountId
            && Math.abs(other.amount - tx.amount) < 0.005;
        if (!matches) {
            throw new ExpectedError(i18n.t('ui:errors.convertToTransferCounterpartMismatch'));
        }
        // Soft delete pelo caminho oficial: lixeira, saldo da conta de lá e lembretes.
        await deleteTransaction(other.id, 'single');
    }

    const previousAccountId = tx.accountId!;
    const now = new Date().toISOString();

    await db
        .update(transactions)
        .set({
            type: 'transfer',
            accountId: sourceAccountId,
            transferDestinationAccountId: destinationAccountId,
            categoryId: null,
            subcategoryId: null,
            isIgnored: 0,
            updatedAt: now,
        })
        .where(eq(transactions.id, id));

    await recalculateAccountBalance(sourceAccountId);
    await recalculateAccountBalance(destinationAccountId);
    // Pontas trocadas pra outras contas: a conta de onde o lançamento saiu também muda.
    if (previousAccountId !== sourceAccountId && previousAccountId !== destinationAccountId) {
        await recalculateAccountBalance(previousAccountId);
    }
}

export async function createBalanceAdjustment(
    accountId: string,
    newBalance: number,
    options?: { date?: string; description?: string },
): Promise<Transaction> {
    const account = await db
        .select({ currentBalance: accounts.currentBalance })
        .from(accounts)
        .where(eq(accounts.id, accountId));

    if (!account[0]) throw new Error('Account not found');

    const difference = newBalance - account[0].currentBalance;

    const id = generateId();
    const nowDate = new Date();
    const now = nowDate.toISOString();
    // Dia LOCAL (não UTC): `toISOString().slice(0,10)` joga o ajuste feito à noite
    // (ex.: 22h no BRT = 01h UTC) para o dia seguinte.
    const today = format(nowDate, 'yyyy-MM-dd');
    // Data retroativa opcional (fechamento de caixa depois da meia-noite): muda só o dia
    // em que o ajuste aparece — o delta é sempre contra o saldo ATUAL da conta.
    const date = options?.date || today;
    const description = options?.description?.trim() || 'Ajuste de saldo';

    await db.insert(transactions).values({
        id,
        type: 'adjustment',
        amount: difference,
        description,
        date,
        // Conferir o saldo é um acontecimento, não um agendamento: ajuste de HOJE
        // carimba a hora (retroativo, não). É o que desempata dois ajustes no mesmo dia.
        time: resolveTransactionTime(date),
        accountId,
        adjustmentType: 'account_balance',
        isConfirmed: 1,
        isRecurring: 0,
        isHiddenFromSubs: 0,
        createdAt: now,
        updatedAt: now,
    });

    await recalculateAccountBalance(accountId);

    const result = await db.select().from(transactions).where(eq(transactions.id, id));
    return result[0];
}

/**
 * Edita um Ajuste de saldo EXISTENTE no lugar (não empilha um novo, ao contrário da pill
 * "Ajustar saldo" que chama createBalanceAdjustment). Mantém a data original salvo se
 * `options.date` vier, e só recalcula o delta para que o saldo da conta passe a bater com
 * `newBalance`. Como o recálculo é aditivo (initialBalance + Σ efeitos; ver
 * recalculateAccountBalance), basta:
 *   newDelta = newBalance − (saldoAtual − deltaAntigo).
 * `options.description` (motivo): undefined não toca; string vazia volta ao rótulo padrão.
 * Ver ADR 0035. Só aceita adjustmentType='account_balance' — artefatos de fatura e depósito
 * de meta não são editáveis pela linha.
 */
export async function updateBalanceAdjustment(
    transactionId: string,
    newBalance: number,
    options?: { date?: string; description?: string },
): Promise<Transaction> {
    const existing = await db.select().from(transactions).where(eq(transactions.id, transactionId));
    const adjustment = existing[0];
    if (!adjustment) throw new Error('Adjustment not found');
    if (adjustment.adjustmentType !== 'account_balance' || !adjustment.accountId) {
        throw new Error('Not an account balance adjustment');
    }

    const accountId = adjustment.accountId;
    const account = await db
        .select({ currentBalance: accounts.currentBalance })
        .from(accounts)
        .where(eq(accounts.id, accountId));
    if (!account[0]) throw new Error('Account not found');

    // Saldo da conta sem o efeito desta linha; o novo delta é a diferença até o alvo.
    const balanceExcludingThis = account[0].currentBalance - adjustment.amount;
    const newDelta = newBalance - balanceExcludingThis;

    await db
        .update(transactions)
        .set({
            amount: newDelta,
            ...(options?.date ? { date: options.date } : {}),
            ...(options?.description !== undefined
                ? { description: options.description.trim() || 'Ajuste de saldo' }
                : {}),
            updatedAt: new Date().toISOString(),
        })
        .where(eq(transactions.id, transactionId));

    await recalculateAccountBalance(accountId);

    const result = await db.select().from(transactions).where(eq(transactions.id, transactionId));
    return result[0];
}

/**
 * Crédito RELATIVO de saldo: levanta o saldo da conta em exatamente `amount` (um Ajuste
 * de saldo positivo), sem depender de um saldo lido antes (ao contrário de
 * createBalanceAdjustment, que é set ABSOLUTO). Isso evita o clobber por cache stale: o
 * lançamento adiciona `amount` qualquer que seja o saldo no momento. Usado pelo top-up da
 * caixinha — "Completar e reservar" (ADR 0017). No-op em amount <= 0.
 */
export async function creditAccountBalanceAdjustment(
    accountId: string,
    amount: number,
    description = 'Ajuste de saldo',
): Promise<Transaction | null> {
    const delta = Math.round(amount * 100) / 100;
    if (delta <= 0) return null;

    const id = generateId();
    const nowDate = new Date();
    const now = nowDate.toISOString();
    // Dia LOCAL (não UTC) — ver createBalanceAdjustment.
    const today = format(nowDate, 'yyyy-MM-dd');

    await db.insert(transactions).values({
        id,
        type: 'adjustment',
        amount: delta,
        description,
        date: today,
        time: resolveTransactionTime(today),
        accountId,
        adjustmentType: 'account_balance',
        isConfirmed: 1,
        isRecurring: 0,
        isHiddenFromSubs: 0,
        createdAt: now,
        updatedAt: now,
    });

    await recalculateAccountBalance(accountId);

    const result = await db.select().from(transactions).where(eq(transactions.id, id));
    return result[0];
}

/**
 * Crédito de RENDIMENTO: registra os juros/CDI que uma caixinha rendeu como RECEITA na conta
 * (categoria Investimentos → Juros / Rendimentos) e credita o saldo. É dinheiro NOVO — por isso
 * entra como receita (≠ "Completar e reservar", que é Ajuste de saldo, e ≠ Guardar, que só
 * desloca o que já existe). A reserva do valor dentro da caixinha é feita à parte
 * (ver useRegistrarRendimento). Resolve a categoria por nome; se o usuário a apagou, lança a
 * receita sem categoria — o crédito do saldo é o que importa. No-op em amount <= 0.
 */
export async function creditAccountYield(
    accountId: string,
    amount: number,
    date: string,
    description = 'Rendimento',
    notes?: string,
): Promise<Transaction | null> {
    const delta = Math.round(amount * 100) / 100;
    if (delta <= 0) return null;

    const [cat] = await db
        .select({ id: categories.id })
        .from(categories)
        .where(and(
            eq(categories.name, 'Investimentos'),
            eq(categories.type, 'income'),
            isNull(categories.deletedAt),
        ));

    let subcategoryId: string | undefined;
    if (cat) {
        const [sub] = await db
            .select({ id: subcategories.id })
            .from(subcategories)
            .where(and(
                eq(subcategories.categoryId, cat.id),
                eq(subcategories.name, 'Juros / Rendimentos'),
                isNull(subcategories.deletedAt),
            ));
        subcategoryId = sub?.id;
    }

    return createTransaction({
        type: 'income',
        amount: delta,
        description,
        date,
        accountId,
        categoryId: cat?.id,
        subcategoryId,
        notes,
        isConfirmed: true,
        // O rendimento é da caixinha (ADR 0164): entra no saldo da conta, mas fica fora das
        // receitas e dos relatórios, pelo mesmo predicado do "Ignorar". A caixinha é quem conta.
        isIgnored: true,
    });
}

/**
 * Apaga a receita de um rendimento de caixinha, pedido pela PRÓPRIA caixinha (o movimento já
 * saiu — ver deleteReservaMovement). Passa por fora da trava do deleteTransaction de propósito.
 * Soft-delete + saldo recalculado, como qualquer exclusão.
 */
export async function removeReservaYieldIncome(transactionId: string): Promise<void> {
    const [tx] = await db.select().from(transactions).where(eq(transactions.id, transactionId));
    if (!tx || tx.deletedAt) return;
    const now = new Date().toISOString();
    await db
        .update(transactions)
        .set({ deletedAt: now, updatedAt: now })
        .where(eq(transactions.id, transactionId));
    if (tx.accountId) await recalculateAccountBalance(tx.accountId);
}

// ─── Batch operations ────────────────────────────────────────

export async function batchUpdateCategory(ids: string[], categoryId: string, subcategoryId?: string | null): Promise<void> {
    if (ids.length === 0) return;
    const financingLinked = await db
        .select({ id: transactions.id })
        .from(transactions)
        .where(and(
            inArray(transactions.id, ids),
            isNull(transactions.deletedAt),
            sql`(${transactions.financingId} IS NOT NULL OR ${transactions.personalLoanId} IS NOT NULL)`,
        ));

    if (financingLinked.length > 0) {
        throw new ExpectedError(i18n.t('ui:errors.financingLoanEntriesManaged'));
    }

    await db
        .update(transactions)
        .set({ categoryId, subcategoryId: subcategoryId ?? null, updatedAt: new Date().toISOString() })
        .where(and(inArray(transactions.id, ids), isNull(transactions.deletedAt)));
}

export async function batchDelete(ids: string[]): Promise<void> {
    if (ids.length === 0) return;

    const goalLinked = await db
        .select({ id: goalDeposits.id })
        .from(goalDeposits)
        .where(inArray(goalDeposits.transactionId, ids))
        .limit(1);
    if (goalLinked.length > 0) {
        throw new ExpectedError(i18n.t('ui:errors.goalDepositsManagedInGoal'));
    }
    if (await hasActiveReservaYield(ids)) {
        throw new ExpectedError(i18n.t('ui:errors.reservaYieldManagedInReserva'));
    }

    const financingLinked = await db
        .select({ id: transactions.id })
        .from(transactions)
        .where(and(
            inArray(transactions.id, ids),
            isNull(transactions.deletedAt),
            sql`(${transactions.financingId} IS NOT NULL OR ${transactions.personalLoanId} IS NOT NULL)`,
        ));

    if (financingLinked.length > 0) {
        throw new ExpectedError(i18n.t('ui:errors.financingLoanEntriesManaged'));
    }

    // Mesma trava do deleteTransaction: a seleção do extrato deixava marcar a linha
    // "Pagamento fatura X" (Sentry REACT-NATIVE-2M) e o registro do pagamento ficava órfão.
    const invoicePaymentLinked = await db
        .select({ id: transactions.id })
        .from(transactions)
        .where(and(
            inArray(transactions.id, ids),
            isNull(transactions.deletedAt),
            eq(transactions.adjustmentType, AdjustmentType.INVOICE_PAYMENT),
        ))
        .limit(1);
    if (invoicePaymentLinked.length > 0) {
        throw new ExpectedError(i18n.t('ui:errors.invoicePaymentManagedInInvoice'));
    }

    const toDelete = await db.select().from(transactions).where(inArray(transactions.id, ids));

    await db
        .update(transactions)
        .set({ deletedAt: new Date().toISOString() })
        .where(and(inArray(transactions.id, ids), isNull(transactions.deletedAt)));

    const affectedAccounts = new Set<string>();
    const affectedInvoices = new Set<string>();
    for (const tx of toDelete) {
        if (tx.accountId) affectedAccounts.add(tx.accountId);
        if (tx.transferDestinationAccountId) affectedAccounts.add(tx.transferDestinationAccountId);
        if (tx.invoiceId) affectedInvoices.add(tx.invoiceId);
    }
    for (const accountId of affectedAccounts) await recalculateAccountBalance(accountId);
    for (const invoiceId of affectedInvoices) await recalculateInvoiceTotal(invoiceId);
}

/**
 * Aplica um DIFF de tags à seleção: `addTagIds` são somadas às tags existentes de cada
 * transação e `removeTagIds` são retiradas de todas — tags fora do diff ficam intactas.
 * É a semântica do BatchTagSheet: tag comum a todos vem pré-marcada, desmarcar = remover.
 */
export async function batchApplyTags(
    transactionIds: string[],
    addTagIds: string[],
    removeTagIds: string[],
): Promise<void> {
    const uniqueTransactionIds = [...new Set(transactionIds)];
    const uniqueAddIds = [...new Set(addTagIds)];
    const uniqueRemoveIds = [...new Set(removeTagIds)].filter((id) => !uniqueAddIds.includes(id));
    if (uniqueTransactionIds.length === 0 || (uniqueAddIds.length === 0 && uniqueRemoveIds.length === 0)) return;

    // Lotes de 400 (2 variáveis por linha/par) pra não estourar o limite de variáveis
    // do SQLite quando a seleção é grande.
    const CHUNK = 400;

    await runInTransaction(async () => {
        if (uniqueRemoveIds.length > 0) {
            for (let i = 0; i < uniqueTransactionIds.length; i += CHUNK) {
                await db.delete(transactionTags).where(and(
                    inArray(transactionTags.transactionId, uniqueTransactionIds.slice(i, i + CHUNK)),
                    inArray(transactionTags.tagId, uniqueRemoveIds),
                ));
            }
        }

        if (uniqueAddIds.length > 0) {
            const existing = await db
                .select({ transactionId: transactionTags.transactionId, tagId: transactionTags.tagId })
                .from(transactionTags)
                .where(and(
                    inArray(transactionTags.transactionId, uniqueTransactionIds),
                    inArray(transactionTags.tagId, uniqueAddIds),
                ));
            // Dedupe contra o banco E dentro do input: par repetido bateria no índice único
            // idx_transaction_tags_unique (migração 0019), abortando o lote inteiro.
            const existingSet = new Set(existing.map(e => `${e.transactionId}:${e.tagId}`));
            const toInsert: { transactionId: string; tagId: string }[] = [];
            for (const transactionId of uniqueTransactionIds) {
                for (const tagId of uniqueAddIds) {
                    if (!existingSet.has(`${transactionId}:${tagId}`)) {
                        toInsert.push({ transactionId, tagId });
                    }
                }
            }
            for (let i = 0; i < toInsert.length; i += CHUNK) {
                await db.insert(transactionTags).values(toInsert.slice(i, i + CHUNK));
            }
        }
    });
}

// ─── Reports queries ─────────────────────────────────────────

export interface CategoryMonthlySpending {
    categoryId: string;
    categoryName: string;
    categoryColor: string;
    categoryIcon: string;
    month: string;
    total: number;
}

export async function getSpendingByCategoryByMonth(
    startMonth: string,
    endMonth: string,
    options?: AnalyticReadOptions,
): Promise<CategoryMonthlySpending[]> {
    const installmentsFullValue = options?.installmentsFullValue ?? false;
    const amountExpr = installmentsFullValue ? installmentFullValueAmount() : transactions.amount;
    const monthKey = monthKeySql(transactions.date, options?.monthStartDay ?? getMonthStartDay());
    const rows = await db
        .select({
            categoryId: transactions.categoryId,
            categoryName: categories.name,
            categoryColor: categories.color,
            categoryIcon: categories.icon,
            month: monthKey.as('month'),
            total: sql<number>`COALESCE(SUM(${amountExpr}), 0)`.as('total'),
        })
        .from(transactions)
        .innerJoin(categories, eq(transactions.categoryId, categories.id))
        .where(
            and(
                eq(transactions.type, 'expense'),
                sql`${monthKey} >= ${startMonth}`,
                sql`${monthKey} <= ${endMonth}`,
                isNull(transactions.deletedAt),
                excludeNonAnalyticInvoiceAdjustments(),
                excludePersonalLoanPrincipal(),
                excludeIgnored(),
                excludeDeletedCardTransactions(),
                ...(installmentsFullValue ? [onlyInstallmentAnchor()] : []),
            ),
        )
        .groupBy(transactions.categoryId, monthKey)
        .orderBy(sql`total DESC`);

    return rows.map((r) => ({
        categoryId: r.categoryId!,
        categoryName: r.categoryName!,
        categoryColor: r.categoryColor!,
        categoryIcon: r.categoryIcon!,
        month: r.month,
        total: r.total,
    }));
}

export interface DayOfWeekSpending {
    dayOfWeek: number; // 0=Sun, 6=Sat
    total: number;
    count: number;
}

/**
 * `includeProjected` fica FORA do `AnalyticReadOptions` de propósito (ADR 0063): projeção
 * não deve ficar ao alcance de qualquer leitura analítica. Aqui é parâmetro próprio porque
 * os dois consumidores desta função têm contratos opostos:
 *
 * - **aba Análise** (default `true`): competência inclui Ocorrência projetada — é o contrato
 *   do verbete "No mês" no CONTEXT.md;
 * - **relatório Gastos por dia da semana** (passa `false`): recorrência tem dia fixo do MÊS,
 *   então projetá-la num gráfico por dia da SEMANA infla o dia em que ela calhou de cair,
 *   com dinheiro que ainda não saiu. Num período futuro sobra só recorrência, e os 7 baldes
 *   viram artefato puro.
 */
export async function getSpendingByDayOfWeek(
    startDate: string,
    endDate: string,
    mode: AnalyticsMode = 'accrual',
    options?: AnalyticReadOptions & { includeProjected?: boolean },
): Promise<DayOfWeekSpending[]> {
    if (mode === 'accrual') {
        const transactions = await getAccrualExpenseTransactionsForRange(startDate, endDate, {
            includeProjectedRecurring: options?.includeProjected ?? true,
            installmentsFullValue: options?.installmentsFullValue,
            invoicesByClosingMonth: options?.invoicesByClosingMonth,
        });
        const totalsByDay = new Map<number, DayOfWeekSpending>();

        for (const transaction of transactions) {
            const amount = getAccrualExpenseAmount(transaction);
            if (amount === 0) continue;

            const dayOfWeek = new Date(`${transaction.date}T12:00:00`).getDay();
            const entry = totalsByDay.get(dayOfWeek) ?? {
                dayOfWeek,
                total: 0,
                count: 0,
            };

            entry.total += amount;
            entry.count += 1;
            totalsByDay.set(dayOfWeek, entry);
        }

        return Array.from(totalsByDay.values()).sort((left, right) => left.dayOfWeek - right.dayOfWeek);
    }

    const rows = await db
        .select({
            dayOfWeek: sql<number>`CAST(strftime('%w', ${transactions.date}) AS INTEGER)`.as('dow'),
            total: sql<number>`COALESCE(SUM(${transactions.amount}), 0)`.as('total'),
            count: sql<number>`COUNT(*)`.as('count'),
        })
        .from(transactions)
        .where(
            and(
                eq(transactions.type, 'expense'),
                gte(transactions.date, startDate),
                lte(transactions.date, endDate),
                isNull(transactions.deletedAt),
                mode === 'cash-flow' ? cashFlowExpenseScope() : excludeNonAnalyticInvoiceAdjustments(),
                excludePersonalLoanPrincipal(),
                excludeIgnored(),
                excludeDeletedCardTransactions(),
            ),
        )
        .groupBy(sql`strftime('%w', ${transactions.date})`)
        .orderBy(sql`dow ASC`);

    return rows;
}

// ─── Spending by Day (ritmo de gasto da Análise) ────────────

export interface DailySpendingPoint {
    /** yyyy-MM-dd */
    day: string;
    total: number;
}

/**
 * Total de despesa por dia do range. Na competência, item de fatura ancora na
 * data-âncora da fatura (vencimento/fechamento) — o "degrau" do gráfico de ritmo
 * cai no dia em que o mês realmente assume o compromisso, não na data da compra
 * (que pode estar fora do range).
 */
export async function getSpendingByDay(
    startDate: string,
    endDate: string,
    mode: AnalyticsMode = 'accrual',
    options?: AnalyticReadOptions,
): Promise<DailySpendingPoint[]> {
    if (mode === 'accrual') {
        const accrualTransactions = await getAccrualExpenseTransactionsForRange(startDate, endDate, {
            includeProjectedRecurring: true,
            useInvoiceAnchorDateForEntries: true,
            installmentsFullValue: options?.installmentsFullValue,
            invoicesByClosingMonth: options?.invoicesByClosingMonth,
        });
        const totalsByDay = new Map<string, number>();

        for (const transaction of accrualTransactions) {
            const amount = getAccrualExpenseAmount(transaction);
            if (amount === 0) continue;
            if (transaction.date < startDate || transaction.date > endDate) continue;
            totalsByDay.set(transaction.date, (totalsByDay.get(transaction.date) ?? 0) + amount);
        }

        return Array.from(totalsByDay.entries())
            .map(([day, total]) => ({ day, total }))
            .sort((left, right) => compareIso(left.day, right.day));
    }

    const rows = await db
        .select({
            day: transactions.date,
            total: sql<number>`COALESCE(SUM(${transactions.amount}), 0)`.as('total'),
        })
        .from(transactions)
        .where(
            and(
                eq(transactions.type, 'expense'),
                gte(transactions.date, startDate),
                lte(transactions.date, endDate),
                isNull(transactions.deletedAt),
                mode === 'cash-flow' ? cashFlowExpenseScope() : excludeNonAnalyticInvoiceAdjustments(),
                excludePersonalLoanPrincipal(),
                excludeIgnored(),
                excludeDeletedCardTransactions(),
            ),
        )
        .groupBy(transactions.date)
        .orderBy(transactions.date);

    return rows;
}

// ─── Top Expenses (maiores gastos da Análise) ───────────────

export interface TopExpenseItem {
    id: string;
    description: string | null;
    amount: number;
    date: string;
    categoryId: string | null;
    categoryName: string | null;
    categoryIcon: string | null;
    categoryColor: string | null;
}

/** Maiores despesas individuais do range, na leitura de competência da Análise. */
export async function getTopExpenses(
    startDate: string,
    endDate: string,
    limit = 5,
    options?: AnalyticReadOptions,
): Promise<TopExpenseItem[]> {
    const accrualTransactions = await getAccrualExpenseTransactionsForRange(startDate, endDate, {
        includeProjectedRecurring: true,
        installmentsFullValue: options?.installmentsFullValue,
        invoicesByClosingMonth: options?.invoicesByClosingMonth,
    });

    return accrualTransactions
        .map((transaction) => ({ transaction, amount: getAccrualExpenseAmount(transaction) }))
        .filter((entry) => entry.amount > 0)
        .sort((left, right) => right.amount - left.amount)
        .slice(0, limit)
        .map(({ transaction, amount }) => ({
            id: transaction.id,
            description: transaction.description ?? null,
            amount,
            date: transaction.date,
            categoryId: transaction.categoryId ?? null,
            categoryName: transaction.categoryName ?? null,
            categoryIcon: transaction.categoryIcon ?? null,
            categoryColor: transaction.categoryColor ?? null,
        }));
}

// ─── Net Worth History ──────────────────────────────────────

export interface NetWorthPoint {
    month: string;
    balance: number;
}

/**
 * Computes net worth (sum of all active account balances) at the end of each month
 * for the last `months` months. Works backwards from the current total balance.
 */
export async function getNetWorthHistory(months: number = 12, monthStartDay?: number): Promise<NetWorthPoint[]> {
    const { getIncludedAccounts } = await import('./accountRepository');
    const includedAccounts = await getIncludedAccounts();
    const balances = buildIncludedAccountBalanceMap(includedAccounts, 'initialBalance');
    // Os `months` PERÍODOS até o de hoje (ADR 0190): com dia 20, em 25/10 o último é
    // 20/out–19/nov ("2026-11"), não o mês-calendário — senão o ponto de hoje ficava de fora.
    const day = monthStartDay ?? getMonthStartDay();
    const keys = monthKeysAround(todayMonthKey(day), months - 1, 0);

    if (balances.size === 0) {
        return keys.map((month) => ({ month, balance: 0 }));
    }

    const rows = await db
        .select({
            date: transactions.date,
            type: transactions.type,
            amount: transactions.amount,
            accountId: transactions.accountId,
            transferDestinationAccountId: transactions.transferDestinationAccountId,
        })
        .from(transactions)
        .where(
            and(
                isNull(transactions.deletedAt),
                eq(transactions.isConfirmed, 1),
                sql`${transactions.type} IN ('income', 'expense', 'transfer', 'adjustment')`,
            ),
        )
        .orderBy(asc(transactions.date), asc(transactions.createdAt));

    // Fim do PERÍODO da chave (ADR 0190): o patrimônio é lido no fechamento do mês da pessoa.
    const boundaries: BalanceBoundary[] = keys.map((month) => ({
        label: month,
        endDate: getMonthDateRange(month, day).endDate,
    }));

    return buildBalanceTimeline(balances, rows, boundaries).map((point) => ({
        month: point.label,
        balance: point.balance,
    }));
}

// ── Report data aggregation ────────────────────────────────────────────

export interface ReportFilters {
    startDate: string;
    endDate: string;
    accountIds?: string[];
    tagIds?: string[];
    categoryIds?: string[];
    type?: 'expense' | 'income' | 'all';
    groupBy?: ReportGroupBy[];
    /** Parcelamento pelo valor total (preferência, ADR 0043). */
    installmentsFullValue?: boolean;
    /** Situação (preferência, ADR 0063): quais lançamentos entram — todas, concluídas ou pendentes. */
    status?: ReportStatusFilter;
    /** Início do mês (ADR 0190): entra pelos hooks pra compor a queryKey; o agrupamento por mês lê o do app. */
    monthStartDay?: number;
}

export type ReportGroupBy = 'category' | 'tag' | 'account' | 'month';

export interface TagBreakdown {
    tagId: string;
    tagName: string;
    tagColor: string | null;
    totalIncome: number;
    totalExpense: number;
    net: number;
    transactionCount: number;
}

export interface AccountBreakdown {
    accountId: string | null;
    accountName: string;
    accountColor: string | null;
    sourceType: 'account' | 'creditCard' | 'unassigned';
    totalIncome: number;
    totalExpense: number;
    net: number;
    transactionCount: number;
}

export interface ReportData {
    filters: ReportFilters;
    totalIncome: number;
    totalExpense: number;
    net: number;
    byCategory: CategorySpending[];
    bySubcategory: SubcategorySpending[];
    byTag: TagBreakdown[];
    byAccount: AccountBreakdown[];
    transactions: TransactionWithRelations[];
    monthlyTotals: { month: string; income: number; expense: number }[];
    /** Nomes das tags de cada transação (todas as tags, sem recorte do filtro) — p/ coluna de tags no export CSV/PDF. */
    tagsByTransaction: Record<string, string[]>;
}

/** Associação transação→tag já buscada, entregue ao agregador puro de relatório. */
export interface ReportTagAssoc {
    transactionId: string;
    tagId: string;
    tagName: string;
    tagColor: string | null;
}

/**
 * Agregação pura do relatório: recebe as transações já filtradas (transfer, adjustment e principal
 * de empréstimo a pessoa já fora) e as associações de tag já buscadas, e monta o ReportData —
 * totais, por categoria/subcategoria/conta/mês/tag. Sem I/O: o repositório busca e delega, e os
 * números rígidos de Análise ficam testáveis com fixtures (ADR-0009). Aritmética idêntica à que
 * vivia inline atrás do db.select() → paridade por construção.
 */
export function buildReportData(
    reportTx: TransactionWithRelations[],
    tagAssocs: ReportTagAssoc[],
    filters: ReportFilters,
): ReportData {
    let totalIncome = 0;
    let totalExpense = 0;
    for (const tx of reportTx) {
        if (tx.type === 'income') totalIncome += tx.amount;
        else if (tx.type === 'expense') totalExpense += tx.amount;
    }

    // Aggregate by category
    const categoryMap = new Map<string, CategorySpending>();
    for (const tx of reportTx) {
        addCategorySpending(categoryMap, tx, tx.amount);
    }
    const byCategory = Array.from(categoryMap.values()).sort((a, b) => b.total - a.total);

    // Aggregate by subcategory when a single category is filtered
    let bySubcategory: SubcategorySpending[] = [];
    if (filters.categoryIds && filters.categoryIds.length === 1) {
        const subMap = new Map<string, SubcategorySpending>();
        let uncategorizedTotal = 0;
        for (const tx of reportTx) {
            if (!tx.categoryId) continue;
            if (tx.subcategoryId) {
                const existing = subMap.get(tx.subcategoryId);
                if (existing) {
                    existing.total += tx.amount;
                } else {
                    subMap.set(tx.subcategoryId, {
                        subcategoryId: tx.subcategoryId,
                        subcategoryName: tx.subcategoryName || 'Subcategoria',
                        total: tx.amount,
                    });
                }
            } else {
                uncategorizedTotal += tx.amount;
            }
        }
        bySubcategory = Array.from(subMap.values()).sort((a, b) => b.total - a.total);
        if (uncategorizedTotal > 0 && bySubcategory.length > 0) {
            bySubcategory.push({
                subcategoryId: UNCATEGORIZED_SUBCATEGORY.id,
                subcategoryName: UNCATEGORIZED_SUBCATEGORY.name,
                total: uncategorizedTotal,
            });
        }
    }

    // Aggregate by account / origin
    const accountMap = new Map<string, AccountBreakdown>();
    for (const tx of reportTx) {
        const entryKey = tx.accountId
            ? `account:${tx.accountId}`
            : tx.creditCardId
                ? `credit-card:${tx.creditCardId}`
                : 'unassigned';
        const entry = accountMap.get(entryKey) ?? {
            accountId: tx.accountId ?? tx.creditCardId ?? null,
            accountName: tx.accountName
                || (tx.creditCardName ? `Cartão ${tx.creditCardName}` : 'Sem conta'),
            accountColor: tx.accountColor ?? null,
            sourceType: tx.accountId ? 'account' : tx.creditCardId ? 'creditCard' : 'unassigned',
            totalIncome: 0,
            totalExpense: 0,
            net: 0,
            transactionCount: 0,
        };

        if (tx.type === 'income') entry.totalIncome += tx.amount;
        else if (tx.type === 'expense') entry.totalExpense += tx.amount;
        entry.transactionCount++;

        accountMap.set(entryKey, entry);
    }

    const byAccount = Array.from(accountMap.values())
        .map((entry) => ({
            ...entry,
            totalIncome: Math.round(entry.totalIncome * 100) / 100,
            totalExpense: Math.round(entry.totalExpense * 100) / 100,
            net: Math.round((entry.totalIncome - entry.totalExpense) * 100) / 100,
        }))
        .sort((a, b) => (b.totalExpense + b.totalIncome) - (a.totalExpense + a.totalIncome));

    // Aggregate by month — pela chave do PERÍODO (ADR 0190); com dia 1 é o mês-calendário.
    const reportMonthStartDay = getMonthStartDay();
    const monthMap = new Map<string, { income: number; expense: number }>();
    for (const tx of reportTx) {
        const month = monthKeyForDate(tx.date, reportMonthStartDay);
        const entry = monthMap.get(month) || { income: 0, expense: 0 };
        if (tx.type === 'income') entry.income += tx.amount;
        else if (tx.type === 'expense') entry.expense += tx.amount;
        monthMap.set(month, entry);
    }
    const monthlyTotals = Array.from(monthMap.entries())
        .sort(([a], [b]) => compareIso(a, b))
        .map(([month, data]) => ({ month, ...data }));

    // Aggregate by tag from the provided associations. As assocs chegam SEM recorte de tag
    // (todas as tags das transações do relatório): tagsByTransaction lista todas — é o que o
    // export CSV/PDF mostra por linha — enquanto o breakdown byTag respeita o filtro de tags
    // do relatório (se houver), preservando o comportamento de só somar as tags selecionadas.
    const tagsByTransaction: Record<string, string[]> = {};
    let byTag: TagBreakdown[] = [];
    if (tagAssocs.length > 0) {
        for (const a of tagAssocs) {
            (tagsByTransaction[a.transactionId] ??= []).push(a.tagName);
        }
        for (const names of Object.values(tagsByTransaction)) {
            names.sort((left, right) => left.localeCompare(right));
        }

        const tagIdFilter = filters.tagIds && filters.tagIds.length > 0 ? new Set(filters.tagIds) : null;
        const breakdownAssocs = tagIdFilter ? tagAssocs.filter((a) => tagIdFilter.has(a.tagId)) : tagAssocs;

        // Build tx→tags lookup
        const txTagMap = new Map<string, string[]>();
        for (const a of breakdownAssocs) {
            const list = txTagMap.get(a.transactionId) || [];
            list.push(a.tagId);
            txTagMap.set(a.transactionId, list);
        }

        // Build per-tag totals
        const tagTotals = new Map<string, TagBreakdown>();
        for (const a of breakdownAssocs) {
            if (!tagTotals.has(a.tagId)) {
                tagTotals.set(a.tagId, {
                    tagId: a.tagId,
                    tagName: a.tagName,
                    tagColor: a.tagColor,
                    totalIncome: 0,
                    totalExpense: 0,
                    net: 0,
                    transactionCount: 0,
                });
            }
        }
        const txById = new Map(reportTx.map(t => [t.id, t]));
        for (const [txId, tagIdList] of txTagMap) {
            const tx = txById.get(txId);
            if (!tx) continue;
            for (const tagId of tagIdList) {
                const entry = tagTotals.get(tagId)!;
                if (tx.type === 'income') entry.totalIncome += tx.amount;
                else if (tx.type === 'expense') entry.totalExpense += tx.amount;
                entry.transactionCount++;
            }
        }
        for (const entry of tagTotals.values()) {
            entry.totalIncome = Math.round(entry.totalIncome * 100) / 100;
            entry.totalExpense = Math.round(entry.totalExpense * 100) / 100;
            entry.net = Math.round((entry.totalIncome - entry.totalExpense) * 100) / 100;
        }
        byTag = Array.from(tagTotals.values()).sort((a, b) => (b.totalExpense + b.totalIncome) - (a.totalExpense + a.totalIncome));
    }

    return {
        filters,
        totalIncome: Math.round(totalIncome * 100) / 100,
        totalExpense: Math.round(totalExpense * 100) / 100,
        net: Math.round((totalIncome - totalExpense) * 100) / 100,
        byCategory,
        bySubcategory,
        byTag,
        byAccount,
        transactions: reportTx,
        monthlyTotals,
        tagsByTransaction,
    };
}

export async function getReportData(filters: ReportFilters): Promise<ReportData> {
    const status = filters.status ?? 'settled';
    const txFilters: TransactionFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        accountIds: filters.accountIds,
        tagIds: filters.tagIds,
        categoryIds: filters.categoryIds,
        // Situação (ADR 0063): o corte de confirmadas e a projeção de recorrência andam
        // juntos — `getTransactions` ignora a projeção quando `isConfirmed === true`
        // (uma Ocorrência projetada nunca está confirmada).
        isConfirmed: confirmedFilterFor(status),
        includeProjectedRecurring: includesProjected(status),
        includeInvoiceArtifacts: false,
        // Include credit card purchases (regular + installments).
        // Installments already have the correct per-month amount.
        includeRawCreditCardTransactions: true,
        // Relatório é leitura analítica — Lançamento ignorado (ADR 0037) fica fora.
        excludeIgnored: true,
        // A Ocorrência projetada só traz a tag herdada da Regra com isto ligado (ADR 0101).
        // Sem ele, ela entrava no total e sumia do byTag — o breakdown não fechava.
        includeTags: true,
    };
    if (filters.type && filters.type !== 'all') {
        txFilters.type = filters.type as TransactionTypeValue;
    }

    const allTx = await getTransactions(txFilters);

    // Filter out transfers for report totals; principal de empréstimo a pessoa
    // (saída/recebimento) não é receita nem despesa — só excedente e baixa contam.
    let reportTx = allTx.filter(t =>
        t.type !== 'transfer'
        && t.type !== 'adjustment'
        && !isPersonalLoanPrincipalEntry(t.personalLoanEntryType),
    );

    // Parcelamento pelo valor total: a âncora vira uma linha única com a soma do
    // grupo, no mês da compra; as demais parcelas saem do relatório. As stats
    // ESPELHAM os filtros de linha acima (confirmadas, tags, categorias): sem
    // isso, uma âncora filtrada (desconfirmada via swipe, ou tag só numa parcela
    // do meio) faria o grupo inteiro sumir do relatório em silêncio.
    if (filters.installmentsFullValue) {
        reportTx = await applyInstallmentFullValue(reportTx, {
            confirmedOnly: txFilters.isConfirmed === true,
            tagIds: filters.tagIds,
            categoryIds: filters.categoryIds,
        });
    }

    // I/O fica no repositório: busca TODAS as associações de tag das transações do relatório
    // (sem recorte de tag — a coluna de tags do export mostra todas as tags de cada linha) e
    // delega toda a aritmética rígida ao agregador puro, que aplica o filtro de tags só no
    // breakdown byTag.
    // Só as linhas REAIS vão ao transaction_tags: a Ocorrência projetada tem id virtual
    // (ADR 0011) e nunca casaria ali. A tag dela vem herdada da Regra, em `tx.tags`.
    const realTxIds = reportTx.filter(t => !isVirtualTransactionId(t.id)).map(t => t.id);
    const tagAssocs: ReportTagAssoc[] = realTxIds.length > 0
        ? await db
            .select({
                transactionId: transactionTags.transactionId,
                tagId: tags.id,
                tagName: tags.name,
                tagColor: tags.color,
            })
            .from(transactionTags)
            .innerJoin(tags, eq(transactionTags.tagId, tags.id))
            .where(inArray(transactionTags.transactionId, realTxIds))
        : [];

    // Assocs das projetadas, derivadas da tag herdada — mesma forma, pra o agregador puro
    // não precisar saber de onde a tag veio (ADR 0101).
    for (const tx of reportTx) {
        if (!isVirtualTransactionId(tx.id)) continue;
        for (const tag of tx.tags ?? []) {
            tagAssocs.push({
                transactionId: tx.id,
                tagId: tag.id,
                tagName: tag.name,
                tagColor: tag.color,
            });
        }
    }

    return buildReportData(reportTx, tagAssocs, filters);
}

// ── Auto-suggestion for notification confirmation ──────────────────────

export interface SuggestionResult {
    categoryId: string;
    subcategoryId: string | null;
    categoryName?: string;
    subcategoryName?: string;
    transactionId: string | null;
    accountId?: string;
    creditCardId?: string;
    ruleId?: string;
    rulePattern?: string;
    /**
     * Veio do CONTEXTO (dia, hora, valor), não da última transação — só em descrição
     * sem informação, como "PIX ENVIADO" (ADR 0131).
     */
    contextual?: {
        /** Probabilidade do palpite entre os rótulos do histórico daquela descrição. */
        probability: number;
        /** 2ª opção, só quando o palpite está abaixo de `CONTEXTUAL_CONFIDENT_PROBABILITY`. */
        alternative: {
            categoryId: string;
            subcategoryId: string | null;
            categoryName?: string;
            subcategoryName?: string;
        } | null;
    };
    /**
     * Veio de lançamentos PARECIDOS, não da mesma descrição nem de regra (ADR 0133).
     * Sem `transactionId`: as tags de outra compra não são desta.
     */
    similar?: {
        /** Fatia do voto dos vizinhos que ficou com a categoria sugerida. */
        confidence: number;
        /**
         * Nome do marketplace quando a sugestão veio do CONSENSO da casa (ADR 0143) —
         * "você costuma lançar Shopee assim" —, não da semelhança da descrição.
         */
        marketplace?: string;
    };
    /**
     * Veio do catálogo de fábrica (ADR 0149): nada da pessoa respondia e a loja é
     * conhecida. É o palpite do app, não o histórico — a tela diz isso.
     */
    catalog?: { pattern: string };
}

/**
 * Teto de linhas lidas pra achar o histórico de uma descrição genérica. A
 * normalização (acento, pontuação) só existe em JS, então o filtro exato é feito
 * aqui; 2.000 lançamentos recentes cobrem meses de Pix de quem mais usa.
 */
const CONTEXTUAL_HISTORY_SCAN_LIMIT = 2000;
/** Com menos que isso no escopo do cartão/conta, o contexto usa o histórico global. */
const CONTEXTUAL_MIN_SCOPED_SAMPLES = 3;

async function suggestFromContext(
    normalizedDesc: string,
    baseConditions: SQL[],
    context: ContextualQuery,
    creditCardId?: string,
    accountId?: string,
): Promise<SuggestionResult | null> {
    const rows = (
        await db
            .select({
                categoryId: transactions.categoryId,
                subcategoryId: subcategories.id,
                categoryName: categories.name,
                subcategoryName: subcategories.name,
                transactionId: transactions.id,
                accountId: transactions.accountId,
                creditCardId: transactions.creditCardId,
                description: transactions.description,
                date: transactions.date,
                time: transactions.time,
                amount: transactions.amount,
            })
            .from(transactions)
            .innerJoin(categories, and(eq(transactions.categoryId, categories.id), isNull(categories.deletedAt)))
            .leftJoin(subcategories, and(eq(transactions.subcategoryId, subcategories.id), isNull(subcategories.deletedAt)))
            .where(and(...baseConditions))
            .orderBy(desc(transactions.date), desc(transactions.createdAt))
            .limit(CONTEXTUAL_HISTORY_SCAN_LIMIT)
    ).filter((row) => row.categoryId && normalizeAutoCategorizationText(row.description) === normalizedDesc);

    const scoped = rows.filter((row) =>
        creditCardId ? row.creditCardId === creditCardId : accountId ? row.accountId === accountId : false,
    );
    const history = scoped.length >= CONTEXTUAL_MIN_SCOPED_SAMPLES ? scoped : rows;
    const ranking = rankCategoriesByContext(
        history.map((row) => ({
            categoryId: row.categoryId as string,
            subcategoryId: row.subcategoryId ?? null,
            date: row.date,
            time: row.time,
            amount: row.amount,
        })),
        context,
    );
    const [best, second] = ranking;
    if (!best) return null;

    // Linha mais recente de cada rótulo: dá os nomes vivos e a transação de onde as
    // tags são sugeridas (a de mesmo rótulo, não a última de qualquer rótulo).
    const latestOf = (label: { categoryId: string; subcategoryId: string | null }) =>
        history.find((row) => row.categoryId === label.categoryId && (row.subcategoryId ?? null) === label.subcategoryId);
    const bestRow = latestOf(best);
    if (!bestRow) return null;
    const secondRow = second ? latestOf(second) : undefined;

    return {
        categoryId: best.categoryId,
        subcategoryId: best.subcategoryId,
        categoryName: bestRow.categoryName,
        subcategoryName: bestRow.subcategoryName ?? undefined,
        transactionId: bestRow.transactionId,
        ...(bestRow.accountId ? { accountId: bestRow.accountId } : {}),
        ...(bestRow.creditCardId ? { creditCardId: bestRow.creditCardId } : {}),
        contextual: {
            probability: best.probability,
            alternative:
                second && secondRow && best.probability < CONTEXTUAL_CONFIDENT_PROBABILITY
                    ? {
                          categoryId: second.categoryId,
                          subcategoryId: second.subcategoryId,
                          categoryName: secondRow.categoryName,
                          subcategoryName: secondRow.subcategoryName ?? undefined,
                      }
                    : null,
        },
    };
}

export async function getLastCategoryForDescription(
    description: string,
    creditCardId?: string,
    accountId?: string,
    transactionType?: AutoCategorizationTargetType,
    context?: ContextualQuery,
): Promise<SuggestionResult | null> {
    const normalizedDesc = normalizeAutoCategorizationText(description);
    if (normalizedDesc.length < 2) return null;

    const baseConditions = [
        isNull(transactions.deletedAt),
        eq(transactions.isConfirmed, 1),
        sql`${transactions.categoryId} IS NOT NULL`,
    ];

    if (transactionType) {
        baseConditions.push(eq(transactions.type, transactionType));
    }

    if (context && isLowSignalDescription(description)) {
        const contextual = await suggestFromContext(normalizedDesc, baseConditions, context, creditCardId, accountId);
        if (contextual) return contextual;
    }

    const findScopedSuggestion = async (
        scope: ReturnType<typeof eq> | undefined,
    ): Promise<SuggestionResult | null> => {
        const rows = await db
            .select({
                categoryId: transactions.categoryId,
                subcategoryId: subcategories.id,
                categoryName: categories.name,
                subcategoryName: subcategories.name,
                transactionId: transactions.id,
                accountId: transactions.accountId,
                creditCardId: transactions.creditCardId,
                description: transactions.description,
            })
            .from(transactions)
            // Join com categoria VIVA: histórico apontando pra categoria apagada não
            // sugere nada (mesma defesa da leitura de regras); subcategoria morta
            // vira null sem derrubar a categoria.
            .innerJoin(categories, and(eq(transactions.categoryId, categories.id), isNull(categories.deletedAt)))
            .leftJoin(subcategories, and(eq(transactions.subcategoryId, subcategories.id), isNull(subcategories.deletedAt)))
            .where(scope ? and(...baseConditions, scope) : and(...baseConditions))
            .orderBy(desc(transactions.date), desc(transactions.createdAt))
            .limit(50);

        const exactMatch = rows.find((row) => normalizeAutoCategorizationText(row.description) === normalizedDesc);
        if (exactMatch?.categoryId) {
            return {
                categoryId: exactMatch.categoryId,
                subcategoryId: exactMatch.subcategoryId ?? null,
                categoryName: exactMatch.categoryName,
                subcategoryName: exactMatch.subcategoryName ?? undefined,
                transactionId: exactMatch.transactionId,
                ...(exactMatch.accountId ? { accountId: exactMatch.accountId } : {}),
                ...(exactMatch.creditCardId ? { creditCardId: exactMatch.creditCardId } : {}),
            };
        }

        return null;
    };

    // First try: scoped to same card or account
    if (creditCardId) {
        const scoped = await findScopedSuggestion(eq(transactions.creditCardId, creditCardId));
        if (scoped) {
            return scoped;
        }
    } else if (accountId) {
        const scoped = await findScopedSuggestion(eq(transactions.accountId, accountId));
        if (scoped) {
            return scoped;
        }
    }

    const global = await findScopedSuggestion(undefined);
    if (global) {
        return global;
    }

    const autoMatch = await matchDescription(description, transactionType);
    if (autoMatch) {
        return {
            categoryId: autoMatch.categoryId,
            subcategoryId: autoMatch.subcategoryId,
            categoryName: autoMatch.categoryName,
            subcategoryName: autoMatch.subcategoryName,
            transactionId: null,
            ruleId: autoMatch.ruleId,
            rulePattern: autoMatch.rulePattern,
        };
    }

    // Último recurso: lançamentos parecidos (ADR 0133).
    if (canSuggestFromSimilarDescriptions(description)) {
        const index = await loadSimilarDescriptionsIndex(transactionType);
        const similar = suggestFromSimilarDescriptions(description, index);
        if (similar) {
            const names = index.names[similar.closestIndex];
            return {
                categoryId: similar.categoryId,
                subcategoryId: similar.subcategoryId,
                categoryName: names.categoryName,
                subcategoryName: names.subcategoryName ?? undefined,
                transactionId: null,
                similar: {
                    confidence: similar.confidence,
                    marketplace: similar.marketplace && marketplaceDisplayName(similar.marketplace),
                },
            };
        }
    }

    // Palpite do catálogo de fábrica, só em despesa (ADR 0149).
    if (transactionType !== 'income') {
        const fromCatalog = (await loadMerchantCatalogResolver())(description);
        if (fromCatalog) {
            return {
                categoryId: fromCatalog.categoryId,
                subcategoryId: fromCatalog.subcategoryId,
                categoryName: fromCatalog.categoryName,
                subcategoryName: fromCatalog.subcategoryName,
                transactionId: null,
                catalog: { pattern: fromCatalog.rulePattern ?? '' },
            };
        }
    }

    return null;
}

/**
 * Ocorrências de recorrência que ainda NÃO viraram linha e mesmo assim são
 * candidatas ao abate (ADR 0066).
 *
 * `generateOccurrences` materializa recorrência de conta só até HOJE (a de
 * cartão vai até o fim do mês, por causa da fatura). O salário do dia 15 que cai
 * no dia 14 não tinha, portanto, linha nenhuma no banco quando a notificação
 * chegou — e a metade "antes" da janela de ±3 dias, que existe justamente pro
 * dinheiro que cai adiantado, era morta pra recorrência de conta.
 *
 * A projeção aqui é a MESMA de `getTransactions` (mesma engine, mesma dedup por
 * slot agendado), então a candidata é exatamente a linha que o usuário vê na
 * lista do mês. Só o escopo de conta projeta: no cartão a ocorrência do mês já
 * está materializada, e alcançar a do mês seguinte seria pescar em outra fatura.
 */
async function getProjectedRecurringMatchCandidates(params: {
    type: 'income' | 'expense';
    minAmount: number;
    maxAmount: number;
    startDate: string;
    endDate: string;
    /** Busca feita por uma compra no CARTÃO: a regra é de conta e o abate leva a ocorrência pra fatura. */
    paidOnCard?: boolean;
}): Promise<NotificationMatchCandidate[]> {
    const rules = await db
        .select({
            id: recurringTransactions.id,
            type: recurringTransactions.type,
            amount: recurringTransactions.amount,
            description: recurringTransactions.description,
            // Abate não é análise: série ignorada segue candidata (o dinheiro cai igual).
            isIgnored: recurringTransactions.isIgnored,
            frequency: recurringTransactions.frequency,
            interval: recurringTransactions.interval,
            weekdays: recurringTransactions.weekdays,
            monthlyMode: recurringTransactions.monthlyMode,
            startDate: recurringTransactions.startDate,
            nextOccurrence: recurringTransactions.nextOccurrence,
            endDate: recurringTransactions.endDate,
            skippedDates: recurringTransactions.skippedDates,
            accountId: recurringTransactions.accountId,
            categoryId: recurringTransactions.categoryId,
            accountName: accounts.name,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
            subcategoryName: subcategories.name,
        })
        .from(recurringTransactions)
        .leftJoin(accounts, eq(recurringTransactions.accountId, accounts.id))
        .leftJoin(categories, eq(recurringTransactions.categoryId, categories.id))
        .leftJoin(subcategories, eq(recurringTransactions.subcategoryId, subcategories.id))
        .where(and(
            eq(recurringTransactions.isActive, 1),
            isNull(recurringTransactions.deletedAt),
            eq(recurringTransactions.type, params.type),
            isNull(recurringTransactions.creditCardId),
            // Mesma faixa da busca por linha real; quem decide é a régua da série.
            gte(recurringTransactions.amount, params.minAmount),
            lte(recurringTransactions.amount, params.maxAmount),
            lte(recurringTransactions.nextOccurrence, params.endDate),
            sql`(${recurringTransactions.endDate} IS NULL OR ${recurringTransactions.endDate} >= ${params.startDate})`,
        ));

    if (rules.length === 0) return [];
    const rulesById = new Map(rules.map((rule) => [rule.id, rule]));

    // Dedup pelo slot AGENDADO, não pela data real: a ocorrência antecipada num
    // abate anterior tem data ≠ slot, e sem isto voltaria como fantasma projetada.
    const occurrenceSlot = sql<string>`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date})`;
    const materialized = await db
        .select({
            recurringId: transactions.recurringId,
            occurrenceDate: occurrenceSlot,
        })
        .from(transactions)
        .where(and(
            isNull(transactions.deletedAt),
            isNotNull(transactions.recurringId),
            gte(occurrenceSlot, params.startDate),
            lte(occurrenceSlot, params.endDate),
        ));

    return buildProjectedRecurringTransactions(
        rules,
        params.startDate,
        params.endDate,
        new Set(
            materialized
                .filter((row) => row.recurringId)
                .map((row) => `${row.recurringId}:${row.occurrenceDate}`),
        ),
    ).map((occurrence) => {
        const rule = rulesById.get(occurrence.recurringId);
        return {
            // Id virtual: serve de chave na UI, mas quem abate é a REGRA (settleOccurrence),
            // por isso `projectedRecurringId`/`projectedOccurrenceDate` viajam junto.
            id: occurrence.id,
            description: occurrence.description,
            date: occurrence.date,
            amount: occurrence.amount,
            accountId: occurrence.accountId,
            accountName: occurrence.accountName,
            categoryName: occurrence.categoryName,
            categoryIcon: rule?.categoryIcon ?? null,
            categoryColor: rule?.categoryColor ?? null,
            subcategoryName: rule?.subcategoryName ?? null,
            financingId: null,
            financingName: null,
            creditCardId: null,
            creditCardName: null,
            personalLoanId: null,
            personName: null,
            projectedRecurringId: occurrence.recurringId,
            projectedOccurrenceDate: occurrence.date,
            recurringFrequency: rule?.frequency ?? null,
            recurringInterval: rule?.interval ?? null,
            seriesKey: `recurring:${occurrence.recurringId}`,
            paidOnCard: params.paidOnCard === true,
        };
    });
}

/**
 * A série de uma linha, na mesma prioridade do abate: recorrência, depois
 * parcela de financiamento, depois parcelamento em conta. Empréstimo a pessoa
 * não tem série aqui — o recebimento é parcial por natureza e o ciclo do
 * empréstimo já registra o valor que entrou.
 */
function notificationMatchSeriesKey(row: {
    recurringId: string | null;
    financingId: string | null;
    installmentId: string | null;
}): string | null {
    if (row.recurringId) return `recurring:${row.recurringId}`;
    if (row.financingId) return `financing:${row.financingId}`;
    if (row.installmentId) return `installment:${row.installmentId}`;
    return null;
}

/**
 * O que cada série das candidatas já pagou (ADR 0132): os últimos pagamentos
 * CONFIRMADOS até a data da notificação, o quanto escorregaram da data agendada
 * e os textos das notificações que já os abateram.
 *
 * Recorrência muda de id ao longo da vida: excluir e recriar, ou editar de um jeito
 * que refaz a regra, deixa os pagamentos antigos apontando pra uma regra que não
 * existe mais. A série então herda os pagamentos de recorrência com a MESMA
 * descrição e categoria cuja regra não está mais viva — sem isso, a Energia com
 * seis meses pagos chegava aqui com um só, e a régua voltava pro centavo. Regra
 * viva com o mesmo nome é outra série e não empresta histórico.
 */
async function getNotificationMatchSeries(
    candidates: NotificationMatchCandidate[],
    type: 'income' | 'expense',
    untilDate: string,
): Promise<Map<string, NotificationMatchSeries>> {
    const idsByKind = { recurring: new Set<string>(), financing: new Set<string>(), installment: new Set<string>() };
    for (const candidate of candidates) {
        if (!candidate.seriesKey) continue;
        const [kind, id] = candidate.seriesKey.split(':') as [keyof typeof idsByKind, string];
        idsByKind[kind]?.add(id);
    }

    const historyColumns = {
        id: transactions.id,
        amount: transactions.amount,
        date: transactions.date,
        description: transactions.description,
        categoryId: transactions.categoryId,
        recurringOccurrenceDate: transactions.recurringOccurrenceDate,
        recurringId: transactions.recurringId,
        financingId: transactions.financingId,
        installmentId: transactions.installmentId,
    };
    type HistoryRow = { key: string; id: string; amount: number; date: string; recurringOccurrenceDate: string | null };
    const history: HistoryRow[] = [];
    const paidBase = [
        isNull(transactions.deletedAt),
        eq(transactions.isConfirmed, 1),
        eq(transactions.type, type),
        lte(transactions.date, untilDate),
    ];

    if (idsByKind.recurring.size > 0) {
        const ruleIds = [...idsByKind.recurring];
        const rules = await db
            .select({
                id: recurringTransactions.id,
                description: recurringTransactions.description,
                categoryId: recurringTransactions.categoryId,
            })
            .from(recurringTransactions)
            .where(inArray(recurringTransactions.id, ruleIds));
        const ruleCategoryIds = [...new Set(rules.map((rule) => rule.categoryId).filter((id): id is string => !!id))];

        const rows = await db
            .select(historyColumns)
            .from(transactions)
            .where(and(
                ...paidBase,
                or(
                    inArray(transactions.recurringId, ruleIds),
                    ruleCategoryIds.length > 0
                        ? and(
                            isNotNull(transactions.recurringId),
                            inArray(transactions.categoryId, ruleCategoryIds),
                            sql`NOT EXISTS (SELECT 1 FROM recurring_transactions origin
                                WHERE origin.id = ${transactions.recurringId}
                                AND origin.deleted_at IS NULL AND origin.is_active = 1)`,
                        )
                        : undefined,
                ),
            ));

        for (const row of rows) {
            if (row.recurringId && idsByKind.recurring.has(row.recurringId)) {
                history.push({ ...row, key: `recurring:${row.recurringId}` });
                continue;
            }
            for (const rule of rules) {
                if (rule.categoryId === row.categoryId && isSameSeriesLabel(rule.description, row.description)) {
                    history.push({ ...row, key: `recurring:${rule.id}` });
                }
            }
        }
    }

    const otherConditions = [
        idsByKind.financing.size > 0
            ? and(
                inArray(transactions.financingId, [...idsByKind.financing]),
                eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
            )
            : undefined,
        idsByKind.installment.size > 0 ? inArray(transactions.installmentId, [...idsByKind.installment]) : undefined,
    ].filter((condition) => condition !== undefined);
    if (otherConditions.length > 0) {
        const rows = await db
            .select(historyColumns)
            .from(transactions)
            .where(and(...paidBase, or(...otherConditions)));
        for (const row of rows) {
            const key = row.financingId ? `financing:${row.financingId}` : `installment:${row.installmentId}`;
            history.push({ ...row, key });
        }
    }
    if (history.length === 0) return new Map();

    history.sort((left, right) => compareIso(left.date, right.date));
    const recentByKey = new Map<string, HistoryRow[]>();
    for (const row of history) {
        const list = recentByKey.get(row.key) ?? [];
        list.push(row);
        recentByKey.set(row.key, list);
    }
    for (const [key, list] of recentByKey) recentByKey.set(key, list.slice(-NOTIFICATION_MATCH_SERIES_HISTORY));

    const recentIds = [...new Set([...recentByKey.values()].flat().map((row) => row.id))];
    const texts = recentIds.length === 0
        ? []
        : await db
            .select({
                transactionId: pendingNotifications.createdTransactionId,
                description: pendingNotifications.description,
            })
            .from(pendingNotifications)
            .where(and(
                eq(pendingNotifications.status, 'confirmed'),
                inArray(pendingNotifications.createdTransactionId, recentIds),
            ));
    const textsByTransaction = new Map<string, string[]>();
    for (const text of texts) {
        if (!text.transactionId) continue;
        textsByTransaction.set(text.transactionId, [...(textsByTransaction.get(text.transactionId) ?? []), text.description]);
    }

    const series = new Map<string, NotificationMatchSeries>();
    for (const [key, list] of recentByKey) {
        series.set(key, {
            paidAmounts: list.map((row) => row.amount),
            // Só a recorrência guarda o slot depois de paga; sem ele não há atraso a medir.
            paymentDelays: list
                .filter((row) => row.recurringOccurrenceDate)
                .map((row) => differenceInCalendarDays(parseISO(row.date), parseISO(row.recurringOccurrenceDate!))),
            notificationTexts: list.flatMap((row) => textsByTransaction.get(row.id) ?? []),
        });
    }
    return series;
}

/**
 * Candidatas a "abater" ao confirmar uma notificação bancária: transações NÃO
 * confirmadas do mesmo sentido, com o MESMO valor e data dentro da janela —
 * mais as ocorrências de recorrência ainda projetadas (ADR 0066).
 *
 * `scope` separa dois universos (ADR 0056): `card` olha linha de cartão (a
 * assinatura/cobrança já lançada na fatura) e `account` só olha linha de conta
 * (conta agendada, parcela de financiamento, recebimento previsto de empréstimo
 * a pessoa). Um Pix nunca abate compra que já está na fatura. O caminho contrário
 * existe com `includeAccountScheduled` (ADR 0137): a compra à vista no cartão
 * também recebe a despesa prevista em conta, marcada `paidOnCard`.
 *
 * Fora do alcance nos dois escopos:
 *  - artefatos de fatura (`adjustmentType`): a despesa pendente "Fatura X" é
 *    quitada pelo fluxo de pagamento de fatura, que sabe abater `invoice_payments`;
 *  - parcelamento (`installmentId`) no escopo de cartão: mexer na data de uma
 *    parcela rebaseia a SÉRIE inteira (`updateInstallmentSeries`), não é abate;
 *  - entry types de financiamento/empréstimo que o abate não sabe tratar — só a
 *    parcela regular e o recebimento previsto aceitam os campos de quitação.
 */
export async function getNotificationMatchCandidates(params: {
    type: 'income' | 'expense';
    amount: number;
    startDate: string;
    endDate: string;
    scope?: NotificationMatchScope;
    /** Cartões com o final da notificação: cobrança de outro cartão não é esta. */
    creditCardIds?: string[];
    /**
     * Compra À VISTA no cartão também procura a despesa prevista em CONTA (ADR 0137).
     * Quem chama decide: parcelada fica de fora, porque o abate é uma linha só.
     */
    includeAccountScheduled?: boolean;
}): Promise<NotificationMatchCandidate[]> {
    const { minAmount, maxAmount } = getNotificationMatchAmountRange(params.amount);
    const scopeCondition = params.scope === 'card'
        ? and(
            isNotNull(transactions.creditCardId),
            isNull(transactions.installmentId),
            params.creditCardIds?.length ? inArray(transactions.creditCardId, params.creditCardIds) : undefined,
        )
        : and(
            isNull(transactions.creditCardId),
            isNull(transactions.invoiceId),
            or(
                isNull(transactions.financingId),
                eq(transactions.financingEntryType, FinancingEntryType.REGULAR_INSTALLMENT),
            ),
            or(
                isNull(transactions.personalLoanId),
                eq(transactions.personalLoanEntryType, PersonalLoanEntryType.SCHEDULED_RECEIPT),
            ),
        );
    // Prevista em conta que uma compra no cartão pode quitar: conta avulsa agendada e
    // ocorrência de recorrência. Parcela de financiamento, recebimento de empréstimo e
    // parcelamento em conta têm ciclo próprio e não se pagam no crédito.
    const paidOnCardCondition = and(
        isNull(transactions.creditCardId),
        isNull(transactions.invoiceId),
        isNull(transactions.financingId),
        isNull(transactions.personalLoanId),
        isNull(transactions.installmentId),
    );
    const searchesAccountScheduled =
        params.scope === 'card' && params.type === 'expense' && params.includeAccountScheduled === true;

    const selectMaterialized = (condition: typeof scopeCondition) => db
        .select({
            id: transactions.id,
            description: transactions.description,
            date: transactions.date,
            amount: transactions.amount,
            accountId: transactions.accountId,
            accountName: accounts.name,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
            subcategoryName: subcategories.name,
            financingId: transactions.financingId,
            financingName: financings.name,
            creditCardId: transactions.creditCardId,
            creditCardName: creditCards.name,
            personalLoanId: transactions.personalLoanId,
            personName: people.name,
            recurringId: transactions.recurringId,
            recurringFrequency: recurringTransactions.frequency,
            recurringInterval: recurringTransactions.interval,
            installmentId: transactions.installmentId,
        })
        .from(transactions)
        .leftJoin(accounts, eq(transactions.accountId, accounts.id))
        .leftJoin(categories, eq(transactions.categoryId, categories.id))
        .leftJoin(subcategories, eq(transactions.subcategoryId, subcategories.id))
        .leftJoin(recurringTransactions, eq(transactions.recurringId, recurringTransactions.id))
        .leftJoin(creditCards, eq(transactions.creditCardId, creditCards.id))
        .leftJoin(financings, eq(transactions.financingId, financings.id))
        .leftJoin(personalLoans, eq(transactions.personalLoanId, personalLoans.id))
        .leftJoin(people, eq(personalLoans.personId, people.id))
        .where(and(
            isNull(transactions.deletedAt),
            eq(transactions.isConfirmed, 0),
            eq(transactions.type, params.type),
            // Faixa larga: tudo que alguma régua de série poderia aceitar (ADR 0132).
            gte(transactions.amount, minAmount),
            lte(transactions.amount, maxAmount),
            gte(transactions.date, params.startDate),
            lte(transactions.date, params.endDate),
            isNull(transactions.adjustmentType),
            condition,
        ))
        .orderBy(asc(transactions.date));

    // Linha real nunca é "projetada" — inclusive a ocorrência de recorrência já
    // materializada, que abate como qualquer conta agendada (tem id de verdade).
    const toCandidates = (
        rows: Awaited<ReturnType<typeof selectMaterialized>>,
        paidOnCard: boolean,
    ): NotificationMatchCandidate[] => rows.map(({ recurringId, installmentId, ...row }) => ({
        ...row,
        projectedRecurringId: null,
        projectedOccurrenceDate: null,
        seriesKey: notificationMatchSeriesKey({ recurringId, financingId: row.financingId, installmentId }),
        paidOnCard,
    }));

    const candidates = toCandidates(await selectMaterialized(scopeCondition), false);

    if (params.scope === 'card' && !searchesAccountScheduled) return candidates;
    if (searchesAccountScheduled) {
        candidates.push(...toCandidates(await selectMaterialized(paidOnCardCondition), true));
    }

    const projected = await getProjectedRecurringMatchCandidates({
        type: params.type,
        minAmount,
        maxAmount,
        startDate: params.startDate,
        endDate: params.endDate,
        paidOnCard: searchesAccountScheduled,
    });

    return [...candidates, ...projected].sort((left, right) => compareIso(left.date, right.date));
}

/**
 * O lançamento previsto que esta notificação paga, se houver (ADR 0056/0066/0132).
 * Busca na faixa e na janela máximas e deixa a régua de cada série decidir: a
 * conta de luz que varia 15% aceita 15%, a assinatura fixa continua no centavo.
 */
export async function findNotificationMatch(params: {
    type: 'income' | 'expense';
    amount: number;
    date: string;
    description: string;
    scope: NotificationMatchScope;
    creditCardIds?: string[];
    includeAccountScheduled?: boolean;
}): Promise<NotificationMatchCandidate | null> {
    const candidates = await getNotificationMatchCandidates({
        type: params.type,
        amount: params.amount,
        scope: params.scope,
        creditCardIds: params.creditCardIds,
        includeAccountScheduled: params.includeAccountScheduled,
        ...getNotificationMatchWindow(params.date, NOTIFICATION_MATCH_MAX_WINDOW_DAYS),
    });
    if (candidates.length === 0) return null;

    const seriesByKey = await getNotificationMatchSeries(candidates, params.type, params.date);
    return pickBestNotificationMatch(
        { description: params.description, date: params.date, amount: params.amount },
        candidates,
        { scope: params.scope, seriesByKey },
    );
}
